import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import DropdownMenu from '../components/common/DropdownMenu';
import SectionHeader from '../components/common/SectionHeader';
import CourtsList from '../components/courts/CourtsList';
import { RosterDragOverlayPreview } from '../components/courts/RosterDragOverlayPreview';
import UpcomingMatch from '../components/courts/UpcomingMatch';
import UpcomingMatchBanner from '../components/courts/UpcomingMatchBanner';
import WaitingList from '../components/players/WaitingList';
import AddGuestModal from '../components/guests/AddGuestModal';
import AddExistingClubPlayerModal from '../components/players/AddExistingClubPlayerModal';
import AddSessionPartnershipModal from '../components/players/AddSessionPartnershipModal';
import usePlayerManagement from '../hooks/usePlayerManagement';
import useCompetitivePlayerHostActions from '../hooks/useCompetitivePlayerHostActions';
import useCourtManagement from '../hooks/useCourtManagement';
import { useSessionScoringConfig } from '../hooks/useSessionScoringConfig';
import { getScoringRulesSummary } from '../utils/gameScoreRules';
import { supabase } from '../utils/supabase';
import { getBestCourtAssignments_TwoStage, updatePlayerInteractions } from '../utils/matchmakingUtils';
import { updateSessionStatus, getSuggestedLatePenalty, setManualLatePenalty, addPlayersToSession } from '../services/sessionService';
import { getSuggestedLatePenaltyFromLiveState } from '../utils/lateJoinerPenaltyUtils';
import { setOwnSessionPresence } from '../services/sessionPresenceService';
import {
  canShowOwnSessionPresence,
  getOwnPresencePauseBlockReason,
  nextPresencePausedList,
  nextPresenceWaitingList,
  pausedIdsFromSessionStatus,
  samePlayerIdList,
} from '../utils/sessionPresence';
import MobileSearchField from '../components/common/MobileSearchField';
import LoadingState from '../components/common/LoadingState';
import { useSeason } from '../context/SeasonContext';
import CurrentPartnerships, { PARTNERSHIPS_MATCHMAKING_INFO } from '../components/players/CurrentPartnerships';
import PlayerManager from '../components/players/PlayerManager';
import CompetitivePlayerOverflowMenu from '../components/players/CompetitivePlayerOverflowMenu';
import { HOST_MENU_EXCLUDE_KEYS } from '../utils/competitivePlayerHostActions';
import MatchPreferenceSelector from '../components/players/MatchPreferenceSelector';
import CourtsTab from '../components/session/CourtsTab';
import SessionRankingTab from '../components/session/SessionRankingTab';
import GamesTab from '../components/session/GamesTab';
import PlayerRankingTab from '../components/session/PlayerRankingTab';
import { SESSION_PANEL_PADDING, SESSION_SECTION_STACK } from '../utils/sessionMobileLayout';
import LatePenaltyModal from '../components/session/LatePenaltyModal';
import SessionPresenceCard from '../components/session/SessionPresenceCard';
import ScoreRecordingNextUpModal from '../components/session/ScoreRecordingNextUpModal';
import SimpleSessionLogger from '../services/simpleSessionLogger';
import { initializeEloRatingsForClub } from '../services/guestService';
import ScheduledGamesList from '../components/scheduled/ScheduledGamesList';
import AddScheduledGameModal from '../components/scheduled/AddScheduledGameModal';
import {
  SCHEDULED_GAMES_HOST_INFO,
  SCHEDULED_GAME_ROSTER_LOCKED_MESSAGE,
  getScheduledGameCancelModalCopy,
} from '../components/scheduled/scheduledGameCopy';
import ConfirmModal from '../components/common/ConfirmModal';
import EndSessionModal from '../components/session/EndSessionModal';
import SessionLayout from '../components/session/SessionLayout';
import SessionTabBar from '../components/session/SessionTabBar';
import SetupGuideBanner from '../components/session/SetupGuideBanner';
import CompetitiveSessionLoadingSkeleton from '../components/session/CompetitiveSessionLoadingSkeleton';
import HostAdminCollapsibleSection from '../components/session/HostAdminCollapsibleSection';
import { GAME_CREATION_ORIGIN, MATCHMAKING_ALGORITHM_NAME, ensureGameCreationMeta, markGameCreationAltered } from '../constants/gameCreation';
import {
  hasPromotableScheduledGame,
  rosterPlayerIdsFromScheduledGame,
} from '@badminton/matchmaking-core';
import { buildSessionPlayerScheduledGamesMap, computeWaitGuaranteeThreshold } from '@badminton/matchmaking-core';
import {
  findPartnershipSplitsInRoster,
  describePartnershipSplitsSummary,
} from '../utils/partnershipRosterUtils';
import { collectScheduledGameIdsFromAssignmentRows, clearScheduledGameFromCourtRow, isAssignmentRowLockedByScheduledGame, isScheduledGameLinkedSlot, resolveScheduledGameForAssignmentRow } from '../utils/scheduledGameConsumption';
import usePlayerCategorisation from '../hooks/usePlayerCategorisation';
import useCompetitiveSessionPersistence from '../hooks/useCompetitiveSessionPersistence';
import useCompetitiveSessionRealtime from '../hooks/useCompetitiveSessionRealtime';
import { SessionSyncCoordinator, AUTHORITATIVE_DEBOUNCE_MS } from '../utils/sessionSyncCoordinator';
import { scoreSubmitSyncDebugLog, logScoreSubmitDebugReady } from '../utils/scoreSubmitDebug';
import { incrementGamesPlayedAndStreaks } from '../utils/incrementSessionGameStats';
import {
  loadPostScoreTargetedState,
  preserveRelaxedMetaOnUnchangedLineups,
  mergeRelaxedMetaFromPreviousRows,
  enrichScoreCompletedLayout,
} from '../services/applyPostScoreServerResult';
import { applyScoreCompletedResult, mergeScoreCompletedCourtAssignments } from '../services/applyScoreCompletedResult';
import useSessionPartnerships from '../hooks/useSessionPartnerships';
import useScheduledGames from '../hooks/useScheduledGames';
import { removeSessionPartnershipMaybeOffline } from '../utils/session/removeSessionPartnershipMaybeOffline';
import {
  getMatchFormat,
  getPlayersPerMatch,
  getPlayersPerSide,
  isSinglesFormat,
  doublesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionSelectValue,
} from '../utils/competitiveMatchFormat';
import { collectPlayerIdsFromAssignments, deriveConsistentWaitingList } from '../utils/sessionMatchModel';
import { getInitials } from '../utils/nameUtils';
import { getRemoveFromSessionBlockReason, canRemovePlayerFromSession } from '../utils/playerUtils';
import { getGenderAvatarBg } from '../utils/genderColors';
import { padUpcomingQueueToSlotCount, courtRosterSig, normalizeNextUpCourtNumbers } from '../utils/courtUtils';
import {
  markCourtMatchStarted,
  clearCourtMatchTimer,
  mergeCourtTimerFromPrevious,
  normalizeMatchStartTime,
  restartCourtMatchTimer,
} from '../utils/matchTimerUtils';
import {
  updateSingleCourtAtomic,
  updateCourtAssignments,
  fetchSingleCourt,
  CourtConflictError,
  deleteNextUpCourtSlot,
  setSessionUpcomingQueueSlotCount,
  mergePlayerWaitMetricsSnapshots,
  repairOrphanedWaitlistPlayers,
  fetchSessionWaitlistAndMetrics,
} from '../services/sessionStateService';
import {
  joinWaitlistCmd as joinWaitlistCmdRemote,
  leaveWaitlistCmd as leaveWaitlistCmdRemote,
  reorderWaitlistCmd as reorderWaitlistCmdRemote,
  setNextUpSlotCmd,
  assignPlayersToCourtCmd as assignPlayersToCourtCmdRemote,
  planUpcomingSlotCmd,
  planCourtFillCmd,
  selectPegBoardGameCmd,
} from '../services/sessionCommands';
import useSessionOfflineQueue from '../hooks/useSessionOfflineQueue';
import OfflineSaveBanner from '../components/session/OfflineSaveBanner';
import { runLocalCourtFill } from '../utils/matchmaking/buildMatchmakingInputV1FromLiveState';
import { applyLocalCourtFill, buildSeatCommandFromFill } from '../utils/session/applyLocalCourtFill';
import { runHostCommandWithOfflineFallback } from '../utils/session/runHostCommandWithOfflineFallback';
import { buildLocalSessionGamesFromMatchHistory } from '../utils/session/buildLocalSessionGames';
import { applyLocalUpcomingSlotCount } from '../utils/session/applyLocalCourtCount';
import { buildLocalGuestPlayer, createOfflinePlayerId } from '../utils/session/buildLocalGuestPlayer';
import {
  OFFLINE_COMMAND_TYPES,
  isLikelyNetworkError,
  shouldHoldLocalOfflineLayout,
  shouldHoldRemoteUpcoming,
  shouldRunLocalHostPath,
} from '../utils/session/offlineQueueConstants';
import { useToast } from '../context/ToastContext';
import { useSessionCommands } from '../hooks/useSessionCommands';
import useSessionTabInstanceId from '../hooks/useSessionTabInstanceId';
import HostsActiveIndicator from '../components/session/HostsActiveIndicator';
import PegBoardPickModal from '../components/pegBoard/PegBoardPickModal';
import { isPegBoardSession } from '../utils/sessionTypeUtils';
import {
  getPicker,
  getEligiblePool,
  getPegBoardPickTarget,
  returnPlayersToWaitlist,
  clampPegBoardSelectionWindow,
  PEG_BOARD_SELECTION_WINDOW_OPTIONS,
} from '../utils/pegBoard';

import { sortedTeamKey, sameCourtLineup, sameRoster, shouldSkipCourtMerge as _shouldSkipCourtMerge, sessionStateChanged as _sessionStateChanged } from '../utils/sessionPersistUtils';

/** Compact +/- controls on desktop; 44px touch targets on mobile. */
const SESSION_COUNTER_BTN =
  'flex h-11 w-11 sm:h-5 sm:w-5 items-center justify-center rounded border border-border-medium text-xs text-text-secondary transition-colors touch-manipulation';

/** Case-insensitive substring on display name (courts tab waitlist / signed-up search). */
function playerNameIncludesQuery(player, rawQuery) {
  const q = (rawQuery ?? '').trim().toLowerCase();
  if (!q) return true;
  const name = (player?.name || player?.fullName || '').trim().toLowerCase();
  return name.includes(q);
}

/** Both teams filled to format size (doubles 2v2, singles 1v1). */
function courtRosterSidesFull(court, playersPerSide) {
  if (playersPerSide == null || !Number.isFinite(playersPerSide)) return false;
  return (
    (court?.team1?.length || 0) === playersPerSide && (court?.team2?.length || 0) === playersPerSide
  );
}

/** Avoid dropping a DB merge when version ties but lineup still differs (multi-device desync). */
const shouldSkipCourtMerge = _shouldSkipCourtMerge;

/** @returns {{ court: number, team: number, slot: number } | null} */
function parseCourtSlotDndId(id) {
  const m = String(id).match(/^court-(\d+)-team(\d+)-slot-(\d+)$/);
  if (!m) return null;
  return { court: Number(m[1]), team: Number(m[2]), slot: Number(m[3]) };
}

/** Strict team droppable id only (not slot ids). @returns {{ court: number, team: number } | null} */
function parseCourtTeamDndId(id) {
  const m = String(id).match(/^court-(\d+)-team(\d+)$/);
  if (!m) return null;
  return { court: Number(m[1]), team: Number(m[2]) };
}

/**
 * Mutates cloned assignment rows (team1/team2 must be new arrays per court).
 * @returns {boolean} false if validation failed
 */
function applyCourtSlotMutation(assignments, from, to, playerId, maxPerTeam) {
  const fc = assignments.find((c) => c.courtNumber === from.court);
  const tc = assignments.find((c) => c.courtNumber === to.court);
  if (!fc || !tc) return false;

  const fk = from.team === 1 ? 'team1' : 'team2';
  const tk = to.team === 1 ? 'team1' : 'team2';

  const fArr = fc[fk];
  const tArr = tc[tk];

  if (from.slot < 0 || from.slot >= fArr.length || fArr[from.slot] !== playerId) {
    return false;
  }
  if (to.slot < 0 || to.slot >= maxPerTeam) {
    return false;
  }

  const sameList = fc === tc && fk === tk;

  if (sameList && from.slot === to.slot) {
    return true;
  }

  const targetOccupied = to.slot < tArr.length;

  if (targetOccupied) {
    if (sameList) {
      const tmp = fArr[from.slot];
      fArr[from.slot] = fArr[to.slot];
      fArr[to.slot] = tmp;
      return true;
    }
    const otherId = tArr[to.slot];
    fArr[from.slot] = otherId;
    tArr[to.slot] = playerId;
    return true;
  }

  const insertAt = Math.min(to.slot, tArr.length);
  if (sameList) {
    fArr.splice(from.slot, 1);
    const adjustedInsert = from.slot < insertAt ? insertAt - 1 : insertAt;
    fArr.splice(adjustedInsert, 0, playerId);
    if (fArr.length > maxPerTeam) return false;
    return true;
  }

  fArr.splice(from.slot, 1);
  tArr.splice(insertAt, 0, playerId);
  if (fArr.length > maxPerTeam || tArr.length > maxPerTeam) return false;
  return true;
}

function applyCourtRosterSideEffectsAfterDnD(courts, touchedCourtNumbers, playersPerMatch, markAltered) {
  const set = new Set(touchedCourtNumbers);
  for (const c of courts) {
    if (!set.has(c.courtNumber)) continue;
    const total = c.team1.length + c.team2.length;
    const nowEmpty = c.team1.length === 0 && c.team2.length === 0;
    if (nowEmpty) {
      clearCourtMatchTimer(c);
    } else if (!c.inProgress && total < playersPerMatch) {
      // Filling phase — not a started match yet
      clearCourtMatchTimer(c);
    }
    // Completing the roster via drag-and-drop (same as fillPlayingCourt when isFull):
    // must mark the court in-progress or score inputs stay hidden (CourtAssignment gate).
    const perSide = playersPerMatch / 2;
    if (
      Number.isInteger(perSide) &&
      perSide > 0 &&
      c.team1.length === perSide &&
      c.team2.length === perSide &&
      !c.winner &&
      !c.inProgress
    ) {
      markCourtMatchStarted(c);
    }
    // Started match (`inProgress` true) but roster no longer full (e.g. drag one player to waitlist):
    // keep inProgress / timer so score inputs stay available until the court is cleared or a score is recorded.
    c.gameCreation = markAltered(
      c.gameCreation,
      c.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
    );
  }
}

/** Place player on first open active-court slot; returns updated courts + court snapshot or null. */
function assignPlayerToFirstOpenActiveCourt(courts, playerId, playersPerSide) {
  for (const src of courts || []) {
    if ((src.team1?.length ?? 0) >= playersPerSide && (src.team2?.length ?? 0) >= playersPerSide) {
      continue;
    }
    const courtNumber = src.courtNumber;
    const teamKey = (src.team1?.length ?? 0) < playersPerSide ? 'team1' : 'team2';
    const updatedCourt = {
      ...src,
      team1: [...(src.team1 || [])],
      team2: [...(src.team2 || [])],
      gameCreation: markGameCreationAltered(
        src.gameCreation,
        src.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
      ),
    };
    updatedCourt[teamKey] = [...(updatedCourt[teamKey] || []), playerId];
    if (
      updatedCourt.team1.length === playersPerSide &&
      updatedCourt.team2.length === playersPerSide
    ) {
      markCourtMatchStarted(updatedCourt);
      updatedCourt.originalTeam1 = [...updatedCourt.team1];
      updatedCourt.originalTeam2 = [...updatedCourt.team2];
    }
    const updatedCourts = (courts || []).map(c =>
      c.courtNumber === courtNumber ? updatedCourt : c
    );
    return { updatedCourts, updatedCourt, courtNumber };
  }
  return null;
}

/** @returns {{ matchIndex: number, team: number, slot: number } | null} */
function parseUpcomingSlotDndId(id) {
  const m = String(id).match(/^upcoming-(\d+)-team(\d+)-slot-(\d+)$/);
  if (!m) return null;
  return { matchIndex: Number(m[1]), team: Number(m[2]), slot: Number(m[3]) };
}

function applyUpcomingSlotMutation(matches, from, to, playerId, maxPerTeam) {
  const fm = matches[from.matchIndex];
  const tm = matches[to.matchIndex];
  if (!fm || !tm) return false;
  const fk = from.team === 1 ? 'team1' : 'team2';
  const tk = to.team === 1 ? 'team1' : 'team2';
  const fArr = fm[fk];
  const tArr = fm === tm && fk === tk ? fArr : tm[tk];
  if (from.slot < 0 || from.slot >= fArr.length || fArr[from.slot] !== playerId) {
    return false;
  }
  if (to.slot < 0 || to.slot >= maxPerTeam) {
    return false;
  }
  const sameList = fm === tm && fk === tk;
  if (sameList && from.slot === to.slot) {
    return true;
  }
  const targetOccupied = to.slot < tArr.length;
  if (targetOccupied) {
    if (sameList) {
      const tmp = fArr[from.slot];
      fArr[from.slot] = fArr[to.slot];
      fArr[to.slot] = tmp;
      return true;
    }
    const otherId = tArr[to.slot];
    fArr[from.slot] = otherId;
    tArr[to.slot] = playerId;
    return true;
  }
  const insertAt = Math.min(to.slot, tArr.length);
  if (sameList) {
    fArr.splice(from.slot, 1);
    const adjustedInsert = from.slot < insertAt ? insertAt - 1 : insertAt;
    fArr.splice(adjustedInsert, 0, playerId);
    if (fArr.length > maxPerTeam) return false;
    return true;
  }
  fArr.splice(from.slot, 1);
  tArr.splice(insertAt, 0, playerId);
  if (fArr.length > maxPerTeam || tArr.length > maxPerTeam) return false;
  return true;
}

function touchUpcomingMatchesForDnD(matches, indices, markAltered) {
  const set = new Set(indices);
  for (let i = 0; i < matches.length; i++) {
    if (!set.has(i)) continue;
    const m = matches[i];
    if (!m) continue;
    m.gameCreation = markAltered(
      m.gameCreation,
      m.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
    );
  }
}

function placeWaitlistPlayerIntoUpcomingSlot(nu, tgt, playerId, maxPerTeam) {
  const tm = nu[tgt.matchIndex];
  if (!tm) return { ok: false };
  const tk = tgt.team === 1 ? 'team1' : 'team2';
  const tArr = tm[tk];
  if (tgt.slot < 0 || tgt.slot >= maxPerTeam) return { ok: false };
  if (tgt.slot < tArr.length) {
    const displaced = tArr[tgt.slot];
    tArr[tgt.slot] = playerId;
    return { ok: true, displaced };
  }
  const insertAt = Math.min(tgt.slot, tArr.length);
  if (tArr.length >= maxPerTeam) return { ok: false };
  tArr.splice(insertAt, 0, playerId);
  return { ok: true, displaced: null };
}

function applyActiveToUpcomingMutation(nc, nu, fromActive, toUpcoming, playerId, maxPerTeam) {
  const fc = nc.find((c) => c.courtNumber === fromActive.court);
  const tm = nu[toUpcoming.matchIndex];
  if (!fc || !tm) return false;
  const fk = fromActive.team === 1 ? 'team1' : 'team2';
  const tk = toUpcoming.team === 1 ? 'team1' : 'team2';
  const fArr = fc[fk];
  const tArr = tm[tk];
  if (fromActive.slot < 0 || fromActive.slot >= fArr.length || fArr[fromActive.slot] !== playerId) {
    return false;
  }
  if (toUpcoming.slot < 0 || toUpcoming.slot >= maxPerTeam) return false;
  const targetOccupied = toUpcoming.slot < tArr.length;
  if (targetOccupied) {
    const other = tArr[toUpcoming.slot];
    fArr[fromActive.slot] = other;
    tArr[toUpcoming.slot] = playerId;
    return true;
  }
  const insertAt = Math.min(toUpcoming.slot, tArr.length);
  fArr.splice(fromActive.slot, 1);
  tArr.splice(insertAt, 0, playerId);
  if (fArr.length > maxPerTeam || tArr.length > maxPerTeam) return false;
  return true;
}

function applyUpcomingToActiveMutation(nc, nu, fromUpcoming, toActive, playerId, maxPerTeam) {
  const fm = nu[fromUpcoming.matchIndex];
  const tc = nc.find((c) => c.courtNumber === toActive.court);
  if (!fm || !tc) return false;
  const fk = fromUpcoming.team === 1 ? 'team1' : 'team2';
  const tk = toActive.team === 1 ? 'team1' : 'team2';
  const fArr = fm[fk];
  const tArr = tc[tk];
  if (fromUpcoming.slot < 0 || fromUpcoming.slot >= fArr.length || fArr[fromUpcoming.slot] !== playerId) {
    return false;
  }
  if (toActive.slot < 0 || toActive.slot >= maxPerTeam) return false;
  const targetOccupied = toActive.slot < tArr.length;
  if (targetOccupied) {
    const other = tArr[toActive.slot];
    fArr[fromUpcoming.slot] = other;
    tArr[toActive.slot] = playerId;
    return true;
  }
  const insertAt = Math.min(toActive.slot, tArr.length);
  fArr.splice(fromUpcoming.slot, 1);
  tArr.splice(insertAt, 0, playerId);
  if (fArr.length > maxPerTeam || tArr.length > maxPerTeam) return false;
  return true;
}

function resolveRosterDragSource(activeData, courtAssignments, nextAssignments) {
  const playerId = activeData.playerId;
  if (playerId == null) return null;
  if (activeData.from === 'waitingList') return { kind: 'waitlist', playerId };

  const pu = parseUpcomingSlotDndId(activeData.from);
  if (pu) {
    const m = nextAssignments?.[pu.matchIndex];
    if (!m) return null;
    const tk = pu.team === 1 ? 'team1' : 'team2';
    if (m[tk]?.[pu.slot] !== playerId) return null;
    return { kind: 'upcoming', playerId, matchIndex: pu.matchIndex, team: pu.team, slot: pu.slot };
  }

  const pc = parseCourtSlotDndId(activeData.from);
  if (pc) {
    const c = courtAssignments?.find((x) => x.courtNumber === pc.court);
    if (!c) return null;
    const tk = pc.team === 1 ? 'team1' : 'team2';
    if (c[tk]?.[pc.slot] !== playerId) return null;
    return { kind: 'active', playerId, court: pc.court, team: pc.team, slot: pc.slot };
  }

  if (activeData.from?.startsWith?.('court-')) {
    const pt = parseCourtTeamDndId(activeData.from);
    if (pt) {
      const c = courtAssignments?.find((x) => x.courtNumber === pt.court);
      if (!c) return null;
      const tk = pt.team === 1 ? 'team1' : 'team2';
      const idx = c[tk]?.indexOf(playerId);
      if (idx == null || idx === -1) return null;
      return { kind: 'active', playerId, court: pt.court, team: pt.team, slot: idx };
    }
  }
  return null;
}

function parseRosterDropTarget(overId) {
  if (overId === 'waitingList') return { kind: 'waitlist' };
  const pu = parseUpcomingSlotDndId(overId);
  if (pu) return { kind: 'upcoming', ...pu };
  const pc = parseCourtSlotDndId(overId);
  if (pc) return { kind: 'active', ...pc };
  const pt = parseCourtTeamDndId(overId);
  if (pt) return { kind: 'activeTeam', ...pt };
  return null;
}

/** Whether a resolved drag source/target sits on a court or upcoming slot locked by a scheduled game. */
function isRosterEndpointLocked(endpoint, courtAssignments, nextAssignments, scheduledGames) {
  if (!endpoint) return false;
  if (endpoint.kind === 'active' || endpoint.kind === 'activeTeam') {
    const court = (courtAssignments || []).find((c) => c.courtNumber === endpoint.court);
    return isAssignmentRowLockedByScheduledGame(court, scheduledGames);
  }
  if (endpoint.kind === 'upcoming') {
    const match = (nextAssignments || [])[endpoint.matchIndex];
    return isAssignmentRowLockedByScheduledGame(match, scheduledGames);
  }
  return false;
}

// Helper for save debounce: compare waiting list and full court/upcoming lineups (all courts, team membership)
const sessionStateChanged = _sessionStateChanged;

/**
 * Normalized DB snapshots do not store post-score UI state (winner, score, Elo deltas),
 * match start timestamps (CourtTimer), or matchmaking debug payloads (court "?" tooltip).
 * Realtime / refetch replaces courts without those fields — merge restores them when the roster is unchanged.
 * @param {number|null} [playersPerSide] – when set, preserves live `inProgress` if DB briefly has `in_progress=false` for the same full roster (no winner).
 */
function finishEphemeralCourtMerge(remote, prevCourts, prevNext, timerOptions = {}) {
  const prevOnSameCourt = prevCourts.find((c) => c.courtNumber === remote.courtNumber);
  const rosterChangedOnCourt =
    prevOnSameCourt != null &&
    remote.inProgress &&
    !sameRoster(remote, prevOnSameCourt);
  const withTimer = mergeCourtTimerFromPrevious(prevOnSameCourt, remote, {
    restartTimer: timerOptions.restartTimer || rosterChangedOnCourt,
  });
  return mergeRelaxedMetaFromPreviousRows(withTimer, prevCourts, prevNext);
}

function mergeEphemeralCourtStateFromPrevious(
  remoteCourts,
  previousCourts,
  playersPerSide = null,
  previousNext = [],
) {
  if (!Array.isArray(remoteCourts)) {
    return remoteCourts;
  }
  const prevCourts = Array.isArray(previousCourts) ? previousCourts : [];
  const prevNext = Array.isArray(previousNext) ? previousNext : [];

  return remoteCourts.map((remote) => {
    const local =
      prevCourts.find((c) => sameCourtLineup(remote, c)) ??
      prevCourts.find((c) => sameRoster(remote, c)) ??
      prevNext.find((c) => sameRoster(remote, c));
    if (!local) {
      return finishEphemeralCourtMerge(remote, prevCourts, prevNext);
    }
    // Debounced save / snapshot can lag behind a live full court — avoid hiding score UI
    if (
      playersPerSide != null &&
      courtRosterSidesFull(remote, playersPerSide) &&
      courtRosterSidesFull(local, playersPerSide) &&
      local.inProgress &&
      !remote.inProgress &&
      !local.winner &&
      !remote.winner
    ) {
      return finishEphemeralCourtMerge(
        {
          ...remote,
          inProgress: true,
        },
        prevCourts,
        prevNext,
      );
    }
    // Never reattach a finished-match winner onto a court the DB marks as live (e.g. after RPC promotion).
    if (local.winner && !remote.winner && !remote.inProgress) {
      const localHasDebug = (local.matchmakingDebugInfo?.topCombinations?.length || 0) > 0;
      const remoteHasDebug = (remote.matchmakingDebugInfo?.topCombinations?.length || 0) > 0;
      return finishEphemeralCourtMerge(
        {
          ...remote,
          winner: local.winner,
          score: local.score,
          eloDeltas: local.eloDeltas,
          unbalancedInfo: local.unbalancedInfo,
          eloSnapshots: local.eloSnapshots,
          originalTeam1: local.originalTeam1,
          originalTeam2: local.originalTeam2,
          inProgress: local.inProgress,
          ...(localHasDebug && !remoteHasDebug
            ? { matchmakingDebugInfo: local.matchmakingDebugInfo }
            : {}),
        },
        prevCourts,
        prevNext,
      );
    }
    const ephemeral = {};
    // Prematch Elo freeze — not in DB snapshot; preserve across realtime refetch
    if (
      remote.inProgress &&
      local.eloSnapshots &&
      Object.keys(local.eloSnapshots).length > 0 &&
      (!remote.eloSnapshots || Object.keys(remote.eloSnapshots).length === 0)
    ) {
      ephemeral.eloSnapshots = local.eloSnapshots;
    }
    // Matchmaking "?" panel — not stored in DB; realtime snapshots drop it
    const localHasDebug = (local.matchmakingDebugInfo?.topCombinations?.length || 0) > 0;
    const remoteHasDebug = (remote.matchmakingDebugInfo?.topCombinations?.length || 0) > 0;
    if (localHasDebug && !remoteHasDebug) {
      ephemeral.matchmakingDebugInfo = local.matchmakingDebugInfo;
    }
    // Relaxed badge metadata — client-only; preserve when roster unchanged
    if (local.matchmakingRelaxed && !remote.matchmakingRelaxed) {
      ephemeral.matchmakingRelaxed = local.matchmakingRelaxed;
      ephemeral.matchmakingRelaxationDetails =
        local.matchmakingRelaxationDetails ?? remote.matchmakingRelaxationDetails;
    }
    if (Object.keys(ephemeral).length > 0) {
      return finishEphemeralCourtMerge({ ...remote, ...ephemeral }, prevCourts, prevNext);
    }
    return finishEphemeralCourtMerge(remote, prevCourts, prevNext);
  });
}

/** Returns { success, message? } or { success: true, courtsToUpdate } for promoting upcoming matches to courts. */
function computePromotionPlan(nextAssignments, courtAssignments, playersPerSide = 2) {
  const playersPerMatch = playersPerSide * 2;
  if (nextAssignments.length === 0) {
    return { success: false, message: 'No upcoming matches to promote' };
  }
  const totalAvailable = nextAssignments.flatMap(m => [...m.team1, ...m.team2]).length;
  if (totalAvailable < playersPerMatch) {
    return { success: false, message: 'Not enough players available to promote matches' };
  }
  // Any court with zero players can receive a promoted match. Requiring !inProgress caused
  // promote to no-op when DB/local state left in_progress=true on an empty court (e.g. sync edge cases).
  const emptyCourts = courtAssignments.filter(
    c => (c.team1?.length || 0) === 0 && (c.team2?.length || 0) === 0
  );
  if (emptyCourts.length === 0) {
    return { success: false, message: 'No empty courts available to promote matches' };
  }
  const courtsToUpdate = [];
  const n = Math.min(nextAssignments.length, emptyCourts.length);
  for (let i = 0; i < n; i++) {
    const match = nextAssignments[i];
    const court = emptyCourts[i];
    if (match.team1.length === playersPerSide && match.team2.length === playersPerSide) {
      courtsToUpdate.push({
        courtNumber: court.courtNumber,
        team1: [...match.team1],
        team2: [...match.team2],
        inProgress: true,
        gameCreation: ensureGameCreationMeta(match.gameCreation, GAME_CREATION_ORIGIN.MANUAL)
      });
    }
  }
  if (courtsToUpdate.length === 0) {
    return {
      success: false,
      message: `No complete matches to promote (need ${playersPerMatch} players per match)`,
    };
  }
  return { success: true, courtsToUpdate };
}

const VALID_COURT_LAYOUTS = new Set(['1', '2', '3', '4', 'auto']);

/** Host UI for competitive sessions: upcoming queue slots 0–2 (adjust + persist use the same cap). */
const MAX_HOST_UPCOMING_QUEUE_SLOTS = 2;

/** sessions.upcoming_queue_slot_count when present; else infer from next_up row count (pre-migration). */
function dbUpcomingSlotCap(savedState, remoteNextLen) {
  const raw = savedState?.upcoming_queue_slot_count;
  if (
    typeof raw === 'number' &&
    raw >= 0 &&
    raw <= MAX_HOST_UPCOMING_QUEUE_SLOTS
  ) {
    return raw;
  }
  return Math.max(
    0,
    Math.min(MAX_HOST_UPCOMING_QUEUE_SLOTS, remoteNextLen || 0)
  );
}

/**
 * Slice next_assignments to cap; append dropped player ids to excessIdList (deduped).
 * Used when applying DB snapshots so row count matches host slot preference.
 */
function trimNextAssignmentsForUpcomingCap(assignments, cap, excessIdList) {
  if (!assignments?.length || assignments.length <= cap) return assignments;
  const dropped = assignments.slice(cap);
  dropped.forEach((m) => {
    [...(m.team1 || []), ...(m.team2 || [])].forEach((id) => {
      if (id && !excessIdList.includes(id)) excessIdList.push(id);
    });
  });
  return assignments.slice(0, cap);
}

/** Empty upcoming slot for padding when filling Next on court 2+ before lower indices exist. */
function emptyUpcomingMatchSlot(courtNumber) {
  return {
    courtNumber,
    team1: [],
    team2: [],
    inProgress: false,
    winner: null,
    score: null,
    matchmakingRelaxed: false,
    matchmakingRelaxationDetails: undefined,
    matchmakingDebugInfo: undefined,
    gameCreation: ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL),
  };
}

/** Per-viewer preference (localStorage only; does not affect other users or session state). */
function getCourtLayoutStorageKey(userId, sessionIdVal) {
  if (userId) return `competitive_session_court_layout:user:${userId}`;
  if (sessionIdVal) return `competitive_session_court_layout:session:${sessionIdVal}`;
  return null;
}

/** Court cards always use compact spacing (no user toggle). */
const COURT_GRID_DENSITY = 'compact';

/** Host expand/collapse for Scheduled / Partnerships / Player sections (JSON in localStorage). */
function getHostSectionsStorageKey(userId, sessionIdVal) {
  if (userId) return `competitive_session_host_sections:user:${userId}`;
  if (sessionIdVal) return `competitive_session_host_sections:session:${sessionIdVal}`;
  return null;
}

const CompetitiveSessionPage = ({ 
  currentUser, 
  selectedClub, 
  sessionId, 
  selectedSetup, 
  isSessionHost = true, 
  initialSession = null,
  sessionCreator = null,
  isClubAdmin = false,
  registerMobileHeaderAction = null,
  isPegBoardSession: isPegBoardSessionProp = false,
}) => {
  const navigate = useNavigate();
  const { season, loading: seasonLoading, setSeason } = useSeason();
  const { addToast } = useToast();
  const sessionCommands = useSessionCommands(sessionId);

  const tabInstanceId = useSessionTabInstanceId();
  
  // Note: Excessive performance logging and mount tracking has been disabled
  // to prevent performance issues. Re-enable in debugConfig.js if needed for debugging.
  
  const [activeTab, setActiveTab] = useState('courts'); // 'courts', 'ranking', 'games', 'playerRanking', 'logGame'
  /* eslint-disable no-unused-vars -- reserved for future use */
  const [gamesRefreshKey, setGamesRefreshKey] = useState(0);
  const [rankingRefreshKey, setRankingRefreshKey] = useState(0);
  const [_clubStrength, setClubStrength] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const matchFormat = useMemo(() => getMatchFormat(sessionData), [sessionData]);
  const isPegBoard = isPegBoardSessionProp || isPegBoardSession(sessionData) || isPegBoardSession(initialSession);
  const offlineRosterBridgeRef = useRef(null);
  const liveStateRef = useRef({});
  const offlineQueue = useSessionOfflineQueue({
    sessionId,
    enabled: isSessionHost && !isPegBoard,
    tabInstanceId,
    clubId: selectedClub?.id || null,
  });

  const joinWaitlistCmd = useCallback((sid, playerId, tid) => (
    runHostCommandWithOfflineFallback({
      offlineQueue,
      session: sessionData,
      type: OFFLINE_COMMAND_TYPES.JOIN_WAITLIST,
      payload: { playerId },
      start: () => joinWaitlistCmdRemote(sid, playerId, tid),
      snapshot: liveStateRef.current,
    })
  ), [offlineQueue, sessionData]);

  const leaveWaitlistCmd = useCallback((sid, playerId, tid) => (
    runHostCommandWithOfflineFallback({
      offlineQueue,
      session: sessionData,
      type: OFFLINE_COMMAND_TYPES.LEAVE_WAITLIST,
      payload: { playerId },
      start: () => leaveWaitlistCmdRemote(sid, playerId, tid),
      snapshot: liveStateRef.current,
    })
  ), [offlineQueue, sessionData]);

  const reorderWaitlistCmd = useCallback((sid, orderedPlayerIds, tid) => (
    runHostCommandWithOfflineFallback({
      offlineQueue,
      session: sessionData,
      type: OFFLINE_COMMAND_TYPES.REORDER_WAITLIST,
      payload: { orderedPlayerIds },
      start: () => reorderWaitlistCmdRemote(sid, orderedPlayerIds, tid),
      snapshot: liveStateRef.current,
    })
  ), [offlineQueue, sessionData]);

  const assignPlayersToCourtCmd = useCallback((sid, courtNumber, courtType, expectedVersion, courtData, tid) => (
    runHostCommandWithOfflineFallback({
      offlineQueue,
      session: sessionData,
      type: OFFLINE_COMMAND_TYPES.SEAT,
      payload: {
        courtType,
        index: courtNumber,
        team1: courtData?.team1 || [],
        team2: courtData?.team2 || [],
        inProgress: courtData?.inProgress === true,
        gameCreation: courtData?.gameCreation || {},
        expectedVersion,
      },
      start: () => assignPlayersToCourtCmdRemote(sid, courtNumber, courtType, expectedVersion, courtData, tid),
      snapshot: liveStateRef.current,
    })
  ), [offlineQueue, sessionData]);

  const requeueUnplayedPlayers = useCallback(
    (waitlist, playerIds) => returnPlayersToWaitlist(waitlist, playerIds, { toFront: isPegBoard }),
    [isPegBoard]
  );
  const scoringConfig = useSessionScoringConfig(sessionData);
  const playersPerSideForSession = useMemo(() => getPlayersPerSide(matchFormat), [matchFormat]);
  const playersPerMatchForSession = useMemo(() => getPlayersPerMatch(matchFormat), [matchFormat]);
  const [_clubStrengthLoading, setClubStrengthLoading] = useState(false);
  const [_clubStrengthError, setClubStrengthError] = useState(null);
  const [showOfflineModeConfirm, setShowOfflineModeConfirm] = useState(false);
  const [enablingOfflineMode, setEnablingOfflineMode] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [scheduledGameCancelTarget, setScheduledGameCancelTarget] = useState(null);
  const [endingSession, setEndingSession] = useState(false);
  const [endSessionError, setEndSessionError] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [ownPresenceLoading, setOwnPresenceLoading] = useState(false);
  /** Where “Add player” was opened from: signed_up vs playing waitlist */
  const [addPlayerEntryTarget, setAddPlayerEntryTarget] = useState('signed_up');
  /** Pre-fill guest form when opened from add-player search */
  const [guestNamePrefill, setGuestNamePrefill] = useState({ firstName: '', lastName: '' });
  /** After first bulk start-matchmaking, new adds go straight to waitlist */
  const [matchmakingStarted, setMatchmakingStarted] = useState(false);
  const [startMatchmakingLoading, setStartMatchmakingLoading] = useState(false);
  const [pegBoardConfirming, setPegBoardConfirming] = useState(false);
  const [pegBoardPickModalOpen, setPegBoardPickModalOpen] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showAddPartnershipModal, setShowAddPartnershipModal] = useState(false);
  /** Courts tab right sidebar: each card expands/collapses independently */
  const [courtsSidebarExpanded, setCourtsSidebarExpanded] = useState({
    waitlist: false,
    partnerships: false,
    scheduled: false,
    gamePreference: false,
  });
  const courtsSidebarDefaultsAppliedRef = useRef(false);
  /** Courts tab: filter chips / signed-up list by name for hosts */
  const [courtsWaitlistSearchQuery, setCourtsWaitlistSearchQuery] = useState('');
  const [courtsHostMenuPlayerId, setCourtsHostMenuPlayerId] = useState(null);

  const defaultAddEntryTarget = matchmakingStarted ? 'active' : 'signed_up';

  const openAddPlayerModal = useCallback(() => {
    setAddPlayerEntryTarget(defaultAddEntryTarget);
    setShowAddPlayerModal(true);
  }, [defaultAddEntryTarget]);

  const closeAddGuestModal = useCallback(() => {
    setShowAddPlayer(false);
    setGuestNamePrefill({ firstName: '', lastName: '' });
    setAddPlayerEntryTarget(defaultAddEntryTarget);
    setShowAddPlayerModal(true);
  }, [defaultAddEntryTarget]);

  const openAddGuestModal = useCallback((prefill = { firstName: '', lastName: '' }) => {
    setShowAddPlayerModal(false);
    setGuestNamePrefill({
      firstName: prefill.firstName || '',
      lastName: prefill.lastName || '',
    });
    setAddPlayerEntryTarget(defaultAddEntryTarget);
    setShowAddPlayer(true);
  }, [defaultAddEntryTarget]);
  const [_seasonOpLoading, setSeasonOpLoading] = useState(false);
  const [_seasonOpError, setSeasonOpError] = useState(null);
  
  // State for matchmaking mode
  const [gameTypeRestriction, setGameTypeRestriction] = useState('all_types'); // Game type restriction setting
  // No deferral system - removed for scheduled games feature
  const [_seasonOpSuccess, setSeasonOpSuccess] = useState(null);
  const [totalGamesCompleted, setTotalGamesCompleted] = useState(0); // Track total games for randomization
  const [clubRole, setClubRole] = useState(null);
  const [_showManageCourts, setShowManageCourts] = useState(false);
  const [showLatePenaltyModal, setShowLatePenaltyModal] = useState(false); // Late penalty modal state
  const [penaltyModalPlayer, setPenaltyModalPlayer] = useState(null); // Player for penalty modal
  const [penaltySuggestion, setPenaltySuggestion] = useState({
    suggestedPenalty: 0,
    averageGames: 0,
    playerGamesPlayed: 0,
    isReturningPlayer: false,
  });
  /** Host-only: “who’s next on court” while a score saves (first upcoming slot snapshot). */
  const [scoreRecordingNextUpModal, setScoreRecordingNextUpModal] = useState(null);
  const [otherHostCount, setOtherHostCount] = useState(0);
  const [courtLayout, setCourtLayout] = useState('4'); // per-user grid density; hosts/admins only (see getCourtLayoutStorageKey)
  const [isSetupGuideExpanded, setIsSetupGuideExpanded] = useState(false); // Collapsible setup guide
  const [liveSessionStatus, setLiveSessionStatus] = useState(null);
  const [reopeningSession, setReopeningSession] = useState(false);
  const [playerStreaks, setPlayerStreaks] = useState({}); // Track win/loss streaks for matchmaking
  const [playerInteractions, setPlayerInteractions] = useState({}); // Track player interactions for escalating penalties (plain object, not Map)
  /* eslint-enable no-unused-vars */
  // Matchmaking mode removed - only using Two-Stage mode now

  const handleCourtLayoutChange = useCallback(
    (option) => {
      setCourtLayout(option);
      const key = getCourtLayoutStorageKey(currentUser?.id, sessionId);
      if (!key || typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(key, option);
      } catch {
        /* quota / private mode */
      }
    },
    [currentUser?.id, sessionId]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = getCourtLayoutStorageKey(currentUser?.id, sessionId);
    if (!key) return;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw && VALID_COURT_LAYOUTS.has(raw)) {
        setCourtLayout(raw);
      }
    } catch {
      /* ignore */
    }
  }, [currentUser?.id, sessionId]);

  const courtLayoutSegmentButtons = useMemo(
    () => (
      <div
        className="inline-flex shrink-0 rounded-lg border border-border-medium bg-surface-subtle p-0.5 shadow-sm"
        role="group"
        aria-label="Courts per row on this device"
      >
        {['1', '2', '3', '4'].map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => handleCourtLayoutChange(option)}
            className={`h-8 w-8 rounded-md text-xs font-medium transition-colors ${
              courtLayout === option || (courtLayout === 'auto' && option === '1')
                ? 'bg-primary text-text-inverse shadow-sm'
                : 'text-text-secondary hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10'
            }`}
            title={`${option} court${option === '1' ? '' : 's'} per row (your display only)`}
          >
            {option}
          </button>
        ))}
      </div>
    ),
    [courtLayout, handleCourtLayoutChange]
  );

  // Initialize session logger for database persistence (read-only for non-hosts to avoid 403 on session_players updates)
  const sessionLogger = useMemo(() => {
    return sessionId ? new SimpleSessionLogger(sessionId, { readOnly: !isSessionHost }) : null;
  }, [sessionId, isSessionHost]);

  const { partnerships, setPartnerships, partnershipLookup, loadSessionPartnerships, handleUpdatePartnerships } = useSessionPartnerships({
    sessionId,
    offlineQueue,
    session: sessionData,
  });

  const {
    scheduledGames,
    setScheduledGames,
    loadScheduledGames,
    createScheduledGame: createScheduledGameFromHook,
    removeScheduledGame,
    updateScheduledGameStatus,
    reorderScheduledGames,
    showAddScheduledGameModal,
    setShowAddScheduledGameModal,
  } = useScheduledGames({
    sessionId,
    clubId: selectedClub?.id,
    currentUserId: currentUser?.id,
    addToast,
    offlineQueue,
    session: sessionData,
    getSnapshot: () => liveStateRef.current,
    getPlayersById: () => liveStateRef.current?.playersById || {},
  });

  const openScheduledGameCancelFromRow = useCallback(
    (row, cancelTrigger) => {
      const game = resolveScheduledGameForAssignmentRow(row, scheduledGames);
      if (!game) {
        addToast({ message: SCHEDULED_GAME_ROSTER_LOCKED_MESSAGE, variant: 'warning', duration: 6000 });
        return;
      }
      setScheduledGameCancelTarget({ ...game, cancelTrigger });
    },
    [scheduledGames, addToast],
  );

  useEffect(() => {
    logScoreSubmitDebugReady('CompetitiveSessionPage');
  }, []);

  // Use initialSession from SessionDashboard when provided to avoid re-fetch; otherwise fetch session
  useEffect(() => {
    if (!sessionId) return;

    const applySessionData = (data) => {
      setSessionData(data);
      if (data?.session_status) setLiveSessionStatus(data.session_status);
      const singles = isSinglesFormat(getMatchFormat(data));
      categorisation.setCategorizationEnabled(
        Boolean(data?.enable_player_categorisation) && !singles
      );
      if (data?.game_type_restriction) setGameTypeRestriction(data.game_type_restriction);
    };

    const runPostSessionSetup = async (data) => {
      if (data?.offline_mode === true || shouldHoldRemoteUpcoming(offlineQueue, data)) {
        return;
      }
      await loadSessionPartnerships();
      await loadScheduledGames();
    };

    if (initialSession?.id === sessionId) {
      setSessionLoading(true);
      applySessionData(initialSession);
      runPostSessionSetup(initialSession).finally(() => setSessionLoading(false));
      return;
    }

    // No initial session: fetch session data
    let cancelled = false;
    setSessionLoading(true);
    (async function fetchSessionData() {
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select(`
            *,
            session_types:session_type_id (
              id,
              name,
              description
            )
          `)
          .eq('id', sessionId)
          .single();

        if (cancelled) return;
        if (error) throw error;

        applySessionData(data);
        await runPostSessionSetup(data);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching session data:', err);
        }
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, initialSession?.id]);

  useEffect(() => {
    async function fetchClubRole() {
      if (!currentUser?.id || !selectedClub?.id) {
        setClubRole(null);
        return;
      }
      const { data } = await supabase
        .from('players_club')
        .select('club_role')
        .eq('player_id', currentUser.id)
        .eq('club_id', selectedClub.id)
        .single();
      setClubRole(data?.club_role || null);
    }
    fetchClubRole();
  }, [currentUser?.id, selectedClub?.id]);

  const isAdmin =
    currentUser?.role === 'platform_admin' ||
    currentUser?.role === 'club_owner' ||
    clubRole === 'admin';

  // Use our custom hooks
  const {
    players = [],
    playersById, // <-- ensure this is available from usePlayerManagement
    activePlayers = [],
    togglePlayerStatus,
    bulkToggleStatus,
    loading: playersLoading,
    setPlayers,
    fetchPlayers,
    fetchSessionPlayerStatus,
    patchSessionPlayerFromRow,
    sessionPlayerStatus,
    setSessionPlayerStatus,
    sessionPlayerExitReasons,
    sessionPlayerPenalties,
    sessionPlayerScheduledGames,
    setSessionPlayerScheduledGames,
    setSessionPlayerPenalties,
    setPlayerSessionStatus: setPlayerSessionStatusOriginal
  } = usePlayerManagement(selectedClub?.id, sessionId, season?.id, { offlineRosterBridgeRef });

  // Wrap setPlayerSessionStatus to skip individual DB updates when using normalized state
  // The batch save (sessionLogger.saveCurrentState) handles all updates
  const setPlayerSessionStatus = useCallback(async (playerId, status, exitReason) => {
    // Check if using normalized state
    const useNormalized = sessionLogger?.useNormalizedState;
    
    if (useNormalized) {
      // Skip individual DB update - batch save will handle it
      return Promise.resolve();
    }
    
    // Use original function for JSONB system
    return setPlayerSessionStatusOriginal(playerId, status, exitReason);
  }, [sessionLogger, setPlayerSessionStatusOriginal]);
  // Note: Force waiting list recalculation on mount has been removed.
  // This was causing duplicates when loading from database.
  // The database state should already be correct and doesn't need recalculation.

  // Function to fetch games played count from database AND calculate streaks
  const fetchGamesPlayedCount = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      
      // Get all games for this session with full details
      const { data: gameData, error } = await supabase
        .from('session_games')
        .select(`
          id,
          winning_team,
          created_at,
          players_session_games(player_id, team_number)
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('❌ Failed to fetch games data:', error);
        return;
      }
      
      
      // Calculate both games count AND streaks from game history
      const gamesCount = {};
      const streaks = {};
      
      if (gameData && gameData.length > 0) {
        gameData.forEach(game => {
          // Count games per player
          (game.players_session_games || []).forEach(pg => {
            const playerId = pg.player_id;
          gamesCount[playerId] = (gamesCount[playerId] || 0) + 1;
            
            // Initialize streak if needed
            if (!streaks[playerId]) {
              streaks[playerId] = {
                wins: 0,
                losses: 0,
                currentWinStreak: 0,
                currentLossStreak: 0
              };
            }
            
            // Check if player won or lost
            const playerWon = pg.team_number === game.winning_team;
            
            if (playerWon) {
              streaks[playerId].wins++;
              streaks[playerId].currentWinStreak++;
              streaks[playerId].currentLossStreak = 0; // Reset loss streak
            } else {
              streaks[playerId].losses++;
              streaks[playerId].currentLossStreak++;
              streaks[playerId].currentWinStreak = 0; // Reset win streak
            }
          });
        });
      }
      
      
      // Update states
      setGamesPlayedCount(gamesCount);
      setPlayerStreaks(streaks);
    } catch (err) {
      console.error('❌ Error fetching games played count:', err);
    }
  }, [sessionId]);

  /** Latest placements for sync handlers (useCourtManagement state may lag one render in closures). */
  const scheduledGamesPlacementRef = useRef(scheduledGames);
  const courtAssignmentsPlacementRef = useRef([]);
  const nextAssignmentsPlacementRef = useRef([]);

  useEffect(() => {
    scheduledGamesPlacementRef.current = scheduledGames;
  }, [scheduledGames]);

  // Rebuild waitlist scheduled badges from DB source of truth after refresh.
  useEffect(() => {
    setSessionPlayerScheduledGames(buildSessionPlayerScheduledGamesMap(scheduledGames));
  }, [scheduledGames, setSessionPlayerScheduledGames]);

  const consumeScheduledGamesPlaced = useCallback(
    async (rawIds, options = {}) => {
      if (!isSessionHost || !sessionId) return;
      const uniq = [...new Set((rawIds || []).map((id) => String(id).trim()).filter(Boolean))];
      if (uniq.length === 0) return;
      // Clear matchmaking-exclusion flags for these players. Must happen before the
      // status update so the realtime refresh sees them as no longer reserved.
      setSessionPlayerScheduledGames((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const pid of Object.keys(next)) {
          const v = next[pid];
          if (v != null && uniq.includes(String(v))) {
            delete next[pid];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
      // Mark as placed (greyed-out in sidebar) instead of deleting. The row is
      // kept for history and remains visible so the host knows the game is in progress.
      // DB sets status=placed via place_scheduled_game / finish_session_match promotion.
      if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) {
        const completedIds = new Set((options.completedIds || []).map(String));
        const placedIds = new Set((options.placedIds || uniq).map(String));
        setScheduledGames((prev) =>
          (prev || [])
            .filter((game) => !completedIds.has(String(game.id)))
            .map((game) => (placedIds.has(String(game.id)) ? { ...game, status: 'placed' } : game))
        );
        return;
      }
      await loadScheduledGames();
      await fetchPlayers(undefined, undefined, { silent: true });
    },
    [isSessionHost, sessionId, loadScheduledGames, setSessionPlayerScheduledGames, fetchPlayers, offlineQueue, sessionData, setScheduledGames],
  );

  /** Filled after hooks below; invoked from useCourtManagement after auto-rotation from score. */
  const afterAutoRotateFromScoreRef = useRef(() => Promise.resolve());
  const runAfterAutoRotateFromScore = useCallback(
    () => afterAutoRotateFromScoreRef.current(),
    []
  );

  const afterFinishMatchSyncRef = useRef(() => Promise.resolve());
  const onAfterFinishMatchSync = useCallback(
    (syncContext) => afterFinishMatchSyncRef.current(syncContext),
    [],
  );

  // Prevents redundant concurrent full reloads when multiple sources (session_courts DELETE
  // + session_games INSERT) fire within the same transaction window. Both load identical
  // committed DB state so coalescing is safe. A pending-authoritative flag ensures we don't
  // drop an ignoreSaveGuard:true upgrade if a non-authoritative sync is already in-flight.
  const fullSyncInFlightRef = useRef(false);
  const fullSyncPendingAuthoritativeRef = useRef(false);
  const sessionSyncCoordinatorRef = useRef(null);
  if (sessionSyncCoordinatorRef.current == null) {
    sessionSyncCoordinatorRef.current = new SessionSyncCoordinator();
  }
  useEffect(() => {
    const coord = sessionSyncCoordinatorRef.current;
    if (!coord) return undefined;
    return () => {
      coord.dispose();
    };
  }, []);
  /**
   * Bumped whenever a structural server command (finish_session_match, etc.) begins its
   * authoritative reload.  The debounced-save callback captures this value at scheduling
   * time and skips the write if it has changed by the time the timer fires — prevents a
   * pre-RPC layout snapshot from overwriting the server-corrected state.
   */
  const commandEpochRef = useRef(0);
  /**
   * True while an authoritative full-reload is executing.  Suppresses granular realtime
   * row merges so half-applied multi-row RPCs don't produce a corrupted intermediate view.
   */
  const structuralSyncInFlightRef = useRef(false);
  /**
   * True from when score preparation begins until the score RPC completes.
   * While set: new debounced next_up writes are deferred (re-queued as dirty),
   * and realtime next_up row merges are suppressed so the DB stays stable under
   * the score transition.  Released in the finally block of updateMatchResultHandler.
   */
  const scorePrepInFlightRef = useRef(false);
  /** Recent score transition context — structural resyncs coalesce into targeted post-score refresh. */
  const lastPostScoreSyncContextRef = useRef(null);
  /**
   * Highest layout_revision applied to this tab's local state.
   * Set by applyScoreCompletedResult; used for idempotency gate.
   */
  const localLayoutRevisionRef = useRef(0);
  /**
   * Holds the real applyScoreCommandResult implementation, assigned after
   * useCourtManagement runs (setCourtAssignments etc. are available).
   * The stable onApplyScoreCommandResult wrapper below reads from this ref.
   */
  const applyScoreCommandResultRef = useRef(null);
  const onApplyScoreCommandResult = useCallback(
    (result) => {
      if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;
      applyScoreCommandResultRef.current?.(result);
    },
    [offlineQueue, sessionData],
  );

  const POST_SCORE_SYNC_CONTEXT_TTL_MS = 4000;

  const buildPostScoreSyncOptions = useCallback((syncContext) => ({
    ignoreSaveGuard: true,
    forceApplyRemoteNextUp: true,
    syncProfile: 'postScore',
    syncContext: syncContext || null,
    useTargetedPostScore: Boolean(syncContext),
    debounceMs: 0,
  }), []);

  const getRecentPostScoreSyncOptions = useCallback(() => {
    const last = lastPostScoreSyncContextRef.current;
    if (!last?.context || !last.at) return null;
    if (Date.now() - last.at > POST_SCORE_SYNC_CONTEXT_TTL_MS) return null;
    return buildPostScoreSyncOptions(last.context);
  }, [buildPostScoreSyncOptions]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const [waitlistDragOverlayPlayerId, setWaitlistDragOverlayPlayerId] = useState(null);
  const handleRosterDragStart = useCallback((event) => {
    const d = event.active?.data?.current;
    if (d?.from === 'waitingList' && d?.playerId) {
      setWaitlistDragOverlayPlayerId(d.playerId);
    }
  }, []);
  const clearWaitlistDragOverlay = useCallback(() => setWaitlistDragOverlayPlayerId(null), []);

  // Note: Redundant monitoring useEffects have been removed.
  // These were causing infinite loops and state corruption.

  // Separate state for games played count (doesn't interfere with main player data)
  const [gamesPlayedCount, setGamesPlayedCount] = useState({});
  const gamesPlayedCountRef = useRef({});
  const playerStreaksRef = useRef({});
  useEffect(() => {
    gamesPlayedCountRef.current = gamesPlayedCount;
  }, [gamesPlayedCount]);
  useEffect(() => {
    playerStreaksRef.current = playerStreaks;
  }, [playerStreaks]);

  // Create computed players data with games played count for UI display
  const playersByIdWithGames = useMemo(() => {
    const result = { ...playersById };
    
    Object.keys(result).forEach(playerId => {
      if (result[playerId]) {
        // Prefer DB-fetched count; when not yet available use base player data so we don't show 0 or stale lower value (e.g. on Next On Court cards)
        const fetchedGames = gamesPlayedCount[playerId];
        const baseGames = result[playerId].gamesPlayedThisSession ?? 0;
        // Use DB count when available; otherwise base. Never show less than the higher of both (fixes "one less" when DB is briefly stale after add-to-court/upcoming or game completion)
        const fromDb = fetchedGames !== undefined && fetchedGames !== null ? fetchedGames : null;
        const actualGames = fromDb !== null
          ? Math.max(fromDb, baseGames)
          : baseGames;
        
        // Use the stored late_joiner_penalty instead of calculating dynamically
        const penalty = sessionPlayerPenalties[playerId] || 0;
        const effectiveGamesPlayed = actualGames + penalty;
        
        result[playerId] = {
          ...result[playerId],
          gamesPlayedThisSession: actualGames,
          lateJoinerPenalty: penalty,
          effectiveGamesPlayed: effectiveGamesPlayed,
          scheduled_game_id: sessionPlayerScheduledGames[playerId] || null
        };
      }
    });
    return result;
  }, [playersById, gamesPlayedCount, sessionPlayerPenalties, sessionPlayerScheduledGames]);

  const categorisation = usePlayerCategorisation({
    sessionId,
    sessionLogger,
    playersById: playersByIdWithGames,
    sessionPlayerStatus,
    gamesPlayedCount,
    initialCategorizationEnabled: Boolean(initialSession?.enable_player_categorisation),
  });

  /** Debounce source of truth (synced in useLayoutEffect below). */
  const latestStateRef = useRef({
    courtAssignments: null,
    nextAssignments: null,
    waitingList: null,
    playerWaitMetrics: {},
  });

  const openScoreRecordingNextUpPreview = useCallback((payload) => {
    setScoreRecordingNextUpModal(payload ?? null);
  }, []);
  const closeScoreRecordingNextUpPreview = useCallback(() => {
    setScoreRecordingNextUpModal(null);
  }, []);

  const onApplyOptimisticPostScore = useCallback((syncContext, optimistic) => {
    commandEpochRef.current += 1;
    // optimistic is null when onApplyScoreCommandResult handled the state update instead
    if (optimistic != null) {
      latestStateRef.current = {
        ...latestStateRef.current,
        courtAssignments: optimistic.courtAssignments,
        nextAssignments: optimistic.nextAssignments,
      };
    }
  }, []);


  const onScoreStatsIncrement = useCallback(({ team1, team2, winningTeam }) => {
    const { gamesPlayedCount: nextCounts, playerStreaks: nextStreaks } =
      incrementGamesPlayedAndStreaks(
        gamesPlayedCountRef.current,
        playerStreaksRef.current,
        { team1, team2, winningTeam },
      );
    gamesPlayedCountRef.current = nextCounts;
    playerStreaksRef.current = nextStreaks;
    setGamesPlayedCount(nextCounts);
    setPlayerStreaks(nextStreaks);
  }, []);

  const freshRotationHighlightTimeoutRef = useRef(null);
  const [freshRotationHighlightCourtNumber, setFreshRotationHighlightCourtNumber] = useState(null);
  const scheduleFreshRotationCourtHighlight = useCallback((courtNumber) => {
    if (courtNumber == null || courtNumber === '') return;
    const n = Number(courtNumber);
    if (!Number.isFinite(n)) return;
    if (freshRotationHighlightTimeoutRef.current) {
      clearTimeout(freshRotationHighlightTimeoutRef.current);
    }
    setFreshRotationHighlightCourtNumber(n);
    freshRotationHighlightTimeoutRef.current = setTimeout(() => {
      freshRotationHighlightTimeoutRef.current = null;
      setFreshRotationHighlightCourtNumber(null);
    }, 10000);
  }, []);

  useEffect(() => {
    return () => {
      if (freshRotationHighlightTimeoutRef.current) {
        clearTimeout(freshRotationHighlightTimeoutRef.current);
      }
    };
  }, []);

  const matchPreferencesEnabled = useMemo(
    () => !isPegBoard && sessionData?.match_preferences_enabled !== false,
    [isPegBoard, sessionData?.match_preferences_enabled],
  );
  const uiPartnerships = useMemo(
    () => (isPegBoard ? [] : partnerships),
    [isPegBoard, partnerships],
  );
  const uiPartnershipLookup = useMemo(
    () => (isPegBoard ? {} : partnershipLookup),
    [isPegBoard, partnershipLookup],
  );
  const uiScheduledGames = useMemo(
    () => (isPegBoard ? [] : scheduledGames),
    [isPegBoard, scheduledGames],
  );
  const uiSessionPlayerScheduledGames = useMemo(
    () => (isPegBoard ? {} : sessionPlayerScheduledGames),
    [isPegBoard, sessionPlayerScheduledGames],
  );

  const cooldownEnabled = useMemo(
    () => sessionData?.cooldown_enabled === true,
    [sessionData?.cooldown_enabled],
  );

  // Stable wrappers — real implementations assigned below once debounce refs exist.
  const acquireScorePrepLockRef = useRef(null);
  const releaseScorePrepLockRef = useRef(null);
  const invokeAcquireScorePrepLock = useCallback(async () => {
    await acquireScorePrepLockRef.current?.();
  }, []);
  const invokeReleaseScorePrepLock = useCallback(() => {
    releaseScorePrepLockRef.current?.();
  }, []);

  const handleMatchPreferencesEnabledToggle = useCallback(async () => {
    if (!sessionId) return;
    const current = sessionData?.match_preferences_enabled !== false;
    const next = !current;
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ match_preferences_enabled: next })
        .eq('id', sessionId);
      if (error) throw error;
      setSessionData((prev) => (prev ? { ...prev, match_preferences_enabled: next } : prev));
    } catch (e) {
      console.error('Failed to update match preferences setting:', e);
    }
  }, [sessionId, sessionData?.match_preferences_enabled]);

  const handleCooldownEnabledToggle = useCallback(async () => {
    if (!sessionId) return;
    const current = sessionData?.cooldown_enabled === true;
    const next = !current;
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ cooldown_enabled: next })
        .eq('id', sessionId);
      if (error) throw error;
      setSessionData((prev) => (prev ? { ...prev, cooldown_enabled: next } : prev));
    } catch (e) {
      console.error('Failed to update cooldown setting:', e);
    }
  }, [sessionId, sessionData?.cooldown_enabled]);

  const isHallPhone = Boolean(
    isSessionHost
    && !isPegBoard
    && (offlineQueue.offlineMode || offlineQueue.record?.offlineMode)
  );

  const showRemoteOfflineModeScreen = Boolean(
    sessionData?.offline_mode
    && !isHallPhone
    && (!isSessionHost || offlineQueue.hydrated)
  );

  const handleEnableOfflineMode = useCallback(async () => {
    if (!sessionId || isPegBoard) return;
    setEnablingOfflineMode(true);
    try {
      if (offlineQueue.setOfflineMode) {
        await offlineQueue.setOfflineMode(true);
      }
      setSessionData((prev) => (prev ? { ...prev, offline_mode: true } : prev));
      const { error } = await supabase
        .from('sessions')
        .update({ offline_mode: true })
        .eq('id', sessionId);
      if (error) throw error;
    } catch (error) {
      console.warn('[offline-mode] Failed to write sessions.offline_mode:', error);
      addToast({
        message: 'Offline mode is on this phone. Other phones may not see that yet.',
        variant: 'warning',
        duration: 6000,
      });
    } finally {
      setEnablingOfflineMode(false);
    }
  }, [addToast, isPegBoard, offlineQueue, sessionId]);

  const getOfflineLiveState = useCallback(() => liveStateRef.current, []);

  const {
    courtAssignments,
    setCourtAssignments,
    nextAssignments,
    setNextAssignments,
    waitingList,
    setWaitingList,
    pausedList,
    setPausedList,
    numberOfCourts,
    setNumberOfCourts,
    userSetNumberOfCourts,
    numberOfWaitingCourts,
    numberOfUpcomingCourts,
    setNumberOfUpcomingCourts,
    currentClubId,
    playerWaitMetrics,
    setPlayerWaitMetrics,
    clubStrengths,
    loading: courtsLoading,
    courtsAssigned,
    gameHistory,
    updateMatchResult,
    removeCourt,
    rotateAndRefillCourts,
    adjustCourtCount,
    adjustWaitingCourtCount,
    getMaxCourts,
    canReduceCourts,
    reduceCourtsReason,
    generateWaitingCourts,
    fillEmptyCourt,
    setClub,
    matchHistory,
    reloadMatchHistory,
    serverClearedActiveCourtsRef,
    serverClearedCourtRosterSigRef,
    autoRotateAfterScoreSubmission,
  } = useCourtManagement({
    players,
    activePlayers,
    sessionId,
    updatePlayers: setPlayers,
    playersById: playersByIdWithGames,
    sessionPlayerStatus,
    fetchPlayers,
    seasonId: season?.id,
    partnerships,
    isSocialSession: false,
    isPegBoardSession: isPegBoard,
    clubId: selectedClub?.id,
    setPlayerSessionStatus,
    // Client matchmaking must exclude players reserved by pending/placed scheduled games.
    scheduledGames,
    sessionPlayerScheduledGames,
    gameTypeRestriction,
    playerCategories:
      !isSinglesFormat(matchFormat) && categorisation.categorizationEnabled
        ? categorisation.playerCategories
        : null,
    matchFormat,
    onAfterAutoRotateFromScore: runAfterAutoRotateFromScore,
    onAfterFinishMatchSync,
    onApplyOptimisticPostScore,
    onApplyScoreCommandResult,
    onScoreStatsIncrement,
    onScoreRecordingPreviewOpen: isSessionHost ? openScoreRecordingNextUpPreview : null,
    onCourtFreshRotationHighlight: scheduleFreshRotationCourtHighlight,
    onConsumeScheduledGames:    isSessionHost ? consumeScheduledGamesPlaced : null,
    onScheduledGamesChange: isSessionHost ? setScheduledGames : null,
    matchPreferencesEnabled,
    cooldownEnabled,
    tabInstanceId,
    acquireScorePrepLock: invokeAcquireScorePrepLock,
    releaseScorePrepLock: invokeReleaseScorePrepLock,
    onScoreEloError: (msg) => addToast({ message: msg, variant: 'error', duration: 8000 }),
    offlineQueue: isSessionHost && !isPegBoard ? offlineQueue : null,
    getOfflineLiveState,
  });

  liveStateRef.current = {
    session: {
      id: sessionId,
      game_type_restriction: gameTypeRestriction,
      competitive_match_format: matchFormat,
      upcoming_queue_slot_count: numberOfUpcomingCourts,
      match_preferences_enabled: matchPreferencesEnabled,
      cooldown_enabled: cooldownEnabled,
      relaxation_preset: sessionData?.relaxation_preset ?? 'default',
    },
    playersById: playersByIdWithGames,
    sessionPlayerStatus,
    waitingList,
    courtAssignments,
    nextAssignments,
    pausedList,
    partnerships,
    scheduledGames,
    matchHistory,
    playerWaitMetrics,
    playerInteractions,
    totalGamesCompleted: matchHistory?.length || 0,
    sessionPlayerPenalties,
  };
  const localSessionGames = useMemo(
    () => buildLocalSessionGamesFromMatchHistory(matchHistory, playersByIdWithGames, sessionId),
    [matchHistory, playersByIdWithGames, sessionId]
  );
  offlineRosterBridgeRef.current = {
    get hasPending() {
      return typeof offlineQueue.hasUnsyncedCommands === 'function'
        ? offlineQueue.hasUnsyncedCommands()
        : offlineQueue.hasPending;
    },
    get offlineMode() {
      return (
        offlineQueue.offlineMode === true
        || offlineQueue.record?.offlineMode === true
        || sessionData?.offline_mode === true
      );
    },
    enqueue: (command) => offlineQueue.enqueue(command, liveStateRef.current),
  };

  useEffect(() => {
    courtAssignmentsPlacementRef.current = courtAssignments;
    nextAssignmentsPlacementRef.current = nextAssignments;
  }, [courtAssignments, nextAssignments]);

  // Phase B+C: real implementation is defined here (after useCourtManagement) so
  // setCourtAssignments / setNextAssignments etc. are in scope.  Assigned into
  // applyScoreCommandResultRef so the stable onApplyScoreCommandResult wrapper
  // (defined before useCourtManagement) can call it without a TDZ issue.
  useEffect(() => {
    applyScoreCommandResultRef.current = (result) => {
      commandEpochRef.current += 1;
      const previousLayout = {
        courtAssignments: latestStateRef.current?.courtAssignments,
        nextAssignments: latestStateRef.current?.nextAssignments,
      };
      applyScoreCompletedResult(
        result,
        { setCourtAssignments, setNextAssignments, setWaitingList, setPlayerWaitMetrics },
        localLayoutRevisionRef,
        mergePlayerWaitMetricsSnapshots,
        () => previousLayout,
      );
      const enriched = enrichScoreCompletedLayout(
        result,
        previousLayout.courtAssignments || [],
        previousLayout.nextAssignments || [],
      );
      if (enriched.courtAssignments != null) {
        latestStateRef.current = {
          ...latestStateRef.current,
          courtAssignments: mergeScoreCompletedCourtAssignments(
            enriched.courtAssignments,
            previousLayout.courtAssignments || [],
            result,
          ),
          nextAssignments:
            enriched.nextAssignments ?? latestStateRef.current.nextAssignments,
          waitingList: result.waitingList ?? latestStateRef.current.waitingList,
        };
      }
    };
  }, [setCourtAssignments, setNextAssignments, setWaitingList, setPlayerWaitMetrics]);

  /** Manual “Rotate” after a finished match is redundant when score submission auto-rotates (persisted session). Keep it if save can’t run so hosts aren’t stuck. */
  const showPostMatchRotateButton = useMemo(
    () => !(autoRotateAfterScoreSubmission && Boolean(sessionId) && Boolean(currentClubId)),
    [autoRotateAfterScoreSubmission, sessionId, currentClubId]
  );

  const numberOfUpcomingCourtsRef = useRef(numberOfUpcomingCourts);
  /** After onRestoreState sets slot count from DB/local; avoids trimming remote snapshot with default count=1. */
  const upcomingSlotsHydratedRef = useRef(false);
  useEffect(() => {
    numberOfUpcomingCourtsRef.current = numberOfUpcomingCourts;
  }, [numberOfUpcomingCourts]);

  /** When another tab adds a next_up session_court row, granular merge extends nextAssignments
   *  before a full reload runs — keep the queue-slot counter in sync with row count. */
  useEffect(() => {
    const len = nextAssignments.length;
    if (len <= 0) return;
    setNumberOfUpcomingCourts((prev) => {
      const capped = Math.min(MAX_HOST_UPCOMING_QUEUE_SLOTS, len);
      return capped > prev ? capped : prev;
    });
  }, [nextAssignments.length, setNumberOfUpcomingCourts]);

  const sessionToolbarGameTypeSelect = useMemo(() => {
    const options = isSinglesFormat(matchFormat)
      ? singlesGameTypeRestrictionOptions
      : doublesGameTypeRestrictionOptions;
    const value = isSinglesFormat(matchFormat)
      ? singlesGameTypeRestrictionSelectValue(gameTypeRestriction)
      : gameTypeRestriction;
    const selected = options.find((o) => o.value === value);
    return {
      options,
      value,
      title: selected?.label ? `Game type: ${selected.label}` : 'Game type restriction',
    };
  }, [matchFormat, gameTypeRestriction]);

  const playersOnCourtsSet = useMemo(
    () => new Set(courtAssignments.flatMap(c => [...(c.team1 || []), ...(c.team2 || [])])),
    [courtAssignments]
  );
  const playersInUpcomingSet = useMemo(
    () => new Set(nextAssignments.flatMap(m => [...(m.team1 || []), ...(m.team2 || [])])),
    [nextAssignments]
  );
  const waitingListSet = useMemo(() => new Set(waitingList), [waitingList]);

  const needsSetupForHostSections = useMemo(
    () =>
      isSessionHost &&
      courtAssignments.length === 0 &&
      nextAssignments.length === 0 &&
      waitingList.length === 0 &&
      (isSinglesFormat(matchFormat) || partnerships.length === 0),
    [
      isSessionHost,
      courtAssignments.length,
      nextAssignments.length,
      waitingList.length,
      matchFormat,
      partnerships.length,
    ]
  );

  const [hostSectionExpanded, setHostSectionExpanded] = useState({
    matchSettings: false,
    scheduled: false,
    partnerships: false,
    players: true,
  });

  const hostSectionsKeyRef = useRef(getHostSectionsStorageKey(currentUser?.id, sessionId));
  hostSectionsKeyRef.current = getHostSectionsStorageKey(currentUser?.id, sessionId);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = getHostSectionsStorageKey(currentUser?.id, sessionId);
    if (!key) return;
    try {
      const raw = window.localStorage.getItem(key);
      const defaults = {
        matchSettings: false,
        scheduled: false,
        partnerships: needsSetupForHostSections && !isSinglesFormat(matchFormat),
        players: true,
      };
      if (!raw) {
        setHostSectionExpanded(defaults);
        return;
      }
      const parsed = JSON.parse(raw);
      setHostSectionExpanded({
        matchSettings:
          typeof parsed.matchSettings === 'boolean' ? parsed.matchSettings : defaults.matchSettings,
        scheduled: typeof parsed.scheduled === 'boolean' ? parsed.scheduled : defaults.scheduled,
        partnerships:
          typeof parsed.partnerships === 'boolean' ? parsed.partnerships : defaults.partnerships,
        players: typeof parsed.players === 'boolean' ? parsed.players : defaults.players,
      });
    } catch {
      setHostSectionExpanded({
        matchSettings: false,
        scheduled: false,
        partnerships: needsSetupForHostSections && !isSinglesFormat(matchFormat),
        players: true,
      });
    }
  }, [currentUser?.id, sessionId, needsSetupForHostSections, matchFormat]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = hostSectionsKeyRef.current;
    if (!key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(hostSectionExpanded));
    } catch {
      /* ignore */
    }
  }, [hostSectionExpanded]);

  const handlePartnershipsUpdate = useCallback(
    (newPartnerships) => {
      const partnershipAdded =
        (newPartnerships?.length ?? 0) > (partnerships?.length ?? 0) ||
        (newPartnerships ?? []).some(
          (p) => !(partnerships ?? []).some((existing) => existing.id === p.id)
        );

      handleUpdatePartnerships(newPartnerships);

      if (partnershipAdded && !isSinglesFormat(matchFormat)) {
        setHostSectionExpanded((s) => ({ ...s, partnerships: true }));
        setCourtsSidebarExpanded((s) => ({ ...s, partnerships: true }));
      }
    },
    [partnerships, handleUpdatePartnerships, matchFormat]
  );

  const showManageTab = isSessionHost || isAdmin || isClubAdmin;
  const canCorrectScore = showManageTab && liveSessionStatus === 'ongoing';

  const handleScoreCorrected = useCallback(async () => {
    setGamesRefreshKey((key) => key + 1);
    setRankingRefreshKey((key) => key + 1);
    try {
      await fetchPlayers(undefined, undefined, { silent: true });
      await fetchGamesPlayedCount();
    } catch (err) {
      console.error('[CompetitiveSessionPage] Post score-correction refresh failed:', err);
    }
    addToast({ message: 'Score updated.', variant: 'success', duration: 4000 });
  }, [fetchPlayers, fetchGamesPlayedCount, addToast]);

  const viewerSessionStatus = currentUser?.id ? sessionPlayerStatus[currentUser.id] : null;
  const viewerInSessionRoster = ['active', 'signed_up', 'paused'].includes(viewerSessionStatus || '');
  /** Playing hosts/admins get a personal tab (game preference, etc.) alongside Manage. */
  const showYouTab = !isPegBoard && viewerInSessionRoster && !!currentUser?.id;
  const sessionIsLive = (liveSessionStatus || sessionData?.session_status) === 'ongoing';
  const showOwnPresenceCard = canShowOwnSessionPresence({
    isPegBoard,
    sessionStatus: sessionIsLive ? 'ongoing' : liveSessionStatus || sessionData?.session_status,
    rosterStatus: viewerSessionStatus,
    viewerPlayerId: currentUser?.id,
  });
  const ownPresencePauseBlockReason = getOwnPresencePauseBlockReason({
    isOnCourt: currentUser?.id ? playersOnCourtsSet.has(currentUser.id) : false,
    isInUpcoming: currentUser?.id ? playersInUpcomingSet.has(currentUser.id) : false,
    scheduledGameId: currentUser?.id ? sessionPlayerScheduledGames[currentUser.id] : null,
  });

  const handleOwnSessionPresence = useCallback(async (action) => {
    if (!sessionId || !currentUser?.id || ownPresenceLoading) return;
    setOwnPresenceLoading(true);
    try {
      const result = await setOwnSessionPresence(sessionId, action);
      if (!result.success) {
        addToast({
          message: result.error || 'Could not update your status.',
          variant: 'error',
          duration: 5000,
        });
        return;
      }

      const nextStatus = result.status || (action === 'pause' ? 'paused' : 'active');
      patchSessionPlayerFromRow({
        player_id: currentUser.id,
        status: nextStatus,
        late_joiner_penalty: result.lateJoinerPenalty,
      });
      setWaitingList((prev) => nextPresenceWaitingList(prev, currentUser.id, nextStatus));
      setPausedList((prev) => nextPresencePausedList(prev, currentUser.id, nextStatus));
      if (nextStatus === 'active') {
        setMatchmakingStarted(true);
      }

      addToast({
        message: result.message,
        variant: result.penaltyMessage ? 'warning' : 'success',
        duration: result.penaltyMessage ? 7000 : 4000,
      });
    } catch (err) {
      console.error('[handleOwnSessionPresence] failed:', err);
      addToast({
        message: err.message || 'Could not update your status.',
        variant: 'error',
        duration: 5000,
      });
    } finally {
      setOwnPresenceLoading(false);
    }
  }, [
    sessionId,
    currentUser?.id,
    ownPresenceLoading,
    addToast,
    patchSessionPlayerFromRow,
    setWaitingList,
    setPausedList,
  ]);
  const pegBoardOccupiedPlayers = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const pushRow = (id, location) => {
      if (!id || seen.has(id) || !playersByIdWithGames[id]) return;
      seen.add(id);
      rows.push({ player: playersByIdWithGames[id], location });
    };
    for (const court of courtAssignments || []) {
      const label = `Court ${court.courtNumber}`;
      for (const id of [...(court.team1 || []), ...(court.team2 || [])]) {
        pushRow(id, label);
      }
    }
    (nextAssignments || []).forEach((match, index) => {
      const label = `Upcoming #${index + 1}`;
      for (const id of [...(match.team1 || []), ...(match.team2 || [])]) {
        pushRow(id, label);
      }
    });
    return rows;
  }, [courtAssignments, nextAssignments, playersByIdWithGames]);
  const pegBoardOccupiedIds = useMemo(
    () => pegBoardOccupiedPlayers.map((row) => row.player.id),
    [pegBoardOccupiedPlayers]
  );
  const pegBoardPickerId = isPegBoard ? getPicker(waitingList) : null;
  const pegBoardPickTarget = useMemo(
    () => (isPegBoard ? getPegBoardPickTarget({ courtAssignments, nextAssignments }) : null),
    [isPegBoard, courtAssignments, nextAssignments]
  );
  const pegBoardCanOpenPick = useMemo(() => {
    if (!isPegBoard) return false;
    const pool = getEligiblePool(waitingList, sessionData?.peg_board_selection_window);
    if (!pool.canPick) return false;
    return Boolean(
      isSessionHost ||
      isClubAdmin ||
      (currentUser?.id && currentUser.id === pool.pickerId)
    );
  }, [
    isPegBoard,
    waitingList,
    sessionData?.peg_board_selection_window,
    isSessionHost,
    isClubAdmin,
    currentUser?.id,
  ]);
  const handleOpenPegBoardPick = useCallback(() => {
    setPegBoardPickModalOpen(true);
  }, []);

  useEffect(() => {
    if (!pegBoardCanOpenPick) setPegBoardPickModalOpen(false);
  }, [pegBoardCanOpenPick]);

  useEffect(() => {
    if (activeTab === 'manage' && !showManageTab) {
      setActiveTab('courts');
    }
    if (isPegBoard && ['ranking', 'playerRanking', 'you'].includes(activeTab)) {
      setActiveTab('courts');
    }
  }, [activeTab, showManageTab, isPegBoard]);

  // Tracks whether DB restore has run — prevents the init-waitlist effect from
  // overwriting a legitimately empty waitlist (all players on courts/upcoming).
  const restoredWaitlistRef = useRef(false);

  const onClearState = useCallback((persistenceSetters) => {
    setCourtAssignments([]);
    setNextAssignments([]);
    setWaitingList([]);
    restoredWaitlistRef.current = false;
    persistenceSetters.setStateLoadedFromDatabase(false);
    persistenceSetters.setIsLoadingState(false);
    persistenceSetters.setIsDataReady(false);
  }, [setCourtAssignments, setNextAssignments, setWaitingList]);

  /** Apply DB snapshot to local court/wait/matchmaking state (initial load + Realtime). */

  const applyPersistedSessionSnapshot = useCallback(async (savedState, applyOptions = {}) => {
    if (!savedState || savedState.session_id !== sessionId) {
        return null;
      }

    const {
      forceApplyRemoteNextUp = false,
      skipNextAssignments = false,
      /** Host: skip setNextAssignments so soft reloads (interactions/partnerships) do not overwrite local upcoming with stale DB rosters. */
      preserveNextAssignments = false,
      /** Post-score authoritative reload: trust DB wait metrics over local optimistic cooldown. */
      trustIncomingWaitMetrics = false,
    } = applyOptions;

    const mappedNext = (savedState.next_assignments || []).map((match) => ({
        ...match,
      gameCreation: ensureGameCreationMeta(match.gameCreation, match.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL),
    }));
    const excessIdsFromTrim = [];
    let nextToApply = trimNextAssignmentsForUpcomingCap(
      mappedNext,
      MAX_HOST_UPCOMING_QUEUE_SLOTS,
      excessIdsFromTrim
    );
    if (isSessionHost && applyOptions.hostUpcomingCap != null) {
      const hostCap = Math.max(
        0,
        Math.min(MAX_HOST_UPCOMING_QUEUE_SLOTS, applyOptions.hostUpcomingCap)
      );
      nextToApply = trimNextAssignmentsForUpcomingCap(nextToApply, hostCap, excessIdsFromTrim);
    }

    let mergedNextSnapshot = nextToApply;
    if (!skipNextAssignments && !preserveNextAssignments) {
      setNextAssignments((prev) => {
        mergedNextSnapshot = preserveRelaxedMetaOnUnchangedLineups(nextToApply, prev);
        return mergedNextSnapshot;
      });
    } else {
      mergedNextSnapshot = latestStateRef.current?.nextAssignments ?? nextToApply;
    }

    let actualCourtCount = 1;
    if (savedState.maxCourtNumber != null) {
      actualCourtCount = Math.max(1, Math.max(savedState.maxCourtNumber, savedState.court_assignments?.length || 0));
      setNumberOfCourts(actualCourtCount);
    } else if (savedState.court_assignments && savedState.court_assignments.length > 0) {
      actualCourtCount = Math.max(1, savedState.court_assignments.length);
      setNumberOfCourts(actualCourtCount);
    } else {
      setNumberOfCourts(1);
      actualCourtCount = 1;
    }

    if (savedState.court_assignments && savedState.court_assignments.length > 0) {
      setCourtAssignments((prev) => {
        const mapped = savedState.court_assignments.map((court) => ({
          ...court,
          gameCreation: court.gameCreation
            ? ensureGameCreationMeta(court.gameCreation, court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL)
            : null,
        }));
        return mergeEphemeralCourtStateFromPrevious(
          mapped,
          prev,
          playersPerSideForSession,
          mergedNextSnapshot,
        );
      });
      const playersOnCourts = new Set();
      savedState.court_assignments.forEach((court) => {
        (court.team1 || []).forEach((playerId) => playersOnCourts.add(playerId));
        (court.team2 || []).forEach((playerId) => playersOnCourts.add(playerId));
      });
      if (playersOnCourts.size > 0) {
        for (const playerId of playersOnCourts) {
          setPlayerSessionStatus(playerId, 'active').catch((err) => {
            console.error(`Failed to activate player ${playerId} on court:`, err);
          });
        }
      }
    } else if (actualCourtCount >= 1) {
      const emptyCourts = [];
      for (let i = 1; i <= actualCourtCount; i++) {
        emptyCourts.push({
          courtNumber: i,
          team1: [],
          team2: [],
          inProgress: false,
          gameCreation: { origin: null, algorithm: null, isAltered: false, scheduledGameId: null },
        });
      }
      setCourtAssignments(emptyCourts);
    }

    const mappedCourtsForWaitlist = (savedState.court_assignments || []).map((court) => ({
      team1: court.team1 || [],
      team2: court.team2 || [],
    }));
    const { waitingList: consistentWaitingList, removedBecausePlacedElsewhere } =
      deriveConsistentWaitingList(
        (() => {
          const base = savedState.waiting_list || [];
          if (excessIdsFromTrim.length === 0) return base;
          const merged = [...base];
          excessIdsFromTrim.forEach((id) => {
            if (!merged.includes(id)) merged.push(id);
          });
          return merged;
        })(),
        mappedCourtsForWaitlist,
        nextToApply,
      );
    if (removedBecausePlacedElsewhere.length > 0) {
      console.warn(
        '[applyPersistedSessionSnapshot] Dropped',
        removedBecausePlacedElsewhere.length,
        'player(s) from waitlist (already on active or upcoming court).',
        removedBecausePlacedElsewhere.slice(0, 6),
      );
    }
    setWaitingList(consistentWaitingList);
    restoredWaitlistRef.current = true;

    const interactionsObj =
      savedState.player_interactions instanceof Map
        ? Object.fromEntries(savedState.player_interactions)
        : savedState.player_interactions || {};
      setPlayerInteractions(interactionsObj);

    setPlayerWaitMetrics((prev) =>
      mergePlayerWaitMetricsSnapshots(prev, savedState.player_wait_metrics || {}, {
        trustIncomingWaitMetrics,
      }),
    );

    if (savedState.playerCategories || (savedState.categoryRanges && savedState.categoryRanges.length > 0)) {
      categorisation.restoreFromSavedState(savedState);
    }

    return {
      consistentWaitingList,
      nextToApply,
      courtAssignments: savedState.court_assignments || [],
      playerWaitMetrics: savedState.player_wait_metrics || {},
    };
  }, [
    sessionId,
    setNextAssignments,
    setCourtAssignments,
    setNumberOfCourts,
    setWaitingList,
    setPlayerInteractions,
    setPlayerWaitMetrics,
    setPlayerSessionStatus,
    categorisation,
    isSessionHost,
    playersPerSideForSession,
  ]);

  const onRestoreState = useCallback(
    async (savedState, persistenceSetters) => {
      const {
        setStateLoadedFromDatabase: setLoaded,
        setIsLoadingState: setLoading,
        setIsDataReady: setDataReady,
      } = persistenceSetters;
      const offlineSnapshot = offlineQueue.record?.snapshot;
      if (offlineQueue.hasPending && offlineSnapshot) {
        if (offlineSnapshot.courtAssignments) {
          setCourtAssignments(offlineSnapshot.courtAssignments);
          setNumberOfCourts(offlineSnapshot.courtAssignments.length);
        }
        if (offlineSnapshot.nextAssignments) setNextAssignments(offlineSnapshot.nextAssignments);
        if (offlineSnapshot.waitingList) setWaitingList(offlineSnapshot.waitingList);
        if (offlineSnapshot.pausedList) setPausedList(offlineSnapshot.pausedList);
        if (offlineSnapshot.playerWaitMetrics) setPlayerWaitMetrics(offlineSnapshot.playerWaitMetrics);
        if (offlineSnapshot.sessionPlayerStatus) {
          setSessionPlayerStatus(offlineSnapshot.sessionPlayerStatus);
        }
        if (offlineSnapshot.playersById && Object.keys(offlineSnapshot.playersById).length > 0) {
          setPlayers(offlineSnapshot.playersById);
        }
        if (Array.isArray(offlineSnapshot.partnerships)) {
          setPartnerships(offlineSnapshot.partnerships);
        }
        if (Array.isArray(offlineSnapshot.scheduledGames)) {
          setScheduledGames(offlineSnapshot.scheduledGames);
        }
        if (offlineSnapshot.session?.upcoming_queue_slot_count != null) {
          setNumberOfUpcomingCourts(offlineSnapshot.session.upcoming_queue_slot_count);
          numberOfUpcomingCourtsRef.current = offlineSnapshot.session.upcoming_queue_slot_count;
        }
        upcomingSlotsHydratedRef.current = true;
        setLoaded(true);
        setLoading(false);
        setDataReady(true);
        return;
      }
      const hasExistingState =
        courtAssignments.length > 0 || waitingList.length > 0 || nextAssignments.length > 0;
      if (hasExistingState) {
        const slotSync = dbUpcomingSlotCap(
          savedState,
          (savedState.next_assignments || []).length
        );
        setNumberOfUpcomingCourts(slotSync);
        numberOfUpcomingCourtsRef.current = slotSync;
        upcomingSlotsHydratedRef.current = true;
    setLoaded(true);
    setLoading(false);
    setDataReady(true);
        return;
      }
      await applyPersistedSessionSnapshot(savedState);
      const orphanIds = savedState.orphaned_waitlist_player_ids || [];
      if (
        isSessionHost &&
        orphanIds.length > 0
      ) {
        void repairOrphanedWaitlistPlayers(sessionId, orphanIds, tabInstanceId);
      }
      const slotSync = dbUpcomingSlotCap(
        savedState,
        (savedState.next_assignments || []).length
      );
      setNumberOfUpcomingCourts(slotSync);
      numberOfUpcomingCourtsRef.current = slotSync;
      upcomingSlotsHydratedRef.current = true;
      setLoaded(true);
      setLoading(false);

      // Upcoming matchmaking runs server-side (plan-upcoming-slot EF via afterFinishMatchSync).
      // Restore shows DB next_up as-is.
      void fetchGamesPlayedCount();

      setDataReady(true);
    },
    [
      courtAssignments.length,
      waitingList.length,
      nextAssignments.length,
      applyPersistedSessionSnapshot,
      fetchGamesPlayedCount,
      setNumberOfUpcomingCourts,
      isSessionHost,
      sessionId,
      tabInstanceId,
      offlineQueue.hasPending,
      offlineQueue.record,
      setCourtAssignments,
      setNextAssignments,
      setWaitingList,
      setPausedList,
      setPlayerWaitMetrics,
      setSessionPlayerStatus,
      setPlayers,
      setNumberOfCourts,
      setPartnerships,
      setScheduledGames,
    ]
  );

  const runRemoteSessionSyncInternal = useCallback(async (syncOptions = {}) => {
    const {
      ignoreSaveGuard = false,
      skipNextAssignments = false,
      forceApplyRemoteNextUp = false,
      syncProfile = 'full',
      syncContext = null,
      useTargetedPostScore = false,
      syncEpochAtStart = null,
    } = syncOptions;
    const syncWallStart = typeof performance !== 'undefined' ? performance.now() : Date.now();

    if (!sessionLogger || !sessionId) {
      return null;
    }
    if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) {
      return null;
    }
    // Normalized sessions have no debounced snapshot saves — save guard is not needed.
    const useNormalized = sessionData?.use_normalized_state !== false;
    if (!useNormalized && !ignoreSaveGuard && sessionLogger.isSaving) {
      return null;
    }

    if (!useNormalized && ignoreSaveGuard) {
      sessionLogger.cancelPendingSave?.();
    }

    fullSyncInFlightRef.current = true;
    if (ignoreSaveGuard || useNormalized) structuralSyncInFlightRef.current = true;
    fullSyncPendingAuthoritativeRef.current = false;

    try {
      let savedState = null;
      const useTargeted =
        useTargetedPostScore &&
        syncContext &&
        (process.env.REACT_APP_SERVER_MATCHMAKING === 'true'
          || sessionData?.use_normalized_state !== false);

      if (useTargeted) {
        const snap = latestStateRef.current;
        const targeted = await loadPostScoreTargetedState(
          sessionId,
          syncContext,
          snap.courtAssignments,
          snap.nextAssignments,
          playersPerSideForSession,
        );
        if (targeted.ok && targeted.state) {
          savedState = {
            session_id: sessionId,
            court_assignments: targeted.state.courtAssignments,
            next_assignments: targeted.state.nextAssignments,
            waiting_list: targeted.state.waitingList,
            player_wait_metrics: targeted.state.playerWaitMetrics,
            upcoming_queue_slot_count: targeted.state.upcomingQueueSlotCount,
          };
          scoreSubmitSyncDebugLog('targeted', {
            courtNumber: syncContext.courtNumber,
            ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - syncWallStart),
          });
        } else {
          scoreSubmitSyncDebugLog('targetedFallback', {
            reason: targeted.fallbackReason,
          });
        }
      }

      if (!savedState) {
        const loadStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
        savedState = await sessionLogger.loadCurrentState();
        scoreSubmitSyncDebugLog('loadCurrentState', {
          ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStart),
        });
      }

      if (!savedState || savedState.session_id !== sessionId) {
        return null;
      }

      if (
        syncEpochAtStart != null &&
        commandEpochRef.current !== syncEpochAtStart
      ) {
        scoreSubmitSyncDebugLog('staleEpoch', {
          syncEpochAtStart,
          currentEpoch: commandEpochRef.current,
        });
        return null;
      }

      const remoteNextLen = (savedState.next_assignments || []).length;
      const dbSlotCap = dbUpcomingSlotCap(savedState, remoteNextLen);

      const mergedHostCap =
        isSessionHost && upcomingSlotsHydratedRef.current ? dbSlotCap : undefined;
      const applyStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const applied = await applyPersistedSessionSnapshot(savedState, {
        hostUpcomingCap: mergedHostCap,
        skipNextAssignments,
        preserveNextAssignments: false,
        forceApplyRemoteNextUp,
        trustIncomingWaitMetrics: ignoreSaveGuard,
      });
      scoreSubmitSyncDebugLog('applySnapshot', {
        ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - applyStart),
      });

      if (ignoreSaveGuard && applied) {
        prevStateRef.current = {
          courtAssignments: applied.courtAssignments,
          nextAssignments: applied.nextToApply,
          waitingList: applied.consistentWaitingList,
          playerWaitMetrics: applied.playerWaitMetrics,
        };
        const activeVersions = {};
        const nextUpVersions = {};
        applied.courtAssignments.forEach((c) => {
          if (c.courtNumber != null && c.version != null) activeVersions[c.courtNumber] = c.version;
        });
        applied.nextToApply.forEach((s) => {
          if (s.courtNumber != null && s.version != null) nextUpVersions[s.courtNumber] = s.version;
        });
        lastAckedCourtVersionsRef.current = {
          active: activeVersions,
          next_up: nextUpVersions,
        };
      }

      const mappedNextForConsume = (savedState.next_assignments || []).map((match) => ({
        ...match,
        gameCreation: ensureGameCreationMeta(
          match.gameCreation,
          match.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL,
        ),
      }));
      const excessFromTrimForConsume = [];
      let nextTrimmedForConsume = trimNextAssignmentsForUpcomingCap(
        mappedNextForConsume,
        MAX_HOST_UPCOMING_QUEUE_SLOTS,
        excessFromTrimForConsume,
      );
      if (mergedHostCap != null) {
        const hostCap = Math.max(
          0,
          Math.min(MAX_HOST_UPCOMING_QUEUE_SLOTS, mergedHostCap),
        );
        nextTrimmedForConsume = trimNextAssignmentsForUpcomingCap(
          nextTrimmedForConsume,
          hostCap,
          excessFromTrimForConsume,
        );
      }
      const nextRowsForScheduledConsume = nextTrimmedForConsume;
      const mappedCourtsForConsume = (savedState.court_assignments || []).map((court) => ({
        team1: court.team1 || [],
        team2: court.team2 || [],
        gameCreation: court.gameCreation
          ? ensureGameCreationMeta(
              court.gameCreation,
              court.gameCreation.origin || GAME_CREATION_ORIGIN.MANUAL,
            )
          : null,
      }));
      // Only collect scheduled game IDs from PLAYING courts — not upcoming slots.
      // Status moves to on_court only when the game is on a playing court.
      // Upcoming slots keep the game as 'pending' until it rotates to a playing court.
      const scheduledIdsOnPlayingCourts = collectScheduledGameIdsFromAssignmentRows(
        mappedCourtsForConsume,
      );
      if (isSessionHost && scheduledIdsOnPlayingCourts.length > 0) {
        await consumeScheduledGamesPlaced(scheduledIdsOnPlayingCourts);
      }

      if (!useNormalized && !ignoreSaveGuard && sessionLogger.isSaving) {
        return null;
      }

      const syncedUpcomingSlots = dbSlotCap;
      setNumberOfUpcomingCourts(syncedUpcomingSlots);
      numberOfUpcomingCourtsRef.current = syncedUpcomingSlots;

      const auxStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const auxPromises =
        syncProfile === 'postScore'
          ? [loadScheduledGames()]
          : [
              fetchPlayers(undefined, undefined, { silent: true }),
              fetchSessionPlayerStatus({ silent: true }),
              reloadMatchHistory(),
              fetchGamesPlayedCount(),
              loadSessionPartnerships(),
              loadScheduledGames(),
            ];

      let fetchPlayersResult = null;
      let matchHistoryFresh = null;

      if (syncProfile === 'postScore') {
        if (auxPromises.length > 0) {
          await Promise.all(auxPromises);
        }
      } else {
        const auxResults = await Promise.all(auxPromises);
        fetchPlayersResult = auxResults[0];
        matchHistoryFresh = auxResults[2];
      }

      scoreSubmitSyncDebugLog('auxFetches', {
        syncProfile,
        ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - auxStart),
      });
      // Return loaded data so post-sync callers can act on fresh state without
      // waiting for a React re-render cycle (latestStateRef can lag one render).
      const freshPlayersById =
        fetchPlayersResult?.success && fetchPlayersResult?.data
          ? fetchPlayersResult.data
          : null;

      // Automatic upcoming matchmaking runs only in afterFinishMatchSyncRef (submit score path).

      scoreSubmitSyncDebugLog('total', {
        syncProfile,
        ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - syncWallStart),
      });

      return {
        nextAssignments: savedState.next_assignments || [],
        waitingList: savedState.waiting_list || [],
        playerWaitMetrics: savedState.player_wait_metrics || {},
        courtAssignments: savedState.court_assignments || [],
        freshPlayersById,
        freshMatchHistory: Array.isArray(matchHistoryFresh) ? matchHistoryFresh : null,
        targetUpcomingSlots: syncedUpcomingSlots,
      };
    } finally {
      fullSyncInFlightRef.current = false;
      structuralSyncInFlightRef.current = false;
    }
  }, [
    sessionLogger,
    sessionId,
    isSessionHost,
    fetchPlayers,
    fetchSessionPlayerStatus,
    applyPersistedSessionSnapshot,
    reloadMatchHistory,
    fetchGamesPlayedCount,
    loadSessionPartnerships,
    loadScheduledGames,
    setNumberOfUpcomingCourts,
    consumeScheduledGamesPlaced,
    sessionData,
    playersPerSideForSession,
    offlineQueue.hasPending,
    offlineQueue.isFlushing,
    sessionData,
  ]);

  const handleRemoteSessionSync = useCallback(
    (syncOptions = {}) =>
      sessionSyncCoordinatorRef.current.requestSync(
        (opts) => runRemoteSessionSyncInternal(opts),
        syncOptions
      ),
    [runRemoteSessionSyncInternal]
  );

  const hadUnsyncedOfflineRef = useRef(false);
  useEffect(() => {
    const pending = shouldHoldLocalOfflineLayout(offlineQueue);
    if (hadUnsyncedOfflineRef.current && !pending && !offlineQueue.blockedReason) {
      void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
    }
    hadUnsyncedOfflineRef.current = pending;
  }, [offlineQueue, offlineQueue.hasPending, offlineQueue.blockedReason, handleRemoteSessionSync]);

  /**
   * Defense-in-depth full resync triggered by structural session_courts DELETE events
   * (e.g. finish_session_match consuming a next_up slot). The primary convergence path
   * is session_games INSERT → scheduleSync({ ignoreSaveGuard: true }) in
   * useCompetitiveSessionRealtime, which reliably fires on all host tabs after any
   * finish_session_match call. This timer handles the narrower case where the
   * session_courts DELETE arrives with REPLICA IDENTITY FULL oldRow data available
   * before the session_games event coalesces — it ensures the stale next_up slot is
   * cleared immediately rather than waiting for the games debounce window.
   */
  const structuralSessionResyncTimerRef = useRef(null);
  /** Debounced authoritative snapshot after structural session_courts issues (DELETE, fetch-null). */
  const scheduleStructuralSessionResync = useCallback(() => {
    if (structuralSessionResyncTimerRef.current != null) {
      clearTimeout(structuralSessionResyncTimerRef.current);
    }
    structuralSessionResyncTimerRef.current = setTimeout(() => {
      structuralSessionResyncTimerRef.current = null;
      const recentPostScore = getRecentPostScoreSyncOptions();
      void handleRemoteSessionSync(
        recentPostScore || { ignoreSaveGuard: true },
      );
    }, 120);
  }, [handleRemoteSessionSync, getRecentPostScoreSyncOptions]);

  useEffect(() => () => {
    if (structuralSessionResyncTimerRef.current != null) {
      clearTimeout(structuralSessionResyncTimerRef.current);
      structuralSessionResyncTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    afterFinishMatchSyncRef.current = async (syncContext) => {
      const epochAtStart = commandEpochRef.current;
      const courtNum = syncContext?.courtNumber;

      if (syncContext) {
        lastPostScoreSyncContextRef.current = { context: syncContext, at: Date.now() };
      }

      try {
        await handleRemoteSessionSync({
          ...buildPostScoreSyncOptions(syncContext),
          syncEpochAtStart: epochAtStart,
        });

        if (commandEpochRef.current === epochAtStart && courtNum != null) {
          serverClearedActiveCourtsRef.current.delete(courtNum);
          delete serverClearedCourtRosterSigRef.current[courtNum];
        }
      } catch (err) {
        console.error('[CompetitiveSessionPage] post-score sync failed:', err);
        addToast({
          message: 'Score saved; refreshing session layout…',
          variant: 'warning',
          duration: 6000,
        });
        try {
          await handleRemoteSessionSync({
            ignoreSaveGuard: true,
            syncProfile: 'full',
            debounceMs: 0,
          });
        } catch (fallbackErr) {
          console.error('[CompetitiveSessionPage] post-score fallback sync failed:', fallbackErr);
        }
      } finally {
        if (commandEpochRef.current !== epochAtStart) {
          void handleRemoteSessionSync({
            ignoreSaveGuard: true,
            forceApplyRemoteNextUp: true,
            syncProfile: 'postScore',
            useTargetedPostScore: false,
            debounceMs: 0,
          });
        }
      }
    };
  }, [handleRemoteSessionSync, addToast, buildPostScoreSyncOptions]);

  const {
    stateLoadedFromDatabase,
    setStateLoadedFromDatabase,
    isLoadingState,
    setIsLoadingState,
    isDataReady,
    setIsDataReady,
  } = useCompetitiveSessionPersistence({
    sessionId,
    sessionLogger,
    selectedClubId: selectedClub?.id,
    seasonId: season?.id,
    fetchPlayers,
    onRestoreState,
    onClearState,
  });

  // ---------------------------------------------------------------------------
  // Granular realtime merge handler
  //
  // Called by useCompetitiveSessionRealtime for every session_courts /
  // session_players row change. We merge only the affected row into local state
  // instead of triggering a full reload.
  //
  // session_courts change → re-fetch that court's players and merge the court.
  // session_players change → re-fetch that court's players if it's a court row;
  //   waiting-list moves are captured indirectly through the court change event.
  //
  // Realtime merge policy (scalable / multi-tab safe):
  //   Never use a global "saving" gate here — it dropped unrelated rows (e.g. next_up
  //   cleared on host A while host B was only persisting an active court).
  //   Only skip merges for the exact resource this tab is outbound-writing right now
  //   (per-court locks + per-player / bulk waitlist locks in the debounced save loop).
  // ---------------------------------------------------------------------------
  const rowChangePendingCourtsRef = useRef(new Set());
  /** Keys `${court_type}:${court_number}` — this tab has an in-flight RPC for that court row. */
  const localCourtWriteLocksRef = useRef(new Set());
  /** Player IDs targeted by outbound waitlist add/remove from this tab's debounced save. */
  const waitlistPlayerWriteLocksRef = useRef(new Set());

  const handlePasteRosterAccept = useCallback(async (writableRows) => {
    const target = defaultAddEntryTarget;
    const clubId = currentClubId || selectedClub?.id;
    const toAdd = (writableRows || [])
      .map((row) => row.selectedPlayerId)
      .filter((playerId) => playerId && sessionPlayerStatus[playerId] === undefined);
    const alreadyCount = (writableRows || []).length - toAdd.length;

    if (!toAdd.length) {
      return { addedCount: 0, alreadyCount };
    }
    if (!sessionId || !clubId) {
      throw new Error('Session or club is missing');
    }

    await addPlayersToSession(sessionId, toAdd, clubId, target);

    if (target === 'active') {
      setWaitingList((prev) => {
        const next = [...prev];
        toAdd.forEach((playerId) => {
          if (!next.includes(playerId)) next.push(playerId);
        });
        return next;
      });
      await Promise.all(
        toAdd.map(async (playerId) => {
          waitlistPlayerWriteLocksRef.current.add(playerId);
          try {
            const wlResult = await joinWaitlistCmd(sessionId, playerId, tabInstanceId);
            if (!wlResult?.ok) {
              console.error('[handlePasteRosterAccept] joinWaitlistCmd failed:', wlResult);
            }
          } finally {
            waitlistPlayerWriteLocksRef.current.delete(playerId);
          }
        })
      );
    }

    await fetchSessionPlayerStatus({ silent: true });
    return { addedCount: toAdd.length, alreadyCount };
  }, [
    defaultAddEntryTarget,
    currentClubId,
    selectedClub?.id,
    sessionPlayerStatus,
    sessionId,
    setWaitingList,
    tabInstanceId,
    fetchSessionPlayerStatus,
  ]);
  /** True while reorder_waitlist RPC runs (touches many session_players rows). */
  const waitlistReorderInFlightRef = useRef(false);
  /**
   * Coalesce bursty session_courts notifications (e.g. finish_session_match sets
   * in_progress=false, then rotation immediately updates teams + in_progress=true).
   * If a second event arrives while fetchSingleCourt+setState is still running for
   * the same court key, we defer one follow-up merge instead of dropping it forever.
   */
  const deferredSessionCourtSyncRef = useRef(new Set());
  const waitlistRosterRefreshTimerRef = useRef(null);

  /** Debounced waitlist + roster status refresh for passive host tabs. */
  const scheduleWaitlistRosterRefresh = useCallback(() => {
    if (waitlistRosterRefreshTimerRef.current != null) {
      clearTimeout(waitlistRosterRefreshTimerRef.current);
    }
    waitlistRosterRefreshTimerRef.current = setTimeout(() => {
      waitlistRosterRefreshTimerRef.current = null;
      if (!sessionId) return;
      if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;
      void (async () => {
        try {
          const [rosterSlice] = await Promise.all([
            fetchSessionWaitlistAndMetrics(sessionId),
            fetchPlayers(undefined, undefined, { silent: true }),
            fetchSessionPlayerStatus({ silent: true }),
          ]);
          if (rosterSlice?.waitingList) {
            setWaitingList(rosterSlice.waitingList);
          }
          if (rosterSlice?.playerWaitMetrics) {
            setPlayerWaitMetrics((prev) =>
              mergePlayerWaitMetricsSnapshots(prev, rosterSlice.playerWaitMetrics)
            );
          }
        } catch (err) {
          console.error('[CompetitiveSessionPage] waitlist roster refresh failed:', err);
        }
      })();
    }, 80);
  }, [
    sessionId,
    setWaitingList,
    setPlayerWaitMetrics,
    fetchPlayers,
    fetchSessionPlayerStatus,
    offlineQueue,
    sessionData,
  ]);

  useEffect(() => () => {
    if (waitlistRosterRefreshTimerRef.current != null) {
      clearTimeout(waitlistRosterRefreshTimerRef.current);
      waitlistRosterRefreshTimerRef.current = null;
    }
  }, []);

  const handleRowChange = useCallback(async (table, eventType, newRow, oldRow) => {
    if (!sessionId) return;
    // Skip individual row patches while an authoritative full-reload is in progress,
    // or while score prep is draining writes / awaiting post-score sync.
    // finish_session_match changes many rows atomically; merging mid-transition
    // can show promoted players on court and on the waitlist at the same time.
    if (structuralSyncInFlightRef.current || scorePrepInFlightRef.current) return;
    if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;

    if (table === 'session_courts') {
      // Structural DELETE (promotion consumed a next_up slot, or host removed active court).
      // With REPLICA IDENTITY FULL, oldRow carries court_number/court_type so we can fast-path
      // a full resync here. Without it, oldRow only has the PK and this falls through harmlessly
      // — the authoritative session_games INSERT path in useCompetitiveSessionRealtime will
      // still trigger handleRemoteSessionSync({ ignoreSaveGuard: true }) for all host tabs.
      if (
        eventType === 'DELETE' &&
        oldRow &&
        oldRow.court_number != null &&
        (oldRow.court_type === 'next_up' || oldRow.court_type === 'active')
      ) {
        scheduleStructuralSessionResync();
        return;
      }

      const courtNumber = newRow?.court_number;
      const courtType   = newRow?.court_type;
      if (courtNumber == null || !courtType) return;

      const key = `${courtType}:${courtNumber}`;
      if (localCourtWriteLocksRef.current.has(key)) return;

      if (rowChangePendingCourtsRef.current.has(key)) {
        deferredSessionCourtSyncRef.current.add(key);
        return;
      }

      const mergeCourtOnce = async () => {
        rowChangePendingCourtsRef.current.add(key);
        try {
          const fresh = await fetchSingleCourt(sessionId, courtNumber, courtType);

          if (!fresh) {
            // next_up: Only INSERT/UPDATE reach this path (DELETE is handled above). A null fetch
            // usually means read lag or session_players firing before session_courts is visible.
            // Never trim local next_up rows here — that stranded passive tabs with one slot while
            // players had already left the waitlist in DB.
            if (courtType === 'next_up') {
              scheduleStructuralSessionResync();
            }
            // Active courts: no-op — empty active slots are maintained by the host; a null
            // fetch here usually means the event arrived before the DB row was created.
            return;
          }

          if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;

          if (courtType === 'active') {
            setCourtAssignments(prev => {
              const idx = prev.findIndex(c => c.courtNumber === courtNumber);
              if (idx === -1) {
                return [...prev, mergeRelaxedMetaFromPreviousRows(fresh, prev, latestStateRef.current?.nextAssignments)];
              }
              if (shouldSkipCourtMerge(prev[idx], fresh)) return prev;
              const updated = [...prev];
              updated[idx] = finishEphemeralCourtMerge(
                mergeRelaxedMetaFromPreviousRows(
                  { ...prev[idx], ...fresh },
                  prev,
                  latestStateRef.current?.nextAssignments,
                ),
                prev,
                latestStateRef.current?.nextAssignments,
              );
              return updated;
            });
          } else if (courtType === 'next_up') {
            // During score preparation, suppress next_up realtime merges to keep the queue stable.
            if (scorePrepInFlightRef.current) return;
            setNextAssignments(prev => {
              const normalized = normalizeNextUpCourtNumbers(prev);
              const idx = normalized.findIndex(
                (c, i) => (c.courtNumber ?? i) === courtNumber
              );
              if (idx === -1) {
                return [...normalized, mergeRelaxedMetaFromPreviousRows(fresh, prev, prev)];
              }
              if (shouldSkipCourtMerge(normalized[idx], fresh)) return prev;
              const updated = [...normalized];
              updated[idx] = mergeRelaxedMetaFromPreviousRows(
                { ...normalized[idx], ...fresh, courtNumber },
                prev,
                prev,
              );
              return updated;
            });
          }

          // Sync ACKed state on successful granular merge — guarded by write lock so
          // our own in-flight write's echo doesn't incorrectly advance the baseline.
          if (!localCourtWriteLocksRef.current.has(key) && fresh.version != null) {
            const normalizedType = courtType === 'next_up' ? 'next_up' : 'active';
            const currentAcked   = lastAckedCourtVersionsRef.current[normalizedType]?.[courtNumber];
            // Only advance forward — never regress on out-of-order realtime events.
            if (currentAcked == null || fresh.version >= currentAcked) {
              dirtyCourtsRef.current.delete(key);
              lastAckedCourtVersionsRef.current = {
                ...lastAckedCourtVersionsRef.current,
                [normalizedType]: {
                  ...lastAckedCourtVersionsRef.current[normalizedType],
                  [courtNumber]: fresh.version,
                },
              };
              // Advance per-court baseline so the next diff doesn't falsely detect a change.
              if (courtType === 'active') {
                prevStateRef.current = {
                  ...prevStateRef.current,
                  courtAssignments: (prevStateRef.current.courtAssignments || []).map(c =>
                    c.courtNumber === courtNumber ? { ...c, ...fresh } : c
                  ),
                };
              } else if (courtType === 'next_up') {
                prevStateRef.current = {
                  ...prevStateRef.current,
                  nextAssignments: (prevStateRef.current.nextAssignments || []).map(s =>
                    s.courtNumber === courtNumber ? { ...s, ...fresh } : s
                  ),
                };
              }
            }
          }
        } catch (err) {
          console.error('Granular court merge failed, falling back to full reload:', err);
          addToast({
            message: 'Session updated by another host — refreshing courts.',
            variant: 'info',
            duration: 3000,
          });
          handleRemoteSessionSync();
        } finally {
          rowChangePendingCourtsRef.current.delete(key);
        }
      };

      await mergeCourtOnce();
      // Drain deferred follow-ups (each pass handles at least one coalesced burst).
      while (deferredSessionCourtSyncRef.current.has(key)) {
        deferredSessionCourtSyncRef.current.delete(key);
        await mergeCourtOnce();
      }
    } else if (table === 'session_players') {
      if (waitlistReorderInFlightRef.current) return;

      // Roster membership / waitlist changes — refresh authoritative slice for passive tabs.
      if (eventType === 'INSERT' || eventType === 'UPDATE') {
        if (newRow) patchSessionPlayerFromRow(newRow);
        if (waitlistPlayerWriteLocksRef.current.size === 0) {
          scheduleWaitlistRosterRefresh();
        }
      } else if (eventType === 'DELETE') {
        if (waitlistPlayerWriteLocksRef.current.size === 0) {
          scheduleWaitlistRosterRefresh();
        }
      }

      const scheduleCourtResyncFromPlayers = (courtNumber, courtType) => {
        if (courtNumber == null || !courtType) return;
        if (courtType !== 'active' && courtType !== 'next_up') return;
        const ck = `${courtType}:${courtNumber}`;
        if (localCourtWriteLocksRef.current.has(ck)) return;
        if (rowChangePendingCourtsRef.current.has(ck)) {
          deferredSessionCourtSyncRef.current.add(ck);
        } else {
          queueMicrotask(() => {
            void handleRowChange('session_courts', 'UPDATE', {
              court_number: courtNumber,
              court_type: courtType,
            });
          });
        }
      };

      // finish_session_match (and similar) clears session_players.court_* — new row has no court.
      // Without this, passive tabs never re-fetch the empty active slot; "Promote" stays until a lucky event.
      if ((eventType === 'UPDATE' || eventType === 'DELETE') && oldRow) {
        const oNum = oldRow.court_number;
        const oType = oldRow.court_type;
        const stillOnSameSlot =
          eventType === 'UPDATE' &&
          newRow?.court_number === oNum &&
          newRow?.court_type === oType &&
          (newRow?.player_list_status === 'on_court' || newRow?.player_list_status === 'next_up');
        if (oNum != null && (oType === 'active' || oType === 'next_up') && !stillOnSameSlot) {
          scheduleCourtResyncFromPlayers(oNum, oType);
        }
      }

      const courtNumberSp = newRow?.court_number;
      const courtTypeSp   = newRow?.court_type;
      const listStatus    = newRow?.player_list_status;

      // Lineup data lives on session_players. After score + rotate, many player rows
      // can change in one transaction; re-merge the affected court so passive tabs
      // converge even if session_courts notifications were coalesced or reordered.
      if (
        courtNumberSp != null &&
        (courtTypeSp === 'active' || courtTypeSp === 'next_up') &&
        (listStatus === 'on_court' || listStatus === 'next_up')
      ) {
        scheduleCourtResyncFromPlayers(courtNumberSp, courtTypeSp);
      }

      // Granular waiting list merge.
      //
      // When a player is added to or removed from the waitlist on another device,
      // Supabase fires a session_players event with the updated row. We merge that
      // single row into local waitingList state — sorted by waitlist_position — so
      // other devices see the change instantly without a full reload.
      //
      // We only act on waitlist status changes here. Court-slot changes are handled
      // by the session_courts branch above (court event → re-fetch court + players).
      const playerId = newRow?.player_id ?? (eventType === 'DELETE' ? oldRow?.player_id : undefined);
      const prevListStatus = oldRow?.player_list_status;
      const newListStatus = newRow?.player_list_status;

      if (!playerId) return;
      if (waitlistPlayerWriteLocksRef.current.has(playerId)) return;

      if (eventType === 'DELETE') {
        if (prevListStatus === 'waitlist') {
          setWaitingList(prev => prev.filter(id => id !== playerId));
        }
        return;
      }

      const patchWaitlistCooldownFromRow = () => {
        if (!newRow || newListStatus !== 'waitlist') return;
        const cooldownChanged =
          oldRow?.is_on_cooldown !== newRow.is_on_cooldown ||
          oldRow?.courts_watched !== newRow.courts_watched;
        if (!cooldownChanged) return;
        setPlayerWaitMetrics((prev) => ({
          ...prev,
          [playerId]: {
            ...(prev[playerId] || {}),
            courtsObserved: newRow.courts_watched ?? prev[playerId]?.courtsObserved ?? 0,
            isOnCooldown: Boolean(newRow.is_on_cooldown),
            sessionPlayerRowUpdatedAt: newRow.updated_at ?? prev[playerId]?.sessionPlayerRowUpdatedAt ?? null,
            lastCourtUpdate: newRow.last_court_update ?? prev[playerId]?.lastCourtUpdate ?? null,
            lastGameCompletedAt: newRow.last_game_completed_at ?? prev[playerId]?.lastGameCompletedAt ?? null,
          },
        }));
      };

      // Player joined waitlist on another device → add to local list if not already there
      if (newListStatus === 'waitlist') {
        setWaitingList((prev) => {
          if (prev.includes(playerId)) return prev;
          const updated = [...prev];
          updated.push(playerId);
          return updated;
        });
        patchWaitlistCooldownFromRow();
        return;
      }

      patchWaitlistCooldownFromRow();

      // Drop from local waitlist when the row leaves waitlist status, or when placed on
      // active/upcoming courts (promotion can leave a stale local waitlist entry if the
      // player was never waitlist in DB but appeared there from a partial merge).
      // Mark-as-playing from signed_up updates `status` but often leaves player_list_status
      // null until joinWaitlist/debounced save — an UPDATE with (null → null) must not
      // remove the optimistic waitlist add (previous code treated null as "left waitlist").
      if (
        (prevListStatus === 'waitlist' && newListStatus !== 'waitlist') ||
        newListStatus === 'on_court' ||
        newListStatus === 'next_up'
      ) {
        setWaitingList((prev) => prev.filter((id) => id !== playerId));
      }
    }
  }, [sessionId, handleRemoteSessionSync, scheduleStructuralSessionResync, scheduleWaitlistRosterRefresh, patchSessionPlayerFromRow, setCourtAssignments, setNextAssignments, setPlayerWaitMetrics, offlineQueue, sessionData]);

  const handleSessionStatusChange = useCallback((status) => {
    if (!status) return;
    setLiveSessionStatus(status);
    setSessionData((prev) => (prev ? { ...prev, session_status: status } : prev));
  }, []);

  const handleReopenSession = useCallback(async () => {
    if (!sessionId || reopeningSession) return;
    setReopeningSession(true);
    try {
      await updateSessionStatus(sessionId, 'ongoing');
      handleSessionStatusChange('ongoing');
      addToast({ message: 'Session reopened — you can continue running games.', variant: 'success', duration: 5000 });
    } catch (err) {
      console.error('Error reopening session:', err);
      addToast({
        message: `Could not reopen session: ${err.message || 'Unknown error'}`,
        variant: 'error',
        duration: 6000,
      });
    } finally {
      setReopeningSession(false);
    }
  }, [sessionId, reopeningSession, handleSessionStatusChange, addToast]);

  useCompetitiveSessionRealtime({
    sessionId,
    enabled: Boolean(isDataReady && sessionId && !sessionLoading && sessionData),
    useNormalizedState: sessionData?.use_normalized_state !== false,
    isSessionHost,
    // Normalized sessions have no debounced snapshot saves — save guard is not needed.
    // Passing null lets scheduleSync fire immediately on all non-authoritative events.
    isSavingGetter: sessionData?.use_normalized_state !== false
      ? null
      : () => Boolean(sessionLogger?.isSaving) || pendingDebounceRef.current,
    // Normalized sessions always converge through Realtime — never skip for the initiator.
    shouldSkipWriterRealtimeSync: sessionData?.use_normalized_state !== false
      ? () => false
      : () => Boolean(
          isSessionHost &&
          sessionSyncCoordinatorRef.current?.shouldSkipWriterRealtimeSync(true),
        ),
    onRowChange:             sessionData?.use_normalized_state !== false ? handleRowChange : undefined,
    onRemoteSync:            handleRemoteSessionSync,
    onScheduledGamesChange:  loadScheduledGames,
    onSessionStatusChange:   handleSessionStatusChange,
    // Phase C: passive tabs apply SCORE_COMPLETED result directly, without a full reload.
    // Idempotent: the initiator already applied the result from the EF response, so the
    // revision gate in applyScoreCompletedResult skips the duplicate.
    onScoreCommandResult:    onApplyScoreCommandResult,
  });

  useEffect(() => {
    if (!categorisation.categorizationEnabled) return;
    if (playersLoading) return;
    if (!isDataReady) return;
    if (!categorisation.shouldRecalculateCategories()) return;
    categorisation.calculatePlayerCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- categorisation identity omitted to avoid unnecessary recalc
  }, [categorisation.categorizationEnabled, playersLoading, isDataReady, categorisation.shouldRecalculateCategories, categorisation.calculatePlayerCategories]);

  const describePartnerBlock = useCallback((partnerId) => {
    if (playersOnCourtsSet.has(partnerId)) {
      return 'Partner currently on court';
    }
    if (playersInUpcomingSet.has(partnerId)) {
      return 'Partner is queued for the next match';
    }
    if (sessionPlayerScheduledGames[partnerId]) {
      return 'Partner reserved for a scheduled game';
    }
    if (sessionPlayerStatus[partnerId] !== 'active') {
      return 'Partner is not active';
    }
    if (!waitingListSet.has(partnerId)) {
      return 'Partner not yet in the queue';
    }
    return 'Partner unavailable';
  }, [playersOnCourtsSet, playersInUpcomingSet, sessionPlayerScheduledGames, sessionPlayerStatus, waitingListSet]);

  const evaluatePartnershipEligibility = useCallback((candidateIds = []) => {
    if (!candidateIds || candidateIds.length === 0) {
      return { eligibleIds: [], lockedMap: new Map() };
    }

    const candidateSet = new Set(candidateIds);
    const eligibleIds = [];
    const lockedMap = new Map();

    candidateIds.forEach((playerId) => {
      const partnerId = partnershipLookup[playerId];
      if (!partnerId) {
        eligibleIds.push(playerId);
        return;
      }

      const partnerReady =
        candidateSet.has(partnerId) &&
        sessionPlayerStatus[partnerId] === 'active' &&
        !sessionPlayerScheduledGames[partnerId] &&
        !playersOnCourtsSet.has(partnerId) &&
        !playersInUpcomingSet.has(partnerId);

      if (partnerReady) {
        eligibleIds.push(playerId);
      } else {
        lockedMap.set(playerId, {
          partnerId,
          reason: describePartnerBlock(partnerId),
        });
      }
    });

    return { eligibleIds, lockedMap };
  }, [
    partnershipLookup,
    sessionPlayerStatus,
    sessionPlayerScheduledGames,
    playersOnCourtsSet,
    playersInUpcomingSet,
    describePartnerBlock,
  ]);

  const notifyPartnershipSplitIfNeeded = useCallback(
    (team1, team2) => {
      if (isSinglesFormat(matchFormat) || !partnerships?.length) return;
      const splits = findPartnershipSplitsInRoster(team1, team2, partnerships);
      const message = describePartnershipSplitsSummary(splits, playersByIdWithGames);
      if (message) {
        addToast({ message, variant: 'warning', duration: 8000 });
      }
    },
    [matchFormat, partnerships, playersByIdWithGames, addToast],
  );

  // Function to manually recalculate and save waiting list (moved here after useCourtManagement)
  const recalculateAndSaveWaitingList = useCallback(() => {
    if (!sessionLogger || !stateLoadedFromDatabase) return;
    
    
    // Calculate the current waiting list (all players not on courts or in upcoming matches)
    const playersOnCourts = new Set();
    courtAssignments.forEach(court => {
      if (court.team1) court.team1.forEach(id => playersOnCourts.add(id));
      if (court.team2) court.team2.forEach(id => playersOnCourts.add(id));
    });
    
    const playersInUpcoming = new Set();
    nextAssignments.forEach(match => {
      if (match.team1) match.team1.forEach(id => playersInUpcoming.add(id));
      if (match.team2) match.team2.forEach(id => playersInUpcoming.add(id));
    });
    
    // Get all active players who are not on courts or in upcoming matches
    const allActivePlayers = Object.keys(playersById).filter(id => 
      playersById[id] && sessionPlayerStatus[id] === 'active'
    );
    
    const calculatedWaitingList = allActivePlayers.filter(id => 
      !playersOnCourts.has(id) && !playersInUpcoming.has(id)
    );
    
    
    // FIXED: No longer need duplicate detection since root causes are fixed
    // The calculated waiting list should now be clean without duplicates
    
    
    // VALIDATION: Check for duplicates in calculated waiting list
    const uniqueCalculatedWaitingList = [...new Set(calculatedWaitingList)];
    if (calculatedWaitingList.length !== uniqueCalculatedWaitingList.length) {
      console.error('🚨 VALIDATION ERROR: Duplicate players detected in calculated waiting list!', {
        totalPlayers: calculatedWaitingList.length,
        uniquePlayers: uniqueCalculatedWaitingList.length,
        duplicates: calculatedWaitingList.length - uniqueCalculatedWaitingList.length,
        allPlayers: calculatedWaitingList,
        uniquePlayersList: uniqueCalculatedWaitingList
      });
      alert(`Validation failed: ${calculatedWaitingList.length - uniqueCalculatedWaitingList.length} duplicate players detected in the queue. This should not happen - please report this bug.`);
      return; // Don't update state if validation fails
    }
    
    setWaitingList(calculatedWaitingList);
  }, [sessionLogger, stateLoadedFromDatabase, courtAssignments, nextAssignments, playersById, sessionPlayerStatus, setWaitingList]);

  useEffect(() => {
    afterAutoRotateFromScoreRef.current = async () => {
      await fetchGamesPlayedCount();
      recalculateAndSaveWaitingList();
    };
  }, [fetchGamesPlayedCount, recalculateAndSaveWaitingList]);

  const applyLocalFillToUi = useCallback((fill, { courtType, index }) => {
    const applied = applyLocalCourtFill(
      { courtAssignments, nextAssignments, waitingList },
      fill,
      { courtType, index }
    );
    if (courtType === 'active') {
      setCourtAssignments(applied.courtAssignments);
    } else {
      setNextAssignments(applied.nextAssignments);
    }
    setWaitingList(applied.waitingList);
    return applied;
  }, [courtAssignments, nextAssignments, waitingList, setCourtAssignments, setNextAssignments, setWaitingList]);

  const fillCourtFromLocalEngine = useCallback(async ({ courtType, index, expectedVersion = -1 }) => {
    const local = runLocalCourtFill(liveStateRef.current, [{ court_type: courtType, index }]);
    if (!local.ok || !local.fills?.length) {
      const msg = local.errors?.join('; ') || 'Local matchmaking could not fill this court.';
      addToast({ message: msg, variant: 'warning', duration: 6000 });
      return { success: false, message: msg };
    }
    const fill = local.fills[0];
    const applied = applyLocalFillToUi(fill, { courtType, index });
    notifyPartnershipSplitIfNeeded(fill.team1, fill.team2);
    const scheduledId = fill.type === 'scheduled' ? fill.selectedScheduledGame?.id : null;
    const scheduledGamesNext = scheduledId
      ? (liveStateRef.current?.scheduledGames || scheduledGames || []).map((game) =>
          String(game.id) === String(scheduledId) ? { ...game, status: 'placed' } : game
        )
      : liveStateRef.current?.scheduledGames || scheduledGames;
    if (scheduledId) {
      setScheduledGames(scheduledGamesNext);
      void consumeScheduledGamesPlaced([scheduledId], { placedIds: [scheduledId] });
    }
    const snapshot = { ...liveStateRef.current, ...applied, scheduledGames: scheduledGamesNext };
    liveStateRef.current = snapshot;
    const queued = await offlineQueue.enqueue(
      buildSeatCommandFromFill(fill, { courtType, index, expectedVersion }),
      snapshot
    );
    addToast({
      message: 'Game created on this device. Waiting to save when the connection is usable.',
      variant: 'success',
      duration: 4000,
    });
    return { success: true, local: true, fill, queuedCommandId: queued?.id };
  }, [addToast, applyLocalFillToUi, notifyPartnershipSplitIfNeeded, offlineQueue, scheduledGames, consumeScheduledGamesPlaced, setScheduledGames]);

  // Wrapper: fill empty or partial active court (Create Game) via server matchmaking.
  const handleFillEmptyCourt = useCallback(async (courtNumber) => {
    if (isPegBoard) {
      return { success: false, message: 'Use the pick panel to place the next Peg Board game.' };
    }
    const court = courtAssignments.find((c) => c.courtNumber === courtNumber);
    if (!court) {
      return { success: false, message: 'Court not found' };
    }

    const pps = playersPerSideForSession;
    const t1 = court.team1?.length ?? 0;
    const t2 = court.team2?.length ?? 0;
    const isFullyEmpty = t1 === 0 && t2 === 0;
    const isPartial =
      !isFullyEmpty &&
      !court.inProgress &&
      (t1 < pps || t2 < pps);

    if (sessionId && (isFullyEmpty || isPartial)) {
      const startFill = () => planCourtFillCmd({
        sessionId,
        targetKind: 'active',
        targetIndex: courtNumber,
        tabInstanceId,
      });
      const expectedVersion = court.version ?? -1;
      if (shouldRunLocalHostPath(offlineQueue, sessionData)) {
        return fillCourtFromLocalEngine({ courtType: 'active', index: courtNumber, expectedVersion });
      }
      const efResult = await startFill();

      if (!efResult?.ok) {
        const msg = efResult?.message || 'Server matchmaking failed for active court.';
        if (efResult?.code === 'COURT_ALREADY_FILLED' || efResult?.code === 'SLOT_ALREADY_FILLED') {
          void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
        }
        addToast({ message: msg, variant: 'warning', duration: 6000 });
        return { success: false, message: msg };
      }

      const fill = efResult.fills?.[0];
      if (fill?.team1 && fill?.team2) {
        notifyPartnershipSplitIfNeeded(fill.team1, fill.team2);
      }

      await handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
      await fetchGamesPlayedCount();
      addToast({
        message: isPartial
          ? `Court ${courtNumber} roster completed via server matchmaking`
          : `Court ${courtNumber} filled via server matchmaking`,
        variant: 'success',
        duration: 4000,
      });
      return { success: true, message: 'Court filled via server matchmaking' };
    }

    if (sessionId) {
      return {
        success: false,
        message: 'Court cannot be filled via matchmaking in its current state.',
      };
    }

    const result = fillEmptyCourt(courtNumber);
    await fetchGamesPlayedCount();
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- courtAssignments intentional for version lookup
  }, [
    fillEmptyCourt,
    fetchGamesPlayedCount,
    sessionId,
    courtAssignments,
    playersPerSideForSession,
    tabInstanceId,
    planCourtFillCmd,
    handleRemoteSessionSync,
    notifyPartnershipSplitIfNeeded,
    addToast,
    isPegBoard,
    offlineQueue,
    sessionData,
    fillCourtFromLocalEngine,
  ]);

  // Wrapper: clear court (header X) + return players to waitlist in DB.
  const handleRemoveCourt = useCallback(async (courtIndex) => {
    const srcCourt = courtAssignments[courtIndex];
    if (!srcCourt) return;
    if (isAssignmentRowLockedByScheduledGame(srcCourt, scheduledGames)) {
      openScheduledGameCancelFromRow(srcCourt, 'court_clear');
      return;
    }

    const courtNum = srcCourt.courtNumber;
    const playerIds = [...(srcCourt.team1 || []), ...(srcCourt.team2 || [])];
    playerIds.forEach((id) => waitlistPlayerWriteLocksRef.current.add(id));

    removeCourt(courtIndex);

    try {
    if (sessionId && sessionData?.use_normalized_state !== false) {
      const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNum];
      const expectedVersion = ackedVersion ?? srcCourt.version ?? -1;
      const lockKey = `active:${courtNum}`;
      localCourtWriteLocksRef.current.add(lockKey);
      try {
        const clearResult = await assignPlayersToCourtCmd(
          sessionId,
          courtNum,
          'active',
          expectedVersion,
          {
            inProgress: false,
            team1: [],
            team2: [],
            gameCreation: ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL),
          },
          tabInstanceId,
        );
        if (clearResult.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: {
              ...lastAckedCourtVersionsRef.current.active,
              [courtNum]: clearResult.newVersion,
            },
          };
        } else {
          addToast({
            message: clearResult.message || 'Could not clear court on server.',
            variant: 'warning',
            duration: 6000,
          });
        }
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
      }

      try {
        for (const playerId of playerIds) {
          const wlResult = await joinWaitlistCmd(sessionId, playerId, tabInstanceId);
          if (!wlResult.ok) {
            console.error('[handleRemoveCourt] joinWaitlistCmd failed:', wlResult);
          }
        }
        if (isPegBoard && playerIds.length > 0) {
          waitlistReorderInFlightRef.current = true;
          try {
            const ordered = requeueUnplayedPlayers(waitingList, playerIds);
            await reorderWaitlistCmd(sessionId, ordered, tabInstanceId);
          } finally {
            waitlistReorderInFlightRef.current = false;
          }
        }
      } catch (err) {
        console.error('[handleRemoveCourt] waitlist persist failed:', err);
      }
    }
    } finally {
      playerIds.forEach((id) => waitlistPlayerWriteLocksRef.current.delete(id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- removeCourt stable from hook
  }, [
    courtAssignments,
    scheduledGames,
    removeCourt,
    sessionId,
    sessionData,
    tabInstanceId,
    addToast,
    openScheduledGameCancelFromRow,
    isPegBoard,
    waitingList,
    requeueUnplayedPlayers,
  ]);

  // Wrapper function for adjustCourtCount that includes database logging
  // NOTE: adjustCourtCount is async and already handles database updates directly
  // We should NOT save state here as it would overwrite the database changes with old state
  const handleAdjustCourtCount = useCallback(async (increment) => {
    
    // Call the async adjustCourtCount and wait for it to complete
    // adjustCourtCountHandler already updates the database directly, so we don't need to save again
    await adjustCourtCount(increment);
      
      // Fetch updated games played count after court count adjustment
    // The state will be updated by adjustCourtCountHandler, and the debounced save will handle it
      fetchGamesPlayedCount();
  }, [adjustCourtCount, courtAssignments, nextAssignments, waitingList, fetchGamesPlayedCount]);

  // -------------------------------------------------------------------------
  // Granular debounced save
  //
  // Instead of a full-snapshot save, we detect which courts changed and write
  // them atomically one-by-one via update_court_state_atomic (OCC RPC).
  // The waiting list and next-up queue continue to use their existing functions.
  //
  // latestStateRef is always the freshest snapshot — the debounce callback reads
  // it so stale closures never cause a stale write.
  // -------------------------------------------------------------------------
  // latestStateRef is declared above (score snapshot + debounce source of truth).
  const prevStateRef   = useRef({ courtAssignments: null, nextAssignments: null, waitingList: null });
  const saveTimeoutRef = useRef(null);

  // True while a save is pending in the debounce queue OR actively writing to DB.
  // Prevents realtime sync from overwriting optimistic state mid-write.
  const pendingDebounceRef        = useRef(false);
  const activeSaveCountRef        = useRef(0);

  // -------------------------------------------------------------------------
  // ACKed-state layer:
  //
  // lastAckedCourtVersionsRef — authoritative OCC version per court slot,
  //   structured as { active: { courtNumber: version }, next_up: { courtNumber: version } }.
  //   Separate from optimistic React state and prevStateRef to prevent
  //   stale-baseline-generated conflicts.
  //
  // dirtyCourtsRef — court keys ("active:N", "next_up:N") that conflicted
  //   and need a retry on the next flush (triggered by realtime merge).
  //
  // pendingImmediateFlushRef — when true, next debounce fires at 0ms instead
  //   of 300ms (set by DnD gesture-end and click-add to court).
  // -------------------------------------------------------------------------
  const lastAckedCourtVersionsRef = useRef({ active: {}, next_up: {} });
  const dirtyCourtsRef            = useRef(new Set());
  const pendingImmediateFlushRef  = useRef(false);

  const markSaveStart = useCallback(() => {
    activeSaveCountRef.current += 1;
    pendingDebounceRef.current = true;
  }, []);

  const markSaveEnd = useCallback(() => {
    activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
    if (activeSaveCountRef.current === 0) pendingDebounceRef.current = false;
  }, []);

  /**
   * Persist full next_up slots before score commit so finish_session_match can promote from DB.
   * Slots missing courtNumber are normalized to 0-based indices (matches setNextUpSlotCmd).
   */
  const flushNextUpBeforeScore = useCallback(async () => {
    if (!sessionId) return;

    const snap = latestStateRef.current;
    const prevNext = prevStateRef.current.nextAssignments || [];
    const slots = normalizeNextUpCourtNumbers(snap.nextAssignments || []);
    const pps = playersPerSideForSession;

    latestStateRef.current = {
      ...snap,
      nextAssignments: slots,
    };

    for (let idx = 0; idx < slots.length; idx += 1) {
      const slot = slots[idx];
      const n1 = (slot.team1 || []).length;
      const n2 = (slot.team2 || []).length;
      if (n1 !== pps || n2 !== pps) continue;

      const prevSlot = prevNext.find((s, i) => (s.courtNumber ?? i) === slot.courtNumber);
      const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slot.courtNumber];
      if (prevSlot && sameCourtLineup(prevSlot, slot) && ackedVersion != null) continue;

      const lockKey = `next_up:${slot.courtNumber}`;
      if (localCourtWriteLocksRef.current.has(lockKey)) continue;

      localCourtWriteLocksRef.current.add(lockKey);
      markSaveStart();
      try {
        const expectedVersion = ackedVersion ?? slot.version ?? -1;
        const result = await setNextUpSlotCmd(
          sessionId,
          slot.courtNumber,
          expectedVersion,
          {
            inProgress: slot.inProgress || false,
            team1: slot.team1 || [],
            team2: slot.team2 || [],
            gameCreation: slot.gameCreation,
          },
          tabInstanceId,
        );
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: {
              ...lastAckedCourtVersionsRef.current.next_up,
              [slot.courtNumber]: result.newVersion,
            },
          };
          const ackedSlot = { ...slot, version: result.newVersion };
          prevStateRef.current = {
            ...prevStateRef.current,
            nextAssignments: normalizeNextUpCourtNumbers(
              (prevStateRef.current.nextAssignments || []).map((s, i) =>
                (s.courtNumber ?? i) === slot.courtNumber ? ackedSlot : s,
              ),
            ),
          };
        } else {
          console.warn('[quiescence] next_up pre-score flush failed for slot', slot.courtNumber, result);
          dirtyCourtsRef.current.add(`next_up:${slot.courtNumber}`);
        }
      } catch (err) {
        console.warn('[quiescence] next_up pre-score flush error for slot', slot.courtNumber, err);
        dirtyCourtsRef.current.add(`next_up:${slot.courtNumber}`);
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
        markSaveEnd();
      }
    }
  }, [
    sessionId,
    playersPerSideForSession,
    tabInstanceId,
    markSaveStart,
    markSaveEnd,
  ]);

  /**
   * Acquires the score-prep lock: sets scorePrepInFlightRef and polls until the
   * next_up queue is quiescent (no pending debounce, no active saves, no in-flight
   * write locks).  Waits up to 3 s before proceeding anyway with a warning.
   * The caller MUST invoke releaseScorePrepLock() in its finally block.
   */
  const acquireScorePrepLock = useCallback(async () => {
    scorePrepInFlightRef.current = true;
    commandEpochRef.current += 1;

    // Normalized sessions have no debounced snapshot saves to quiesce —
    // all writes are direct RPCs that complete before returning to the caller.
    if (sessionData?.use_normalized_state !== false) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingDebounceRef.current = false;
    if (sessionLogger) sessionLogger.cancelPendingSave?.();

    await flushNextUpBeforeScore();

    const maxWait = 1500;
    const start = Date.now();
    while (true) {
      const nextUpLocked = [...localCourtWriteLocksRef.current]
        .some(k => k.startsWith('next_up:'));
      if (activeSaveCountRef.current === 0 && !nextUpLocked) break;
      if (Date.now() - start > maxWait) {
        console.warn('[quiescence] Timeout waiting for in-flight court writes — proceeding with score submit');
        break;
      }
      await new Promise(r => setTimeout(r, 30));
    }
  }, [sessionData?.use_normalized_state, sessionLogger, flushNextUpBeforeScore]);

  const releaseScorePrepLock = useCallback(() => {
    scorePrepInFlightRef.current = false;
  }, []);

  acquireScorePrepLockRef.current = acquireScorePrepLock;
  releaseScorePrepLockRef.current = releaseScorePrepLock;
  
  React.useLayoutEffect(() => {
    latestStateRef.current = { courtAssignments, nextAssignments, waitingList, playerWaitMetrics };

    if (!sessionLogger || !stateLoadedFromDatabase) return;
    if (!sessionId) return;

    // Normalized sessions: all writes go through direct RPC handlers — the debounce
    // loop is a no-op. Don't set pendingDebounceRef so isSavingGetter stays false.
    if (sessionData?.use_normalized_state !== false) return;

    // Also fire when dirty courts are queued (even if React state matches baseline).
    if (!sessionStateChanged(prevStateRef.current, latestStateRef.current) && dirtyCourtsRef.current.size === 0) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    pendingDebounceRef.current = true;
    // Capture epoch at scheduling time. If a structural RPC completes before the timer
    // fires, commandEpochRef will have advanced and the callback skips the stale write.
    const epochAtSchedule = commandEpochRef.current;
    // Honour DnD gesture-end / click-add immediate-flush requests.
    const useImmediateFlush = pendingImmediateFlushRef.current;
    pendingImmediateFlushRef.current = false;

    saveTimeoutRef.current = setTimeout(async () => {
      // A structural server command completed after this save was queued — its
      // authoritative snapshot already applied.  Discard the stale pre-RPC layout.
      if (commandEpochRef.current !== epochAtSchedule) {
        pendingDebounceRef.current = false;
        return;
      }

      // Normalized sessions: all layout writes go through direct RPC calls in
      // individual handlers (assignPlayersToCourtCmd, setNextUpSlotCmd, etc.).
      // The debounce loop exists only as a persistence fallback for legacy JSONB sessions.
      if (sessionData?.use_normalized_state !== false) {
        pendingDebounceRef.current = false;
        return;
      }

      const snap = latestStateRef.current;
      const prev = prevStateRef.current;

      // ACKed baseline — starts from the last confirmed state and is advanced
      // per-resource only when a write actually succeeds. This replaces the
      // previous prevStateRef.current = snap before RPCs (which falsely marked
      // everything as persisted before any write completed).
      const ackedBaseline = {
        courtAssignments: prev.courtAssignments ? [...prev.courtAssignments] : [],
        nextAssignments:  prev.nextAssignments  ? [...prev.nextAssignments]  : [],
        waitingList:      prev.waitingList      ? [...prev.waitingList]      : [],
      };

      // Non-host viewers merge realtime into React only — never write courts/waitlist back
      // (would clobber the host's promotion after finish_match with a stale "empty" snapshot).
      if (sessionLogger.readOnly) {
        pendingDebounceRef.current = false;
        return;
      }

      // -----------------------------------------------------------------------
      // Pre-compute waitlist diff before court writes so we can restore players
      // if a coupled court write fails (prevents orphaning: player off waitlist
      // in DB + off court in DB when the court write conflicted).
      // -----------------------------------------------------------------------
      const prevWaiting    = prev.waitingList || [];
      const nextWaiting    = snap.waitingList || [];
      const prevWaitingSet = new Set(prevWaiting);
      const nextWaitingSet = new Set(nextWaiting);
      const addedPlayers   = nextWaiting.filter(id => !prevWaitingSet.has(id));
      const removedPlayers = prevWaiting.filter(id => !nextWaitingSet.has(id));
      const keptPlayers    = nextWaiting.filter(id => prevWaitingSet.has(id));
      const prevKeptOrder  = prevWaiting.filter(id => nextWaitingSet.has(id));
      const orderChanged   = keptPlayers.some((id, i) => id !== prevKeptOrder[i]);

      // -----------------------------------------------------------------------
      // 1. Active courts — per-court atomic writes (OCC)
      // -----------------------------------------------------------------------
      const useNormalized = sessionData?.use_normalized_state !== false;

      if (useNormalized) {
        const prevCourts = prev.courtAssignments || [];
        const nextCourts = snap.courtAssignments || [];

        // Include dirty-flagged courts even when lineup matches baseline (need version sync).
        const dirtyActiveKeys = new Set(
          [...dirtyCourtsRef.current].filter(k => k.startsWith('active:'))
        );
        const changedCourts = nextCourts.filter(court => {
          const prevCourt = prevCourts.find(c => c.courtNumber === court.courtNumber);
          return !prevCourt
            || !sameCourtLineup(prevCourt, court)
            || dirtyActiveKeys.has(`active:${court.courtNumber}`);
        });
        // Clear dirty flags for courts we're about to attempt this flush.
        changedCourts.forEach(court => dirtyCourtsRef.current.delete(`active:${court.courtNumber}`));

        let courtBatchHadConflict = false;

        for (const court of changedCourts) {
          // Host tab: finish_session_match cleared session_players on the server but
          // local state still has team1/team2 until rotate. Skipping persist avoids
          // putting the finished lineup back on the court. If the host edits the
          // roster before rotating, the sig changes and we resume normal persistence.
          if (serverClearedActiveCourtsRef.current.has(court.courtNumber)) {
            const expected = serverClearedCourtRosterSigRef.current[court.courtNumber];
            if (expected === courtRosterSig(court)) {
              continue;
            }
            serverClearedActiveCourtsRef.current.delete(court.courtNumber);
            delete serverClearedCourtRosterSigRef.current[court.courtNumber];
          }

          // Use lastAckedCourtVersionsRef as the authoritative OCC version —
          // avoids stale-baseline-derived conflicts when prevCourt.version
          // may reflect an unacknowledged optimistic state.
          const ackedVersion    = lastAckedCourtVersionsRef.current.active?.[court.courtNumber];
          const expectedVersion = ackedVersion ?? court.version ?? -1;

          const lockKey = `active:${court.courtNumber}`;
          localCourtWriteLocksRef.current.add(lockKey);
          markSaveStart();
          try {
            console.debug('[court-persist]', {
              courtNumber: court.courtNumber, expectedVersion, ackedVersion,
              lineup: `${court.team1?.length ?? 0}v${court.team2?.length ?? 0}`,
            });
            const result = await updateSingleCourtAtomic(
              sessionId,
              court.courtNumber,
              'active',
              expectedVersion,
              {
                inProgress:   court.inProgress || false,
                team1:        court.team1 || [],
                team2:        court.team2 || [],
                gameCreation: court.gameCreation,
              },
              tabInstanceId
            );
            // ACK: record confirmed version and advance this court's baseline slice.
            lastAckedCourtVersionsRef.current = {
              ...lastAckedCourtVersionsRef.current,
              active: { ...lastAckedCourtVersionsRef.current.active, [court.courtNumber]: result.newVersion },
            };
            const ackedCourt = snap.courtAssignments?.find(c => c.courtNumber === court.courtNumber);
            if (ackedCourt) {
              const ackedWithVersion = { ...ackedCourt, version: result.newVersion };
              const idx = ackedBaseline.courtAssignments.findIndex(c => c.courtNumber === court.courtNumber);
              if (idx !== -1) {
                ackedBaseline.courtAssignments = ackedBaseline.courtAssignments.map((c, i) =>
                  i === idx ? ackedWithVersion : c
                );
              } else {
                ackedBaseline.courtAssignments = [...ackedBaseline.courtAssignments, ackedWithVersion];
              }
            }
            // Sync version into React state for the next render cycle.
            setCourtAssignments(prev => prev.map(c =>
              c.courtNumber === court.courtNumber ? { ...c, version: result.newVersion } : c
            ));
          } catch (err) {
            if (err instanceof CourtConflictError) {
              courtBatchHadConflict = true;
              console.debug('[court-conflict]', {
                courtNumber: court.courtNumber, expectedVersion, currentVersion: err.currentVersion,
                snapLineup: `${court.team1?.length ?? 0}v${court.team2?.length ?? 0}`,
              });
              // Store DB's current version so the next retry uses the correct expected version.
              lastAckedCourtVersionsRef.current = {
                ...lastAckedCourtVersionsRef.current,
                active: { ...lastAckedCourtVersionsRef.current.active, [court.courtNumber]: err.currentVersion },
              };
              // Mark dirty for retry via the next natural flush (realtime merge will trigger it).
              // No synchronous re-fetch here — that can cause retry storms during DnD/reorder.
              dirtyCourtsRef.current.add(`active:${court.courtNumber}`);
              // OCC conflict: concurrent host writes on the same court — log only, retry via next flush.
              console.warn(`[court-conflict] Court ${court.courtNumber} OCC mismatch — retry queued. currentVersion=${err.currentVersion}`);
            } else {
              courtBatchHadConflict = true;
              console.error(`Court ${court.courtNumber} atomic save failed:`, err);
              addToast({
                message: `Court ${court.courtNumber} couldn't be saved. Check your connection and try again.`,
                variant: 'error',
                duration: 7000,
              });
            }
            // Do NOT advance ackedBaseline for this court — leave prev slice intact
            // so the next diff still detects this court as needing a write.
          } finally {
            localCourtWriteLocksRef.current.delete(lockKey);
            markSaveEnd();
          }
        }

        // If any court write failed/conflicted, restore players that were to be
        // removed from the local waitlist — this prevents them being orphaned when
        // the coupled court write didn't commit to DB.
        if (courtBatchHadConflict && removedPlayers.length > 0) {
          setWaitingList(prev => {
            const restored = [...prev];
            removedPlayers.forEach(id => { if (!restored.includes(id)) restored.push(id); });
            return restored;
          });
        }

        // -----------------------------------------------------------------------
        // 2. Waiting list — skip entirely if any court write failed/conflicted
        //
        // Coupling rule: if a court write fails, do NOT run waitlist removes for
        // players that were added to that court in the same gesture. Running them
        // would orphan the player (off court in DB + off waitlist in DB).
        // -----------------------------------------------------------------------
        if (!courtBatchHadConflict) {
          let waitlistAllSucceeded = true;

          // Add new players — route through unified command path with lease verification
          for (const playerId of addedPlayers) {
            waitlistPlayerWriteLocksRef.current.add(playerId);
            markSaveStart();
            try {
              const addResult = await joinWaitlistCmd(sessionId, playerId, tabInstanceId);
              if (!addResult.ok) {
                waitlistAllSucceeded = false;
                console.error(`joinWaitlistCmd failed for ${playerId}:`, addResult);
                addToast({
                  message: "Couldn't add a player to the queue. Check your connection.",
                  variant: 'error',
                  duration: 6000,
                });
              }
            } catch (err) {
              waitlistAllSucceeded = false;
              console.error(`joinWaitlistCmd error for ${playerId}:`, err);
            } finally {
              waitlistPlayerWriteLocksRef.current.delete(playerId);
              markSaveEnd();
            }
          }

          // Remove departed players — route through unified command path with lease verification
          for (const playerId of removedPlayers) {
            waitlistPlayerWriteLocksRef.current.add(playerId);
            markSaveStart();
            try {
              const removeResult = await leaveWaitlistCmd(sessionId, playerId, tabInstanceId);
              if (!removeResult.ok) {
                waitlistAllSucceeded = false;
                console.error(`leaveWaitlistCmd failed for ${playerId}:`, removeResult);
                addToast({
                  message: "Couldn't remove a player from the queue. Check your connection.",
                  variant: 'error',
                  duration: 6000,
                });
              }
            } catch (err) {
              waitlistAllSucceeded = false;
              console.error(`leaveWaitlistCmd error for ${playerId}:`, err);
            } finally {
              waitlistPlayerWriteLocksRef.current.delete(playerId);
              markSaveEnd();
            }
          }

          // Persist new waitlist order after drag-reorder, or after adds that are not
          // simply appended (Peg Board returns unplayed players to the front).
          const keptFromPrev = prevWaiting.filter((id) => nextWaitingSet.has(id));
          const appendShape = [...keptFromPrev, ...addedPlayers];
          const addedOutOfAppendOrder =
            addedPlayers.length > 0 &&
            nextWaiting.some((id, i) => id !== appendShape[i]);
          if (
            (orderChanged && addedPlayers.length === 0 && removedPlayers.length === 0) ||
            addedOutOfAppendOrder
          ) {
            waitlistReorderInFlightRef.current = true;
            markSaveStart();
            try {
              const reorderResult = await reorderWaitlistCmd(sessionId, nextWaiting, tabInstanceId);
              if (!reorderResult.ok) {
                waitlistAllSucceeded = false;
                console.error('reorderWaitlistCmd failed:', reorderResult);
                addToast({
                  message: "Queue order couldn't be saved — another host may have reordered it simultaneously.",
                  variant: 'warning',
                  duration: 6000,
                });
              }
            } catch (err) {
              waitlistAllSucceeded = false;
              console.error('reorderWaitlistCmd error:', err);
            } finally {
              waitlistReorderInFlightRef.current = false;
              markSaveEnd();
            }
          }

          if (waitlistAllSucceeded) {
            ackedBaseline.waitingList = snap.waitingList;
          }
        }

        // -----------------------------------------------------------------------
        // 3. Next-up queue — per-slot atomic writes (OCC via update_court_state_atomic)
        //    Skip when court batch conflicted (same coupling logic as waitlist).
        // -----------------------------------------------------------------------
        if (!courtBatchHadConflict) {
          const prevNext = normalizeNextUpCourtNumbers(prev.nextAssignments || []);
          const nextNext = normalizeNextUpCourtNumbers(snap.nextAssignments || []);

          const prevNextCourtNums = new Set(prevNext.map((s) => s.courtNumber));
          const nextNextCourtNums = new Set(nextNext.map((s) => s.courtNumber));

          // ACKed next-up slots — updated per slot on success.
          let ackedNextSlots = [...ackedBaseline.nextAssignments];

          for (const cn of prevNextCourtNums) {
            if (nextNextCourtNums.has(cn)) continue;
            const lockKey = `next_up:delete:${cn}`;
            localCourtWriteLocksRef.current.add(lockKey);
            markSaveStart();
            try {
              await deleteNextUpCourtSlot(sessionId, cn);
              ackedNextSlots = ackedNextSlots.filter(s => s.courtNumber !== cn);
            } catch (err) {
              console.error(`deleteNextUpCourtSlot(${cn}) failed:`, err);
              addToast({
                message: 'Could not remove an extra upcoming queue slot on the server. Try again or refresh.',
                variant: 'warning',
                duration: 6000,
              });
            } finally {
              localCourtWriteLocksRef.current.delete(lockKey);
              markSaveEnd();
            }
          }

          const dirtyNextKeys = new Set(
            [...dirtyCourtsRef.current].filter(k => k.startsWith('next_up:'))
          );
          const changedNextSlots = nextNext.filter((slot, idx) => {
            const prevSlot = prevNext.find((s) => s.courtNumber === (slot.courtNumber ?? idx));
            return !prevSlot
              || !sameCourtLineup(prevSlot, slot)
              || dirtyNextKeys.has(`next_up:${slot.courtNumber}`);
          });
          changedNextSlots.forEach(slot => dirtyCourtsRef.current.delete(`next_up:${slot.courtNumber}`));

          // Score prep is in flight — defer next_up writes to avoid racing the score transition.
          // Re-queue all changed slots as dirty so they flush once the lock is released.
          if (scorePrepInFlightRef.current) {
            for (const slot of changedNextSlots) {
              dirtyCourtsRef.current.add(`next_up:${slot.courtNumber}`);
            }
          }

          for (const slot of (scorePrepInFlightRef.current ? [] : changedNextSlots)) {
            const ackedNextVersion    = lastAckedCourtVersionsRef.current.next_up?.[slot.courtNumber];
            const expectedNextVersion = ackedNextVersion ?? slot.version ?? -1;
            const lockKey = `next_up:${slot.courtNumber}`;
            localCourtWriteLocksRef.current.add(lockKey);
            markSaveStart();
            try {
              const result = await setNextUpSlotCmd(
                sessionId,
                slot.courtNumber,
                expectedNextVersion,
                {
                  inProgress:   slot.inProgress || false,
                  team1:        slot.team1 || [],
                  team2:        slot.team2 || [],
                  gameCreation: slot.gameCreation,
                },
                tabInstanceId
              );
              if (result.ok) {
                lastAckedCourtVersionsRef.current = {
                  ...lastAckedCourtVersionsRef.current,
                  next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slot.courtNumber]: result.newVersion },
                };
                setNextAssignments(prev => prev.map(s =>
                  s.courtNumber === slot.courtNumber ? { ...s, version: result.newVersion } : s
                ));
                const ackedSlot = snap.nextAssignments?.find(s => s.courtNumber === slot.courtNumber);
                if (ackedSlot) {
                  const ackedWithVersion = { ...ackedSlot, version: result.newVersion };
                  const idx = ackedNextSlots.findIndex(s => s.courtNumber === slot.courtNumber);
                  if (idx !== -1) {
                    ackedNextSlots = ackedNextSlots.map((s, i) => i === idx ? ackedWithVersion : s);
                  } else {
                    ackedNextSlots = [...ackedNextSlots, ackedWithVersion];
                  }
                }
              } else if (result.code === 'COURT_CONFLICT') {
                lastAckedCourtVersionsRef.current = {
                  ...lastAckedCourtVersionsRef.current,
                  next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slot.courtNumber]: result.currentVersion ?? slot.version },
                };
                dirtyCourtsRef.current.add(`next_up:${slot.courtNumber}`);
                // OCC conflict: concurrent host writes on the same court — log only, retry via next flush.
                // boundaries or network retries — log only, no user-facing toast.
                console.warn(`[next-up-conflict] Slot ${slot.courtNumber} OCC mismatch — retry queued. currentVersion=${result.currentVersion}`);
              } else {
                console.error(`Next-up slot ${slot.courtNumber} save failed:`, result);
              }
            } catch (err) {
              console.error(`Next-up slot ${slot.courtNumber} unexpected error:`, err);
            } finally {
              localCourtWriteLocksRef.current.delete(lockKey);
              markSaveEnd();
            }
          }

          ackedBaseline.nextAssignments = ackedNextSlots;
        }

      } else {
        // Legacy JSONB path — unchanged
        markSaveStart();
        try {
          await sessionLogger.saveCurrentState(
            snap.nextAssignments,
            snap.waitingList,
            snap.courtAssignments,
            playerInteractions,
            'TWO_STAGE',
            new Map(),
            [],
            playerWaitMetrics
          );
        } catch (error) {
          console.error('Session state save failed:', error.message);
        } finally {
          markSaveEnd();
        }
      }

      // Advance baseline to only what was actually ACKed this flush.
      prevStateRef.current = ackedBaseline;
      if (activeSaveCountRef.current === 0) pendingDebounceRef.current = false;

    }, useImmediateFlush ? 0 : 300);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      pendingDebounceRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtAssignments, nextAssignments, waitingList, sessionLogger, stateLoadedFromDatabase, sessionId, sessionData]);

  /** Persist full active-court layout after host court reorder (avoids per-court OCC version mismatch). */
  const persistCourtReorder = useCallback(
    async (renumbered) => {
      if (!sessionId || !stateLoadedFromDatabase) return;
      if (sessionLogger?.readOnly) return;
      const useNormalized = sessionData?.use_normalized_state !== false;
      if (!useNormalized) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
        pendingDebounceRef.current = false;
      }

      markSaveStart();
      try {
        await updateCourtAssignments(sessionId, renumbered, 'active', false);
        const withVersions = await Promise.all(
          renumbered.map(async (c) => {
            const fresh = await fetchSingleCourt(sessionId, c.courtNumber, 'active');
            return fresh ? { ...c, version: fresh.version } : c;
          })
        );
        setCourtAssignments(withVersions);

        // Hydrate lastAckedCourtVersionsRef with all fresh versions so the next
        // debounced write uses correct OCC versions (not pre-reorder stale ones).
        const freshActiveVersions = {};
        withVersions.forEach(c => {
          if (c.version != null) freshActiveVersions[c.courtNumber] = c.version;
        });
        lastAckedCourtVersionsRef.current = {
          ...lastAckedCourtVersionsRef.current,
          active: freshActiveVersions,
        };
        // ACK the full active-court baseline so the next diff starts clean.
        prevStateRef.current = {
          ...latestStateRef.current,
          courtAssignments: withVersions,
        };
      } catch (err) {
        console.error('Court reorder save failed:', err);
        addToast({
          message: 'Could not save court order. Try again or refresh the page.',
          variant: 'error',
          duration: 7000,
        });
      } finally {
        markSaveEnd();
      }
    },
    [
      sessionId,
      stateLoadedFromDatabase,
      sessionLogger,
      sessionData?.use_normalized_state,
      markSaveStart,
      markSaveEnd,
      addToast,
      setCourtAssignments,
    ]
  );

  const courtReorderInFlightRef = useRef(false);
  const [courtReorderInFlight, setCourtReorderInFlight] = useState(false);

  const handleMoveCourt = useCallback(
    async (courtNumber, direction) => {
      if (!isSessionHost) return;
      if (courtReorderInFlightRef.current) return;
      if (direction !== -1 && direction !== 1) return;

      const sorted = [...(courtAssignments || [])].sort((a, b) => a.courtNumber - b.courtNumber);
      const oldIndex = sorted.findIndex((c) => Number(c.courtNumber) === Number(courtNumber));
      if (oldIndex === -1) return;
      const newIndex = oldIndex + direction;
      if (newIndex < 0 || newIndex >= sorted.length) return;

      const reordered = [...sorted];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      const renumbered = reordered.map((c, i) => ({ ...c, courtNumber: i + 1 }));

      courtReorderInFlightRef.current = true;
      setCourtReorderInFlight(true);
      setCourtAssignments(renumbered);
      try {
        await persistCourtReorder(renumbered);
      } finally {
        courtReorderInFlightRef.current = false;
        setCourtReorderInFlight(false);
      }
    },
    [isSessionHost, courtAssignments, persistCourtReorder, setCourtAssignments]
  );

  // Add a ref to track if we just completed a rotation
  const justCompletedRotation = useRef(false);

  // Custom updateMatchResult handler that fetches games played count and updates streaks
  const handleUpdateMatchResultWithGamesCount = useCallback(async (courtIndex, score, testMode = false) => {
    const { needsGamesPlayedRefresh = true } =
      (await updateMatchResult(courtIndex, score, testMode)) || {};
    
    // Note: Player streaks are now calculated from gameHistory, not manually tracked
    // They will be recalculated when gameHistory is fetched after this game is saved
    
    // Update player interactions for escalating penalties
    const court = courtAssignments[courtIndex];
    if (court && court.team1 && court.team2) {
      const team1Players = court.team1.map(id => playersById[id]).filter(Boolean);
      const team2Players = court.team2.map(id => playersById[id]).filter(Boolean);
      
      // Update persistent player interactions state
      setPlayerInteractions(prevInteractions => {
        // Ensure it's a plain object (not a Map) for consistency
        const interactionsObj = prevInteractions instanceof Map 
          ? Object.fromEntries(prevInteractions)
          : (prevInteractions || {});
        const mockSessionState = { playerInteractions: interactionsObj };
        updatePlayerInteractions(team1Players, team2Players, mockSessionState);
        // Return the updated object (updatePlayerInteractions modifies it in place)
        return { ...mockSessionState.playerInteractions };
      });
      
    }
    
    if (needsGamesPlayedRefresh) {
    await fetchGamesPlayedCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- waitingList intentional; full deps would over-trigger
  }, [updateMatchResult, fetchGamesPlayedCount, courtAssignments, playersById, waitingList]);

  // Custom rotation handler that sets the flag
  const handleRotationWithWaitingListUpdate = useCallback(() => {
    justCompletedRotation.current = true;

    // Get the finished player IDs from rotation
    // Note: Wait metrics are reset inside rotateAndRefillCourts BEFORE state updates
    // to ensure cooldown icons display immediately
    const finishedPlayerIds = rotateAndRefillCourts();
    

    // Fetch updated games played count after rotation
    fetchGamesPlayedCount();
  }, [rotateAndRefillCourts, fetchGamesPlayedCount]);

  // Listen for changes after rotation and recalculate waiting list
  React.useEffect(() => {
    if (justCompletedRotation.current && sessionLogger && stateLoadedFromDatabase) {
      
      // Use async function to handle the async operations
      const handleRotationComplete = async () => {
        await fetchGamesPlayedCount(); // Fetch updated games played count
        recalculateAndSaveWaitingList();
        justCompletedRotation.current = false;
      };
      
      handleRotationComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: recalculateAndSaveWaitingList omitted to avoid re-run loop
  }, [courtAssignments, nextAssignments, sessionLogger, stateLoadedFromDatabase]);


  // Log gameHistory every time it changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.gameHistory = gameHistory;
    }
  }, [gameHistory]);

  // Track total games completed for soft randomization
  React.useEffect(() => {
    if (gameHistory && Array.isArray(gameHistory)) {
      setTotalGamesCompleted(gameHistory.length);
    }
  }, [gameHistory]);

  // Track if we've set the initial court count from session setup
  const initialCourtCountSet = React.useRef(false);
  
  // Use session's court count if available (only on initial load and if user hasn't manually set it)
  useEffect(() => {
    if (sessionData?.number_of_courts && !courtsAssigned && !userSetNumberOfCourts && !initialCourtCountSet.current) {
      setNumberOfCourts(sessionData.number_of_courts);
      initialCourtCountSet.current = true;
    } else if (!sessionData?.number_of_courts && !courtsAssigned && !userSetNumberOfCourts && !initialCourtCountSet.current) {
      // Default to 2 courts if session doesn't specify (competitive sessions)
      setNumberOfCourts(2);
      initialCourtCountSet.current = true;
    }
  }, [sessionData, courtsAssigned, userSetNumberOfCourts, setNumberOfCourts]);

  // Note: Empty courts initialization with sequential numbering has been removed.
  // Courts are now loaded from database via loadCurrentState, which preserves actual court numbers
  // from session_courts table (allows gaps like 1, 2, 4). If no courts exist in DB, they will be
  // created by adjustCourtCountHandler which uses gap-filling logic to maintain proper numbering.

  // Initialize waiting list and paused list with existing players on page load.
  // Skip if DB restore already ran — the restored (possibly empty) waitlist is intentional.
  useEffect(() => {
    if (!stateLoadedFromDatabase || isLoadingState) {
      return;
    }
    if (restoredWaitlistRef.current) {
      return;
    }
    
    if (players.length > 0 && sessionPlayerStatus && waitingList.length === 0 && pausedList.length === 0) {
      const activePlayerIds = Object.keys(sessionPlayerStatus).filter(
        id => sessionPlayerStatus[id] === 'active'
      );
      const pausedPlayerIds = Object.keys(sessionPlayerStatus).filter(
        id => sessionPlayerStatus[id] === 'paused'
      );
      
      // Only add active players who aren't already on courts
      const playersToAdd = activePlayerIds.filter(id => 
        !courtAssignments.some(court => 
          court.team1.includes(id) || court.team2.includes(id)
        )
      );
      
      if (playersToAdd.length > 0) {
        setWaitingList(playersToAdd);
      }
      
      if (pausedPlayerIds.length > 0) {
        setPausedList(pausedPlayerIds);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setters stable; intentional deps for load-once
  }, [players, sessionPlayerStatus, waitingList.length, pausedList.length, courtAssignments, stateLoadedFromDatabase, isLoadingState]);

  // Note: Auto-generation of next-on-court assignments has been removed.
  // Users now manually control next-on-court via "Generate Game" button per court.
  // This matches real user behavior observed in demo sessions.
  
  // Note: Automatic court count adjustment with sequential numbering has been removed.
  // Court count adjustments are now handled by adjustCourtCountHandler which uses
  // database-driven gap-filling logic (fills gaps first, then adds at end).
  // The database is the source of truth for court numbers, not local state.

  // Fetch club strength from Supabase
  const fetchClubStrength = useCallback(async (clubId) => {
    setClubStrengthLoading(true);
    setClubStrengthError(null);
    try {
      const { data, error } = await supabase
        .from('clubs')
        .select('strength_rating')
        .eq('id', clubId)
        .single();
      if (error) throw error;
      setClubStrength(data?.strength_rating ?? null);
    } catch (err) {
      setClubStrengthError('Not available');
      if (clubStrengths && clubStrengths[clubId]) {
        setClubStrength(clubStrengths[clubId]);
        setClubStrengthError(null);
      }
    } finally {
      setClubStrengthLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- clubStrengths omitted to prevent unnecessary re-creation
  }, []);

  // Set the club when selectedClub changes
  useEffect(() => {
    if (selectedClub && selectedClub.id) {
      setClub(selectedClub.id);
      fetchClubStrength(selectedClub.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when selectedClub.id changes
  }, [selectedClub?.id]);

  const buildLocalLatePenaltySuggestion = (playerId) =>
    getSuggestedLatePenaltyFromLiveState({
      playerId,
      sessionPlayerStatus,
      playersById: playersByIdWithGames,
      sessionPlayerPenalties,
      matchHistory,
    });

  const createOfflineSessionGuest = useCallback(async (formData, sessionEntryStatus) => {
    const playerId = createOfflinePlayerId();
    const guest = buildLocalGuestPlayer(formData, { id: playerId });
    const status = sessionEntryStatus === 'active' ? 'active' : 'signed_up';
    setPlayers({ ...playersById, [playerId]: guest });
    setSessionPlayerStatus((prev) => ({ ...prev, [playerId]: status }));
    const snapshot = {
      ...liveStateRef.current,
      playersById: {
        ...(liveStateRef.current?.playersById || playersById || {}),
        [playerId]: guest,
      },
      sessionPlayerStatus: {
        ...(liveStateRef.current?.sessionPlayerStatus || sessionPlayerStatus || {}),
        [playerId]: status,
      },
    };
    liveStateRef.current = snapshot;
    await offlineQueue.enqueue({
      type: OFFLINE_COMMAND_TYPES.CREATE_GUEST,
      playerId,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName?.trim() || null,
      gender: formData.gender,
      mixedElo: Number(formData.mixedElo) || null,
      levelsElo: Number(formData.levelsElo) || null,
      createdBy: currentUser?.id || null,
      seasonId: season?.id || null,
      status,
    }, snapshot);
    return guest;
  }, [
    currentUser?.id,
    offlineQueue,
    playersById,
    season?.id,
    sessionPlayerStatus,
    setPlayers,
    setSessionPlayerStatus,
  ]);

  const persistLatePenalty = async (playerId, penalty) => {
    const nextPenalty = Number(penalty) || 0;
    setSessionPlayerPenalties((prev) => ({ ...prev, [playerId]: nextPenalty }));
    try {
      await setManualLatePenalty(sessionId, playerId, nextPenalty);
    } catch (error) {
      if (shouldRunLocalHostPath(offlineQueue, sessionData)) {
        await offlineQueue.enqueue(
          { type: OFFLINE_COMMAND_TYPES.LATE_PENALTY, playerId, penalty: nextPenalty },
          liveStateRef.current
        );
        return;
      }
      throw error;
    }
  };

  const resolveLatePenaltySuggestion = async (playerId) => {
    if (shouldRunLocalHostPath(offlineQueue, sessionData)) {
      return buildLocalLatePenaltySuggestion(playerId);
    }
    return getSuggestedLatePenalty(sessionId, playerId);
  };

  // Handler for toggling player status that also updates waiting list
  const handleTogglePlayerStatus = async (playerId, targetStatus = null, exitReason = null, options = {}) => {
    const { skipLatePenalty: skipLatePenaltyOption = false } = options;
    const skipLatePenalty = skipLatePenaltyOption || isPegBoard;
    const wouldTransitionToPaused =
      targetStatus === 'paused' ||
      (targetStatus == null && sessionPlayerStatus[playerId] === 'active');
    if (wouldTransitionToPaused && playersOnCourtsSet.has(playerId)) {
      return false;
    }
    if (targetStatus === 'remove') {
      const blockReason = getRemoveFromSessionBlockReason({
        playerId,
        player: playersById[playerId],
        isOnCourt: playersOnCourtsSet.has(playerId),
        isInUpcoming: playersInUpcomingSet.has(playerId),
        sessionPlayerScheduledGames,
        partnershipLookup,
        playersById,
      });
      if (blockReason) {
        addToast({
          message: blockReason,
          variant: 'error',
          duration: 5000,
        });
        return false;
      }
    }
    const result = await togglePlayerStatus(playerId, targetStatus, exitReason);
    if (!result.success) {
      console.error('Failed to toggle player status:', result.error);
      addToast({
        message: result.error || 'Failed to update player status.',
        variant: 'error',
        duration: 5000,
      });
      return false;
    }

    // Get the actual new status from the result
    const newStatus = result.newStatus;

    // Auto-cancel any pending scheduled game when a player leaves or pauses.
    // Their reservation would block the slot indefinitely otherwise.
    const playerWillBeUnavailable = targetStatus === 'remove' || newStatus === 'paused' || newStatus === 'inactive';
    if (playerWillBeUnavailable) {
      const scheduledGameId = sessionPlayerScheduledGames[playerId];
      if (scheduledGameId) {
        const playerName = playersById[playerId]
          ? `${playersById[playerId].first_name || ''} ${playersById[playerId].last_name || ''}`.trim()
          : 'Player';
        try {
          await removeScheduledGame(scheduledGameId);
          addToast({ message: `Scheduled game cancelled — ${playerName} is no longer available`, variant: 'info', duration: 4000 });
        } catch (err) {
          console.error('[handleTogglePlayerStatus] Failed to auto-cancel scheduled game:', err);
        }
      }
    }

    // Handle player removal from session
    if (targetStatus === 'remove' || newStatus === undefined) {
      setWaitingList(prev => prev.filter(id => id !== playerId));
      setPausedList(prev => prev.filter(id => id !== playerId));
      setNextAssignments(prev =>
        prev.length === 0
          ? prev
          : prev.map(match => ({
              ...match,
              team1: (match.team1 || []).filter(id => id !== playerId),
              team2: (match.team2 || []).filter(id => id !== playerId),
            }))
      );
      setPlayerWaitMetrics(prev => {
        if (!prev[playerId]) return prev;
        const copy = { ...prev };
        delete copy[playerId];
        return copy;
      });
      setSessionPlayerPenalties(prev => {
        if (prev[playerId] === undefined) return prev;
        const copy = { ...prev };
        delete copy[playerId];
        return copy;
      });

      if (targetStatus === 'remove' && sessionId && sessionData?.use_normalized_state !== false) {
        waitlistPlayerWriteLocksRef.current.add(playerId);
        try {
          const removeResult = await leaveWaitlistCmd(sessionId, playerId, tabInstanceId);
          if (!removeResult?.ok) {
            console.error('[handleTogglePlayerStatus] leaveWaitlistCmd failed:', removeResult);
          }
        } catch (err) {
          console.error('[handleTogglePlayerStatus] leaveWaitlistCmd error:', err);
        } finally {
          waitlistPlayerWriteLocksRef.current.delete(playerId);
        }
      }

      const onCourt = courtAssignments.find(court =>
        (court.team1 || []).includes(playerId) || (court.team2 || []).includes(playerId)
      );
      if (onCourt) {
        const teamKey = (onCourt.team1 || []).includes(playerId) ? 'team1' : 'team2';
        setCourtAssignments(prev => prev.map(court => {
          if (court.courtNumber !== onCourt.courtNumber) return court;
          const updated = {
            ...court,
            [teamKey]: (court[teamKey] || []).filter(id => id !== playerId),
          };
          const total = (updated.team1?.length || 0) + (updated.team2?.length || 0);
          if (total < playersPerMatchForSession) {
            clearCourtMatchTimer(updated);
          }
          updated.gameCreation = markGameCreationAltered(
            court.gameCreation,
            court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          return updated;
        }));
      }

      if (targetStatus === 'remove') {
        const partnerId = partnershipLookup[playerId];
        if (partnerId && sessionId) {
          try {
            const partnershipResult = await removeSessionPartnershipMaybeOffline({
              sessionId,
              player1Id: playerId,
              player2Id: partnerId,
              offlineQueue,
              session: sessionData,
              snapshot: liveStateRef.current,
            });
            if (partnershipResult.success) {
              handleUpdatePartnerships(
                partnerships.filter(
                  (p) => p.player1Id !== playerId && p.player2Id !== playerId
                )
              );
            } else {
              console.warn('[handleTogglePlayerStatus] Partnership cleanup failed:', partnershipResult.error);
            }
          } catch (partnershipErr) {
            console.warn('[handleTogglePlayerStatus] Partnership cleanup error:', partnershipErr);
          }
        }

        const playerName = playersById[playerId]
          ? `${playersById[playerId].first_name || ''} ${playersById[playerId].last_name || ''}`.trim()
          : 'Player';
        addToast({
          message: `${playerName || 'Player'} removed from session`,
          variant: 'success',
          duration: 4000,
        });
      }
      return true;
    }

    // Handle player list management based on status
    if (newStatus === 'active') {
      // Unpause: always remove from paused list so UI updates immediately
      setPausedList(prev => prev.filter(id => id !== playerId));
      // Unpaused players go to waiting list only (never back onto a court slot)
      const onCourt = courtAssignments.find(court =>
        (court.team1 || []).includes(playerId) || (court.team2 || []).includes(playerId)
      );
      if (onCourt) {
        const teamKey = (onCourt.team1 || []).includes(playerId) ? 'team1' : 'team2';
        setCourtAssignments(prev => prev.map(court => {
          if (court.courtNumber !== onCourt.courtNumber) return court;
          const updated = {
            ...court,
            [teamKey]: (court[teamKey] || []).filter(id => id !== playerId),
          };
          const total = (updated.team1?.length || 0) + (updated.team2?.length || 0);
          if (total < playersPerMatchForSession) {
            clearCourtMatchTimer(updated);
          }
          updated.gameCreation = markGameCreationAltered(
            court.gameCreation,
            court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          return updated;
        }));
      }
      setWaitingList(prev => (prev.includes(playerId) ? prev : [...prev, playerId]));

      // Persist waitlist in DB immediately so snapshots / realtime match local state.
      // Mark-as-playing only updates session_players.status until this runs; debounced
      // save can lag behind a soft full sync and drop the player from the UI.
      if (sessionId && sessionData?.use_normalized_state !== false) {
        waitlistPlayerWriteLocksRef.current.add(playerId);
        void joinWaitlistCmd(sessionId, playerId, tabInstanceId)
          .then((wlResult) => {
            if (!wlResult?.ok) {
              console.error('[handleTogglePlayerStatus] joinWaitlistCmd failed:', wlResult);
              addToast({
                message: wlResult?.message || "Couldn't add player to the queue.",
                variant: 'error',
                duration: 5000,
              });
            }
          })
          .finally(() => {
            waitlistPlayerWriteLocksRef.current.delete(playerId);
          });
      }

      // Late-joiner penalty after waitlist/court UI — avoids lag when adding as playing from modal
      if (!skipLatePenalty) {
        try {
          const suggestion = await resolveLatePenaltySuggestion(playerId);

          if (suggestion.shouldShowModal) {
            setPenaltyModalPlayer(playersById[playerId] || playersByIdWithGames[playerId]);
            setPenaltySuggestion(suggestion);
            setShowLatePenaltyModal(true);
          } else {
            await persistLatePenalty(playerId, 0);
          }
        } catch (error) {
          console.error('Error checking penalty suggestion:', error);
          try {
            await persistLatePenalty(playerId, 0);
          } catch (penaltyError) {
            console.warn('[handleTogglePlayerStatus] late penalty skipped:', penaltyError);
            setSessionPlayerPenalties(prev => ({
              ...prev,
              [playerId]: 0
            }));
          }
        }
      } else {
        try {
          await persistLatePenalty(playerId, 0);
        } catch (penaltyError) {
          console.warn('[handleTogglePlayerStatus] late penalty skipped:', penaltyError);
          setSessionPlayerPenalties(prev => ({
            ...prev,
            [playerId]: 0
          }));
        }
      }

      if (targetStatus === 'active' || newStatus === 'active') {
        setMatchmakingStarted(true);
      }
    } else if (newStatus === 'paused') {
      // Move to paused list
      setWaitingList(prev => prev.filter(id => id !== playerId));
      setPausedList(prev => {
        if (!prev.includes(playerId)) {
          return [...prev, playerId];
        }
        return prev;
      });
      // Peg Board pick RPC uses waitlist rows, not session_players.status.
      // Leave the waitlist so a paused picker does not keep the turn.
      if (isPegBoard && sessionId && sessionData?.use_normalized_state !== false) {
        waitlistPlayerWriteLocksRef.current.add(playerId);
        void leaveWaitlistCmd(sessionId, playerId, tabInstanceId)
          .then((wlResult) => {
            if (!wlResult?.ok) {
              console.error('[handleTogglePlayerStatus] leaveWaitlistCmd failed on pause:', wlResult);
            }
          })
          .finally(() => {
            waitlistPlayerWriteLocksRef.current.delete(playerId);
          });
      }
      // If player was on a court, remove them from court state so Add Player works without refresh
      const onCourt = courtAssignments.find(court =>
        (court.team1 || []).includes(playerId) || (court.team2 || []).includes(playerId)
      );
      if (onCourt) {
        const teamKey = (onCourt.team1 || []).includes(playerId) ? 'team1' : 'team2';
        setCourtAssignments(prev => prev.map(court => {
          if (court.courtNumber !== onCourt.courtNumber) return court;
          const updated = {
            ...court,
            [teamKey]: (court[teamKey] || []).filter(id => id !== playerId),
          };
          const total = (updated.team1?.length || 0) + (updated.team2?.length || 0);
          if (total < playersPerMatchForSession) {
            clearCourtMatchTimer(updated);
          }
          updated.gameCreation = markGameCreationAltered(
            court.gameCreation,
            court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          return updated;
        }));
      }
    } else {
      // Remove from both lists for inactive/finished/signed_up
      setWaitingList(prev => prev.filter(id => id !== playerId));
      setPausedList(prev => prev.filter(id => id !== playerId));
    }
    return true;
  };

  // Penalty modal handlers
  const handlePenaltyConfirm = async (penalty) => {
    try {
      await persistLatePenalty(penaltyModalPlayer.id, penalty);
    } catch (error) {
      console.error('Failed to set penalty:', error);
      addToast({
        message: error.message || 'Could not save the late penalty.',
        variant: 'error',
        duration: 5000,
      });
    } finally {
      setShowLatePenaltyModal(false);
      setPenaltyModalPlayer(null);
    }
  };

  const handlePenaltyCancel = () => {
    // User closed modal without setting penalty - default to 0
    handlePenaltyConfirm(0);
  };

  const persistPlayersToWaitlist = async (playerIds) => {
    if (!playerIds.length) return;
    if (sessionId && sessionData?.use_normalized_state !== false) {
      for (const playerId of playerIds) {
        waitlistPlayerWriteLocksRef.current.add(playerId);
        try {
          const wlResult = await joinWaitlistCmd(sessionId, playerId, tabInstanceId);
          if (!wlResult?.ok) {
            console.error('[persistPlayersToWaitlist] joinWaitlistCmd failed:', wlResult);
            addToast({
              message: wlResult?.message || "Couldn't add player to the queue.",
              variant: 'error',
              duration: 5000,
            });
          }
        } finally {
          waitlistPlayerWriteLocksRef.current.delete(playerId);
        }
      }
    }
  };

  /** Join waitlist then, on Peg Board, rewrite positions so unplayed returns stay at the front. */
  const persistReturnToWaitlist = async (playerIds, nextWaitlist) => {
    const ids = [...new Set((playerIds || []).filter(Boolean))];
    if (!ids.length) return;
    if (!sessionId || sessionData?.use_normalized_state === false) return;
    for (const playerId of ids) {
      const wlResult = await joinWaitlistCmd(sessionId, playerId, tabInstanceId);
      if (!wlResult?.ok) {
        console.error('[persistReturnToWaitlist] joinWaitlistCmd failed:', wlResult);
      }
    }
    if (!isPegBoard || !Array.isArray(nextWaitlist)) return;
    waitlistReorderInFlightRef.current = true;
    try {
      const reorderResult = await reorderWaitlistCmd(sessionId, nextWaitlist, tabInstanceId);
      if (!reorderResult?.ok) {
        console.error('[persistReturnToWaitlist] reorderWaitlistCmd failed:', reorderResult);
      }
    } finally {
      waitlistReorderInFlightRef.current = false;
    }
  };

  const handleStartMatchmakingForAll = async () => {
    setStartMatchmakingLoading(true);
    try {
      const result = await bulkToggleStatus('mark_arrived');
      if (!result.success) {
        addToast({
          message: result.error || 'Failed to start matchmaking.',
          variant: 'error',
          duration: 5000,
        });
        return;
      }

      const activatedPlayerIds = result.activatedPlayerIds || [];
      if (activatedPlayerIds.length === 0) {
        addToast({
          message: 'No players waiting to start matchmaking.',
          variant: 'info',
          duration: 4000,
        });
        return;
      }

      setWaitingList((prev) => {
        const next = [...prev];
        activatedPlayerIds.forEach((id) => {
          if (!next.includes(id)) next.push(id);
        });
        return next;
      });

      await persistPlayersToWaitlist(activatedPlayerIds);

      for (const playerId of activatedPlayerIds) {
        try {
          await persistLatePenalty(playerId, 0);
        } catch (penaltyError) {
          console.warn('[handleStartMatchmakingForAll] late penalty skipped:', penaltyError);
        }
      }

      setMatchmakingStarted(true);
      addToast({
        message: `${activatedPlayerIds.length} player${activatedPlayerIds.length === 1 ? '' : 's'} added to the queue.`,
        variant: 'success',
        duration: 4000,
      });
    } catch (error) {
      console.error('[handleStartMatchmakingForAll] Failed:', error);
      addToast({
        message: error.message || 'Failed to start matchmaking.',
        variant: 'error',
        duration: 5000,
      });
    } finally {
      setStartMatchmakingLoading(false);
    }
  };

  // Handler for bulk toggling player status
  const handleBulkToggleStatus = async (action) => {
    const result = await bulkToggleStatus(action);
    if (!result.success) {
      // Handle error - could show a toast notification here
      console.error('Failed to bulk toggle player status:', result.error);
      return;
    }

    // Update waiting list based on bulk action
    if (action === 'activate_all') {
      // Get the player IDs that were actually activated from the result
      const activatedPlayerIds = result.activatedPlayerIds || [];
      
      // Move activated players from paused list to waiting list
      const playersToMove = activatedPlayerIds.filter(id => 
        !courtAssignments.some(court => 
          court.team1.includes(id) || court.team2.includes(id)
        )
      );
      
      if (playersToMove.length > 0) {
        // Remove from paused list
        setPausedList(prev => prev.filter(id => !playersToMove.includes(id)));
        // Add to waiting list
        setWaitingList(prev => {
          const newList = [...prev];
          playersToMove.forEach(id => {
            if (!newList.includes(id)) {
              newList.push(id);
            }
          });
          return newList;
        });
      }
    } else if (action === 'deactivate_active') {
      // Get the player IDs that were actually deactivated from the result
      const deactivatedPlayerIds = result.deactivatedPlayerIds || [];
      
      // Remove all deactivated players from both waiting and paused lists
      setWaitingList(prev => prev.filter(id => !deactivatedPlayerIds.includes(id)));
      setPausedList(prev => prev.filter(id => !deactivatedPlayerIds.includes(id)));
    }
  };

  const {
    hostActionContext,
    openPlayerInfoModal: openHostPlayerInfoModal,
    renderHostActionModals,
  } = useCompetitivePlayerHostActions({
    sessionId,
    seasonId: season?.id,
    currentClubId,
    sessionPlayerStatus,
    sessionPlayerExitReasons,
    sessionPlayerPenalties,
    sessionPlayerScheduledGames: uiSessionPlayerScheduledGames,
    partnershipLookup: uiPartnershipLookup,
    playersById,
    players,
    playerWaitMetrics,
    playersOnActiveCourts: playersOnCourtsSet,
    playersInUpcomingCourts: playersInUpcomingSet,
    matchPreferencesEnabled,
    hideMatchPreferenceMenu: isPegBoard,
    hideLateJoinPenaltyMenu: isPegBoard,
    hideMensDoublesEligibilityMenu: isPegBoard,
    togglePlayerStatus: handleTogglePlayerStatus,
    onGuestUpdated: fetchPlayers,
    onPenaltyUpdated: async (playerId, penalty) => {
      await persistLatePenalty(playerId, penalty);
      try {
        await fetchPlayers();
      } catch (error) {
        if (!isLikelyNetworkError(error)) throw error;
      }
    },
    onMatchPreferenceSaved: (pid, newPref, newStrength, rowUpdatedAt) => {
      setPlayerWaitMetrics((prev) => ({
        ...prev,
        [pid]: {
          ...(prev[pid] || {}),
          matchPreference: newPref,
          preferenceStrength: newStrength,
          sessionPlayerRowUpdatedAt: rowUpdatedAt,
        },
      }));
    },
    onMatchPreferenceReload: () => {
      if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;
      void handleRemoteSessionSync({ ignoreSaveGuard: true });
    },
    offlineQueue,
    session: sessionData,
    getSnapshot: () => liveStateRef.current,
    onMensEligibilitySaved: (pid, eligible, shadowElo) => {
      setPlayerWaitMetrics((prev) => ({
        ...prev,
        [pid]: {
          ...(prev[pid] || {}),
          mensDoublesEligible: eligible,
          mensDoublesShadowElo: shadowElo,
        },
      }));
      void handleRemoteSessionSync({ ignoreSaveGuard: true });
    },
  });

  // Refactor: courtAssignments and waitingList are now arrays of IDs
  // All logic below must use IDs, not player objects

  /* eslint-disable no-unused-vars -- handlers/vars reserved for UI or future use */
  // Add a waiting player to the first available team on any court, or remove from court if player is null
  const handleAddPlayerToCourt = (playerId, force = false, newCourtAssignments = null) => {
    if (playerId && !force) {
      if (!playersById[playerId]) return; // Defensive: only assign valid player IDs
      // 1. Try to fill an open spot on a playing court (inProgress courts)
      let added = false;
      let updatedCourtAssignments = (courtAssignments || []).map(court => {
        if (!added && court.inProgress && court.team1.length < playersPerSideForSession) {
          added = true;
          const updatedCourt = {
            ...court,
            team1: [...court.team1, playerId]
          };
          updatedCourt.gameCreation = markGameCreationAltered(
            court.gameCreation,
            court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          return updatedCourt;
        } else if (!added && court.inProgress && court.team2.length < playersPerSideForSession) {
          added = true;
          const updatedCourt = {
            ...court,
            team2: [...court.team2, playerId]
          };
          updatedCourt.gameCreation = markGameCreationAltered(
            court.gameCreation,
            court.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          return updatedCourt;
        }
        return court;
      });
      // 2. If not added, try to fill an open spot in an upcoming match
      if (!added && nextAssignments && nextAssignments.length > 0) {
        let updatedNextAssignments = nextAssignments.map(match => {
          if (!added && match.team1.length < playersPerSideForSession) {
            added = true;
            const updatedMatch = {
              ...match,
              team1: [...match.team1, playerId]
            };
            updatedMatch.gameCreation = markGameCreationAltered(
              match.gameCreation,
              match.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
            );
            return updatedMatch;
          } else if (!added && match.team2.length < playersPerSideForSession) {
            added = true;
            const updatedMatch = {
              ...match,
              team2: [...match.team2, playerId]
            };
            updatedMatch.gameCreation = markGameCreationAltered(
              match.gameCreation,
              match.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
            );
            return updatedMatch;
          }
          return match;
        });
        if (added) setNextAssignments(updatedNextAssignments);
      }
      if (added) {
        setCourtAssignments(updatedCourtAssignments);
        setWaitingList((waitingList || []).filter(id => id !== playerId));
        
        // Reset wait metrics for player who got assigned to court
        setPlayerWaitMetrics(prevMetrics => {
          const newMetrics = { ...prevMetrics };
          if (newMetrics[playerId]) {
            newMetrics[playerId] = {
              ...newMetrics[playerId],
              courtsObserved: 0,
              lastCourtUpdate: Date.now(),
              consecutiveWaits: 0,
            };
          }
          return newMetrics;
        });
        
        // Fetch updated games played count after adding player to court
        fetchGamesPlayedCount();
      }
    } else if (playerId && force) {
      if (!playersById[playerId]) return;
      setWaitingList(prev => (prev || []).includes(playerId) ? prev : [...(prev || []), playerId]);
    } else if (!playerId && newCourtAssignments) {
      // Defensive: filter out any player IDs not in playersById
      const filteredAssignments = newCourtAssignments.map(court => ({
        ...court,
        team1: court.team1.filter(id => playersById[id]),
        team2: court.team2.filter(id => playersById[id])
      }));
      setCourtAssignments(filteredAssignments);
    }
  };

  // Add a player to a specific court and team position
  const handleAddPlayerToSpecificPosition = async (playerId, courtNumber, teamNumber) => {
    
    if (!playerId || !playersById[playerId]) {
      return;
    }

    const rosterStatus = sessionPlayerStatus[playerId];
    if (rosterStatus === 'signed_up' || rosterStatus === 'paused') {
      addToast({
        message: 'Mark the player as playing (add to queue) before assigning them to a court.',
        variant: 'warning',
        duration: 5000,
      });
      return;
    }

    const teamKey = teamNumber === 1 ? 'team1' : 'team2';
    const srcCourt = (courtAssignments || []).find(c => c.courtNumber === courtNumber);
    if (!srcCourt || srcCourt[teamKey].length >= playersPerSideForSession || srcCourt[teamKey].includes(playerId)) {
      return;
    }

    // Compute new court state
    const updatedTeam = [...srcCourt[teamKey], playerId];
    let updatedCourt = {
      ...srcCourt,
      [teamKey]: updatedTeam,
      gameCreation: markGameCreationAltered(
        srcCourt.gameCreation,
        srcCourt.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
      ),
    };
    if (
      updatedCourt.team1.length === playersPerSideForSession &&
      updatedCourt.team2.length === playersPerSideForSession
    ) {
      markCourtMatchStarted(updatedCourt);
      updatedCourt.originalTeam1 = [...updatedCourt.team1];
      updatedCourt.originalTeam2 = [...updatedCourt.team2];
    }
    const updatedCourtAssignments = (courtAssignments || []).map(c =>
      c.courtNumber === courtNumber ? updatedCourt : c
    );

    // Optimistic UI updates
    setWaitingList(prev => (prev || []).filter(id => id !== playerId));
    setPausedList(prev => prev.filter(id => id !== playerId));
    setNextAssignments(prev => prev.length === 0 ? prev : prev.map(match => ({
      ...match,
      team1: match.team1.filter(id => id !== playerId),
      team2: match.team2.filter(id => id !== playerId),
    })));
    setCourtAssignments(updatedCourtAssignments);

    // Normalized sessions: persist immediately via direct RPC (no debounce)
    if (sessionId && sessionData?.use_normalized_state !== false) {
      const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNumber];
      const expectedVersion = ackedVersion ?? srcCourt.version ?? -1;
      const lockKey = `active:${courtNumber}`;
      localCourtWriteLocksRef.current.add(lockKey);
      try {
        const result = await assignPlayersToCourtCmd(
          sessionId, courtNumber, 'active', expectedVersion,
          { inProgress: updatedCourt.inProgress || false, team1: updatedCourt.team1, team2: updatedCourt.team2, gameCreation: updatedCourt.gameCreation },
          tabInstanceId
        );
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.newVersion },
          };
          setCourtAssignments(prev => prev.map(c =>
            c.courtNumber === courtNumber ? { ...c, version: result.newVersion } : c
          ));
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.currentVersion },
          };
          addToast({ message: 'Court updated by another host — refreshing…', variant: 'warning', duration: 5000 });
        } else {
          addToast({ message: `Court ${courtNumber} couldn't be saved. Check your connection and try again.`, variant: 'error', duration: 7000 });
        }
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
      }
    }

    // Ensure player status is set to 'active' when manually added to court
    setPlayerSessionStatus(playerId, 'active').catch(err => {
      console.error(`Failed to activate player ${playerId} after manual add:`, err);
    });
  };

  // Remove a player from a court and add to waiting list
  const handleRemovePlayerFromCourt = async (playerId, team, courtNumber) => {
    const srcCourt = (courtAssignments || []).find(c => c.courtNumber === courtNumber);
    if (!srcCourt) return;
    if (isAssignmentRowLockedByScheduledGame(srcCourt, scheduledGames)) {
      openScheduledGameCancelFromRow(srcCourt, 'court_player');
      return;
    }

    const teamKey = team === 1 ? 'team1' : 'team2';
    let updatedCourt = {
      ...srcCourt,
      [teamKey]: srcCourt[teamKey].filter(id => id !== playerId),
    };
    const totalPlayers = updatedCourt.team1.length + updatedCourt.team2.length;
    const nowEmpty = updatedCourt.team1.length === 0 && updatedCourt.team2.length === 0;
    if (nowEmpty || (!srcCourt.inProgress && totalPlayers < playersPerMatchForSession)) {
      clearCourtMatchTimer(updatedCourt);
    }
    updatedCourt.gameCreation = markGameCreationAltered(
      srcCourt.gameCreation,
      srcCourt.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
    );

    // Optimistic UI updates — Peg Board returns unplayed players to the front of the queue
    const nextWaitlist = requeueUnplayedPlayers(waitingList || [], [playerId]);
    waitlistPlayerWriteLocksRef.current.add(playerId);
    setWaitingList(nextWaitlist);
    setCourtAssignments(prev => prev.map(c => c.courtNumber === courtNumber ? updatedCourt : c));
    fetchGamesPlayedCount();

    // Normalized sessions: persist immediately via direct RPC (no debounce)
    if (sessionId && sessionData?.use_normalized_state !== false) {
      const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNumber];
      const expectedVersion = ackedVersion ?? srcCourt.version ?? -1;
      const lockKey = `active:${courtNumber}`;
      localCourtWriteLocksRef.current.add(lockKey);
      try {
        const result = await assignPlayersToCourtCmd(
          sessionId, courtNumber, 'active', expectedVersion,
          { inProgress: updatedCourt.inProgress || false, team1: updatedCourt.team1, team2: updatedCourt.team2, gameCreation: updatedCourt.gameCreation },
          tabInstanceId
        );
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.newVersion },
          };
          setCourtAssignments(prev => prev.map(c =>
            c.courtNumber === courtNumber ? { ...c, version: result.newVersion } : c
          ));
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.currentVersion },
          };
          addToast({ message: 'Court updated by another host — refreshing…', variant: 'warning', duration: 5000 });
        } else {
          addToast({ message: `Court ${courtNumber} couldn't be saved. Check your connection and try again.`, variant: 'error', duration: 7000 });
        }
        await persistReturnToWaitlist([playerId], nextWaitlist);
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
        waitlistPlayerWriteLocksRef.current.delete(playerId);
      }
    } else {
      waitlistPlayerWriteLocksRef.current.delete(playerId);
    }
  };

  // Session statistics
  // sessionStats is now provided directly by the hook

  // Filter players to only those who are part of the selected club
  const clubPlayers = currentClubId
    ? (players || []).filter(player => typeof player.final_levels_elo === 'number')
    : (players || []);

  /**
   * Persist one active court after waitlist-driven assignment (click handlers).
   */
  const persistActiveCourtIfNormalized = useCallback(async (courtNumber, courtData, fallbackVersion) => {
    if (!sessionId || sessionData?.use_normalized_state === false) return;
    const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNumber];
    const expectedVersion = ackedVersion ?? fallbackVersion ?? -1;
    const lockKey = `active:${courtNumber}`;
    localCourtWriteLocksRef.current.add(lockKey);
    try {
      const result = await assignPlayersToCourtCmd(
        sessionId, courtNumber, 'active', expectedVersion,
        {
          inProgress: courtData.inProgress || false,
          team1: courtData.team1,
          team2: courtData.team2,
          gameCreation: courtData.gameCreation,
        },
        tabInstanceId
      );
      if (result.ok) {
        lastAckedCourtVersionsRef.current = {
          ...lastAckedCourtVersionsRef.current,
          active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.newVersion },
        };
        setCourtAssignments(prev => prev.map(c =>
          c.courtNumber === courtNumber ? { ...c, version: result.newVersion } : c
        ));
      } else if (result.code === 'COURT_CONFLICT') {
        lastAckedCourtVersionsRef.current = {
          ...lastAckedCourtVersionsRef.current,
          active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.currentVersion },
        };
      }
    } finally {
      localCourtWriteLocksRef.current.delete(lockKey);
    }
  }, [sessionId, sessionData, tabInstanceId]);

  /**
   * Fire-and-forget persistence for DnD court roster changes.
   * Called after setCourtAssignments so state is already optimistic.
   * Realtime will deliver the authoritative state on success or conflict.
   */
  const persistDndCourtChanges = useCallback((updatedCourts, changedCourtNumbers) => {
    if (!sessionId || sessionData?.use_normalized_state === false) return;
    for (const courtNumber of changedCourtNumbers) {
      const court = updatedCourts.find(c => c.courtNumber === courtNumber);
      if (!court) continue;
      const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNumber];
      const expectedVersion = ackedVersion ?? court.version ?? -1;
      const lockKey = `active:${courtNumber}`;
      if (localCourtWriteLocksRef.current.has(lockKey)) continue;
      localCourtWriteLocksRef.current.add(lockKey);
      void assignPlayersToCourtCmd(
        sessionId, courtNumber, 'active', expectedVersion,
        { inProgress: court.inProgress || false, team1: court.team1, team2: court.team2, gameCreation: court.gameCreation },
        tabInstanceId
      ).then(result => {
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.newVersion },
          };
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNumber]: result.currentVersion },
          };
        }
      }).finally(() => {
        localCourtWriteLocksRef.current.delete(lockKey);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs; sessionId/sessionData/tabInstanceId intentional
  }, [sessionId, sessionData, tabInstanceId]);

  /**
   * Fire-and-forget persistence for DnD next-up slot changes.
   */
  const persistDndNextUpChanges = useCallback((updatedSlots, changedSlotIndices) => {
    if (!sessionId || sessionData?.use_normalized_state === false) return;
    for (const slotIndex of changedSlotIndices) {
      const slot = updatedSlots.find((s, i) => (s.courtNumber ?? i) === slotIndex);
      if (!slot) continue;
      const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slotIndex];
      const expectedVersion = ackedVersion ?? slot.version ?? -1;
      const lockKey = `next_up:${slotIndex}`;
      if (localCourtWriteLocksRef.current.has(lockKey)) continue;
      localCourtWriteLocksRef.current.add(lockKey);
      void setNextUpSlotCmd(
        sessionId, slotIndex, expectedVersion,
        { inProgress: slot.inProgress || false, team1: slot.team1 || [], team2: slot.team2 || [], gameCreation: slot.gameCreation },
        tabInstanceId
      ).then(result => {
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotIndex]: result.newVersion },
          };
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotIndex]: result.currentVersion },
          };
        }
      }).finally(() => {
        localCourtWriteLocksRef.current.delete(lockKey);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs; sessionId/sessionData/tabInstanceId intentional
  }, [sessionId, sessionData, tabInstanceId]);

  /** Persist one next-up slot via RPC (normalized sessions). */
  const persistUpcomingSlotToDb = useCallback(async (slotIndex, slotData) => {
    if (!sessionId || sessionData?.use_normalized_state === false) {
      return { ok: true, skipped: true };
    }
    const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slotIndex];
    const expectedVersion = ackedVersion ?? slotData?.version ?? -1;
    const lockKey = `next_up:${slotIndex}`;
    localCourtWriteLocksRef.current.add(lockKey);
    try {
      const result = await setNextUpSlotCmd(
        sessionId,
        slotIndex,
        expectedVersion,
        {
          inProgress: slotData.inProgress || false,
          team1: slotData.team1 || [],
          team2: slotData.team2 || [],
          gameCreation: slotData.gameCreation,
        },
        tabInstanceId
      );
      if (result.ok) {
        lastAckedCourtVersionsRef.current = {
          ...lastAckedCourtVersionsRef.current,
          next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotIndex]: result.newVersion },
        };
      } else if (result.code === 'COURT_CONFLICT') {
        lastAckedCourtVersionsRef.current = {
          ...lastAckedCourtVersionsRef.current,
          next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotIndex]: result.currentVersion },
        };
      }
      return result;
    } finally {
      localCourtWriteLocksRef.current.delete(lockKey);
    }
  }, [sessionId, sessionData, tabInstanceId]);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!isSessionHost) return;
    if (!over || !active?.data?.current) return;

    // All roster mutations below should bypass the 300ms debounce and flush
    // immediately on pointer-up — shortens the collaborative race window.
    pendingImmediateFlushRef.current = true;

    const activeData = active.data.current;
    const playerId = activeData.playerId;
    if (playerId == null) return;

    const overId = String(over.id);
    const maxPerTeam = playersPerSideForSession;
    const ppm = playersPerMatchForSession;

    const cloneCourts = () =>
      (courtAssignments || []).map((c) => ({ ...c, team1: [...c.team1], team2: [...c.team2] }));
    const cloneUpcoming = () =>
      (nextAssignments || []).map((m) => ({ ...m, team1: [...m.team1], team2: [...m.team2] }));

    const src = resolveRosterDragSource(activeData, courtAssignments, nextAssignments);
    if (!src) return;
    const tgt = parseRosterDropTarget(overId);
    if (!tgt) return;

    if (
      isRosterEndpointLocked(src, courtAssignments, nextAssignments, scheduledGames) ||
      isRosterEndpointLocked(tgt, courtAssignments, nextAssignments, scheduledGames)
    ) {
      addToast({ message: SCHEDULED_GAME_ROSTER_LOCKED_MESSAGE, variant: 'warning', duration: 6000 });
      return;
    }

    if (src.kind === 'waitlist') {
      let wl = [...(waitingList || [])];
      const wi = wl.indexOf(playerId);
      if (wi === -1) return;

      // Hard-preference warning: player voluntarily holds out for a specific game type.
      // Warn the host before placing them onto a court.
      if (tgt.kind !== 'waitlist') {
        const metrics = playerWaitMetrics?.[playerId];
        if (
          metrics?.preferenceStrength === 'hard' &&
          metrics?.matchPreference &&
          metrics.matchPreference !== 'no_preference'
        ) {
          const playerName = playersById?.[playerId]?.name || 'This player';
          const prefLabel = metrics.matchPreference === 'prefer_mixed'
            ? 'Mixed Doubles'
            : 'same-gender Doubles (Levels)';
          const confirmed = window.confirm(
            `${playerName} has a hard preference for ${prefLabel}.\n\n` +
            `Assigning them manually will override this preference for this game. Continue?`
          );
          if (!confirmed) return;
        }
      }

      wl.splice(wi, 1);

      if (tgt.kind === 'waitlist') {
        wl.splice(wi, 0, playerId);
        setWaitingList(wl);
        return;
      }

      if (tgt.kind === 'upcoming') {
        const nu = cloneUpcoming();
        const res = placeWaitlistPlayerIntoUpcomingSlot(nu, tgt, playerId, maxPerTeam);
        if (!res.ok) {
          wl.splice(wi, 0, playerId);
          return;
        }
        if (res.displaced != null) wl = requeueUnplayedPlayers(wl, [res.displaced]);
        touchUpcomingMatchesForDnD(nu, [tgt.matchIndex], markGameCreationAltered);
        setNextAssignments(nu);
        setWaitingList(wl);
        fetchGamesPlayedCount();
        persistDndNextUpChanges(nu, [nu[tgt.matchIndex]?.courtNumber ?? tgt.matchIndex]);
        return;
      }

      if (tgt.kind === 'active') {
        const nc = cloneCourts();
        const courtIdx = nc.findIndex((c) => c.courtNumber === tgt.court);
        if (courtIdx === -1) {
          wl.splice(wi, 0, playerId);
          return;
        }
        const teamKey = tgt.team === 1 ? 'team1' : 'team2';
        const teamArr = nc[courtIdx][teamKey];
        if (tgt.slot < teamArr.length) {
          const displaced = teamArr[tgt.slot];
          teamArr[tgt.slot] = playerId;
          if (displaced != null) wl = requeueUnplayedPlayers(wl, [displaced]);
        } else {
          const insertAt = Math.min(tgt.slot, teamArr.length);
          if (teamArr.length >= maxPerTeam) {
            wl.splice(wi, 0, playerId);
            return;
          }
          teamArr.splice(insertAt, 0, playerId);
        }
        applyCourtRosterSideEffectsAfterDnD(nc, [tgt.court], ppm, markGameCreationAltered);
        setCourtAssignments(nc);
        setWaitingList(wl);
        fetchGamesPlayedCount();
        persistDndCourtChanges(nc, [tgt.court]);
        return;
      }

      if (tgt.kind === 'activeTeam') {
        const nc = cloneCourts();
        const courtIdx = nc.findIndex((c) => c.courtNumber === tgt.court);
        if (courtIdx === -1) {
          wl.splice(wi, 0, playerId);
          return;
        }
        const teamKey = tgt.team === 1 ? 'team1' : 'team2';
        if (nc[courtIdx][teamKey].length < maxPerTeam) {
          nc[courtIdx][teamKey].push(playerId);
          applyCourtRosterSideEffectsAfterDnD(nc, [tgt.court], ppm, markGameCreationAltered);
          setCourtAssignments(nc);
          setWaitingList(wl);
          fetchGamesPlayedCount();
          persistDndCourtChanges(nc, [tgt.court]);
        } else {
          wl.splice(wi, 0, playerId);
        }
        return;
      }

      wl.splice(wi, 0, playerId);
      return;
    }

    if (src.kind === 'active') {
      if (tgt.kind === 'waitlist') {
        handleRemovePlayerFromCourt(playerId, src.team, src.court);
        return;
      }
      if (tgt.kind === 'active') {
        if (src.court === tgt.court && src.team === tgt.team && src.slot === tgt.slot) return;
        const nc = cloneCourts();
        if (!applyCourtSlotMutation(nc, src, tgt, playerId, maxPerTeam)) return;
        applyCourtRosterSideEffectsAfterDnD(nc, [src.court, tgt.court], ppm, markGameCreationAltered);
        setCourtAssignments(nc);
        fetchGamesPlayedCount();
        const changedCourts = src.court === tgt.court ? [src.court] : [src.court, tgt.court];
        persistDndCourtChanges(nc, changedCourts);
        return;
      }
      if (tgt.kind === 'upcoming') {
        const nc = cloneCourts();
        const nu = cloneUpcoming();
        if (!applyActiveToUpcomingMutation(nc, nu, src, tgt, playerId, maxPerTeam)) return;
        applyCourtRosterSideEffectsAfterDnD(nc, [src.court], ppm, markGameCreationAltered);
        touchUpcomingMatchesForDnD(nu, [tgt.matchIndex], markGameCreationAltered);
        setCourtAssignments(nc);
        setNextAssignments(nu);
        fetchGamesPlayedCount();
        persistDndCourtChanges(nc, [src.court]);
        persistDndNextUpChanges(nu, [nu[tgt.matchIndex]?.courtNumber ?? tgt.matchIndex]);
        return;
      }
      if (tgt.kind === 'activeTeam') {
        const nc = cloneCourts();
        const fromCourtIdx = nc.findIndex((c) => c.courtNumber === src.court);
        if (fromCourtIdx === -1) return;
        const fromTeamKey = src.team === 1 ? 'team1' : 'team2';
        const fromArr = nc[fromCourtIdx][fromTeamKey];
        const fromIdx = fromArr.indexOf(playerId);
        if (fromIdx === -1) return;
        fromArr.splice(fromIdx, 1);
        const courtIdx = nc.findIndex((c) => c.courtNumber === tgt.court);
        if (courtIdx === -1) {
          fromArr.splice(fromIdx, 0, playerId);
          return;
        }
        const teamKey = tgt.team === 1 ? 'team1' : 'team2';
        if (nc[courtIdx][teamKey].length < maxPerTeam) {
          nc[courtIdx][teamKey].push(playerId);
          applyCourtRosterSideEffectsAfterDnD(nc, [src.court, tgt.court], ppm, markGameCreationAltered);
          setCourtAssignments(nc);
          fetchGamesPlayedCount();
          const changedCourts2 = src.court === tgt.court ? [src.court] : [src.court, tgt.court];
          persistDndCourtChanges(nc, changedCourts2);
        } else {
          fromArr.splice(fromIdx, 0, playerId);
        }
        return;
      }
      return;
    }

    if (src.kind === 'upcoming') {
      if (tgt.kind === 'waitlist') {
        const nu = cloneUpcoming();
        const m = nu[src.matchIndex];
        if (!m) return;
        const tk = src.team === 1 ? 'team1' : 'team2';
        if (m[tk][src.slot] !== playerId) return;
        const nextTeam = [...m[tk]];
        nextTeam.splice(src.slot, 1);
        nu[src.matchIndex] = {
          ...m,
          [tk]: nextTeam,
          gameCreation: markGameCreationAltered(
            m.gameCreation,
            m.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          ),
        };
        setNextAssignments(nu);
        setWaitingList((prev) => requeueUnplayedPlayers(prev, [playerId]));
        fetchGamesPlayedCount();
        persistDndNextUpChanges(nu, [nu[src.matchIndex]?.courtNumber ?? src.matchIndex]);
        return;
      }
      if (tgt.kind === 'active') {
        const nc = cloneCourts();
        const nu = cloneUpcoming();
        if (!applyUpcomingToActiveMutation(nc, nu, src, tgt, playerId, maxPerTeam)) return;
        applyCourtRosterSideEffectsAfterDnD(nc, [tgt.court], ppm, markGameCreationAltered);
        touchUpcomingMatchesForDnD(nu, [src.matchIndex], markGameCreationAltered);
        setCourtAssignments(nc);
        setNextAssignments(nu);
        fetchGamesPlayedCount();
        persistDndCourtChanges(nc, [tgt.court]);
        persistDndNextUpChanges(nu, [nu[src.matchIndex]?.courtNumber ?? src.matchIndex]);
        return;
      }
      if (tgt.kind === 'upcoming') {
        const nu = cloneUpcoming();
        if (!applyUpcomingSlotMutation(nu, src, tgt, playerId, maxPerTeam)) return;
        touchUpcomingMatchesForDnD(nu, [src.matchIndex, tgt.matchIndex], markGameCreationAltered);
        setNextAssignments(nu);
        fetchGamesPlayedCount();
        const changedSlots = src.matchIndex === tgt.matchIndex
          ? [nu[src.matchIndex]?.courtNumber ?? src.matchIndex]
          : [nu[src.matchIndex]?.courtNumber ?? src.matchIndex, nu[tgt.matchIndex]?.courtNumber ?? tgt.matchIndex];
        persistDndNextUpChanges(nu, changedSlots);
        return;
      }
      if (tgt.kind === 'activeTeam') {
        const nc = cloneCourts();
        const courtIdx = nc.findIndex((c) => c.courtNumber === tgt.court);
        const tk = tgt.team === 1 ? 'team1' : 'team2';
        if (courtIdx === -1 || nc[courtIdx][tk].length >= maxPerTeam) return;
        const nu = cloneUpcoming();
        const m = nu[src.matchIndex];
        const fk = src.team === 1 ? 'team1' : 'team2';
        const fArr = [...m[fk]];
        const idx = fArr.indexOf(playerId);
        if (idx === -1) return;
        fArr.splice(idx, 1);
        nu[src.matchIndex] = {
          ...m,
          [fk]: fArr,
          gameCreation: markGameCreationAltered(
            m.gameCreation,
            m.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          ),
        };
        nc[courtIdx][tk].push(playerId);
        applyCourtRosterSideEffectsAfterDnD(nc, [tgt.court], ppm, markGameCreationAltered);
        touchUpcomingMatchesForDnD(nu, [src.matchIndex], markGameCreationAltered);
        setCourtAssignments(nc);
        setNextAssignments(nu);
        fetchGamesPlayedCount();
        persistDndCourtChanges(nc, [tgt.court]);
        persistDndNextUpChanges(nu, [nu[src.matchIndex]?.courtNumber ?? src.matchIndex]);
        return;
      }
    }
  };

  /** Clear one upcoming match: all players return to waitlist (same idea as clearing an active court). */
  const handleClearUpcomingMatch = useCallback(
    async (matchIndex) => {
      const match = nextAssignments[matchIndex];
      if (!match) return;
      if (isAssignmentRowLockedByScheduledGame(match, scheduledGames)) {
        openScheduledGameCancelFromRow(match, 'upcoming_clear');
        return;
      }
      const playerIdsToReturn = [...(match.team1 || []), ...(match.team2 || [])].filter(Boolean);
      if (playerIdsToReturn.length === 0) return;
      const slotCourtNumber = match.courtNumber ?? matchIndex;
      const nextWaitlist = requeueUnplayedPlayers(waitingList, playerIdsToReturn);
      playerIdsToReturn.forEach((id) => waitlistPlayerWriteLocksRef.current.add(id));

      fetchGamesPlayedCount();
      setWaitingList(nextWaitlist);
      const clearedSlot = {
        ...match,
        team1: [],
        team2: [],
        inProgress: false,
        winner: null,
        score: null,
        gameCreation: markGameCreationAltered(
          match.gameCreation,
          match.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
        ),
      };
      setNextAssignments((prev) => {
        if (!prev?.length || matchIndex >= prev.length) return prev;
        const updated = [...prev];
        updated[matchIndex] = clearedSlot;
        return updated;
      });

      // Normalized sessions: persist immediately via direct RPC (no debounce)
      if (sessionId && sessionData?.use_normalized_state !== false) {
        const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slotCourtNumber];
        const expectedVersion = ackedVersion ?? match.version ?? -1;
        const lockKey = `next_up:${slotCourtNumber}`;
        localCourtWriteLocksRef.current.add(lockKey);
        try {
          const result = await setNextUpSlotCmd(
            sessionId, slotCourtNumber, expectedVersion,
            { inProgress: false, team1: [], team2: [], gameCreation: null },
            tabInstanceId
          );
          if (result.ok) {
            lastAckedCourtVersionsRef.current = {
              ...lastAckedCourtVersionsRef.current,
              next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.newVersion },
            };
          } else if (result.code === 'COURT_CONFLICT') {
            lastAckedCourtVersionsRef.current = {
              ...lastAckedCourtVersionsRef.current,
              next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.currentVersion },
            };
          } else {
            addToast({ message: "Couldn't save upcoming queue change. Try again.", variant: 'error', duration: 6000 });
          }
          await persistReturnToWaitlist(playerIdsToReturn, nextWaitlist);
        } finally {
          localCourtWriteLocksRef.current.delete(lockKey);
          playerIdsToReturn.forEach((id) => waitlistPlayerWriteLocksRef.current.delete(id));
        }
      } else {
        playerIdsToReturn.forEach((id) => waitlistPlayerWriteLocksRef.current.delete(id));
      }
    },
    [nextAssignments, waitingList, fetchGamesPlayedCount, sessionId, sessionData, addToast, scheduledGames, openScheduledGameCancelFromRow, requeueUnplayedPlayers]
  );

  // Handler for removing player from upcoming match
  const handleRemoveFromUpcoming = useCallback(async (playerId, team, matchIndex) => {
    const srcMatch = nextAssignments[matchIndex];
    if (!srcMatch) return;
    if (isAssignmentRowLockedByScheduledGame(srcMatch, scheduledGames)) {
      openScheduledGameCancelFromRow(srcMatch, 'court_player');
      return;
    }

    fetchGamesPlayedCount();

    const teamKey = team === 1 ? 'team1' : 'team2';
    const slotCourtNumber = srcMatch.courtNumber ?? matchIndex;

    const updatedMatch = {
      ...srcMatch,
      [teamKey]: srcMatch[teamKey].filter(id => id !== playerId),
      gameCreation: markGameCreationAltered(
        srcMatch.gameCreation,
        srcMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
      ),
    };

    const nextWaitlist = requeueUnplayedPlayers(waitingList, [playerId]);
    waitlistPlayerWriteLocksRef.current.add(playerId);

    setNextAssignments(prev => {
      if (prev.length === 0 || matchIndex >= prev.length) return prev;
      const updated = [...prev];
      updated[matchIndex] = updatedMatch;
      return updated;
    });
    setWaitingList(nextWaitlist);

    // Normalized sessions: persist immediately via direct RPC (no debounce)
    if (sessionId && sessionData?.use_normalized_state !== false) {
      const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slotCourtNumber];
      const expectedVersion = ackedVersion ?? srcMatch.version ?? -1;
      const lockKey = `next_up:${slotCourtNumber}`;
      localCourtWriteLocksRef.current.add(lockKey);
      try {
        const result = await setNextUpSlotCmd(
          sessionId, slotCourtNumber, expectedVersion,
          { inProgress: updatedMatch.inProgress || false, team1: updatedMatch.team1, team2: updatedMatch.team2, gameCreation: updatedMatch.gameCreation },
          tabInstanceId
        );
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.newVersion },
          };
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.currentVersion },
          };
        } else {
          addToast({ message: "Couldn't save upcoming queue change. Try again.", variant: 'error', duration: 6000 });
        }
        await persistReturnToWaitlist([playerId], nextWaitlist);
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
        waitlistPlayerWriteLocksRef.current.delete(playerId);
      }
    } else {
      waitlistPlayerWriteLocksRef.current.delete(playerId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- nextAssignments/sessionId/sessionData/tabInstanceId stable for this shape
  }, [fetchGamesPlayedCount, nextAssignments, waitingList, sessionId, sessionData, addToast, scheduledGames, openScheduledGameCancelFromRow, requeueUnplayedPlayers]);

  // Handler for adding player to specific upcoming match
  const handleAddPlayerToUpcoming = useCallback(async (playerId, teamNumber, matchIndex) => {
    if (numberOfUpcomingCourts <= 0 || matchIndex >= numberOfUpcomingCourts) return;
    const rosterStatus = sessionPlayerStatus[playerId];
    if (rosterStatus === 'signed_up' || rosterStatus === 'paused') {
      addToast({
        message: 'Mark the player as playing (add to queue) before assigning them to next on court.',
        variant: 'warning',
        duration: 5000,
      });
      return;
    }

    fetchGamesPlayedCount();

    // Compute new next-up state before calling state setters (for RPC payload)
    const normalizedNext = normalizeNextUpCourtNumbers([...(nextAssignments || [])]);
    while (normalizedNext.length <= matchIndex) {
      normalizedNext.push(emptyUpcomingMatchSlot(normalizedNext.length));
    }
    const srcMatch = normalizedNext[matchIndex];
    const slotCourtNumber = matchIndex; // courtNumber is the slot index after normalisation
    let updatedMatch = { ...srcMatch };
    if (teamNumber === 1 && updatedMatch.team1.length < playersPerSideForSession) {
      updatedMatch.team1 = [...updatedMatch.team1, playerId];
    } else if (teamNumber === 2 && updatedMatch.team2.length < playersPerSideForSession) {
      updatedMatch.team2 = [...updatedMatch.team2, playerId];
    }
    updatedMatch.gameCreation = markGameCreationAltered(
      updatedMatch.gameCreation,
      updatedMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
    );
    updatedMatch = { ...updatedMatch, courtNumber: slotCourtNumber };
    normalizedNext[matchIndex] = updatedMatch;

    setWaitingList(prev => prev.filter(id => id !== playerId));
    setPausedList(prev => prev.filter(id => id !== playerId));
    setNextAssignments(normalizedNext);

    setPlayerSessionStatus(playerId, 'active').catch((err) => {
      console.error(`Failed to activate player ${playerId} after adding to upcoming:`, err);
    });

    // Normalized sessions: persist immediately via direct RPC (no debounce)
    if (sessionId && sessionData?.use_normalized_state !== false) {
      const ackedVersion = lastAckedCourtVersionsRef.current.next_up?.[slotCourtNumber];
      const expectedVersion = ackedVersion ?? srcMatch?.version ?? -1;
      const lockKey = `next_up:${slotCourtNumber}`;
      localCourtWriteLocksRef.current.add(lockKey);
      try {
        const result = await setNextUpSlotCmd(
          sessionId, slotCourtNumber, expectedVersion,
          { inProgress: updatedMatch.inProgress || false, team1: updatedMatch.team1, team2: updatedMatch.team2, gameCreation: updatedMatch.gameCreation },
          tabInstanceId
        );
        if (result.ok) {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.newVersion },
          };
        } else if (result.code === 'COURT_CONFLICT') {
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            next_up: { ...lastAckedCourtVersionsRef.current.next_up, [slotCourtNumber]: result.currentVersion },
          };
        } else {
          addToast({ message: "Couldn't save upcoming queue change. Try again.", variant: 'error', duration: 6000 });
        }
      } finally {
        localCourtWriteLocksRef.current.delete(lockKey);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionId/sessionData/tabInstanceId stable; nextAssignments intentional
  }, [sessionPlayerStatus, addToast, setPlayerSessionStatus, setPausedList, nextAssignments, fetchGamesPlayedCount, sessionId, sessionData, playersPerSideForSession, numberOfUpcomingCourts]);

  // Handler for adding player to court or upcoming match
  const handleAddToUpcoming = useCallback(async (playerId) => {
    pendingImmediateFlushRef.current = true;
    setWaitingList(prev => prev.filter(id => id !== playerId));

    const courts = latestStateRef.current.courtAssignments || courtAssignments;
    const courtAssign = assignPlayerToFirstOpenActiveCourt(courts, playerId, playersPerSideForSession);
    if (courtAssign) {
      setCourtAssignments(courtAssign.updatedCourts);
      await persistActiveCourtIfNormalized(
        courtAssign.courtNumber,
        courtAssign.updatedCourt,
        courtAssign.updatedCourt.version
      );
      return;
    }

    // If no empty court spots, then try upcoming matches
    setNextAssignments(prev => {
      for (let i = 0; i < prev.length; i++) {
        const updatedMatch = { ...prev[i] };
        if (updatedMatch.team1.length < playersPerSideForSession) {
          updatedMatch.team1 = [...updatedMatch.team1, playerId];
          updatedMatch.gameCreation = markGameCreationAltered(
            updatedMatch.gameCreation,
            updatedMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          const updatedAssignments = [...prev];
          updatedAssignments[i] = updatedMatch;
          return updatedAssignments;
        } else if (updatedMatch.team2.length < playersPerSideForSession) {
          updatedMatch.team2 = [...updatedMatch.team2, playerId];
          updatedMatch.gameCreation = markGameCreationAltered(
            updatedMatch.gameCreation,
            updatedMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          const updatedAssignments = [...prev];
          updatedAssignments[i] = updatedMatch;
          return updatedAssignments;
        }
      }
      if (prev.length < numberOfUpcomingCourts) {
        const newMatch = {
          team1: [playerId],
          team2: [],
          courtNumber: prev.length,
          inProgress: false,
          winner: null,
          gameCreation: ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL)
        };
        return normalizeNextUpCourtNumbers([...prev, newMatch]);
      }
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setters stable; intentional minimal deps
  }, [numberOfUpcomingCourts, playersPerSideForSession, courtAssignments, persistActiveCourtIfNormalized]);

  // Handler for removing player from waiting list
  const handleRemoveFromWaiting = useCallback(async (playerId) => {
    setWaitingList(prev => prev.filter(id => id !== playerId));
    // Fire-and-forget the RPC directly (don't wait for the debounce)
    if (sessionId && isSessionHost) {
      leaveWaitlistCmd(sessionId, playerId, tabInstanceId).then(result => {
        if (!result.ok) console.error('[handleRemoveFromWaiting] RPC failed:', result);
      });
    }

    const courts = latestStateRef.current.courtAssignments || courtAssignments;
    const courtAssign = assignPlayerToFirstOpenActiveCourt(courts, playerId, playersPerSideForSession);
    if (courtAssign) {
      setCourtAssignments(courtAssign.updatedCourts);
      await persistActiveCourtIfNormalized(
        courtAssign.courtNumber,
        courtAssign.updatedCourt,
        courtAssign.updatedCourt.version
      );
      return;
    }

    // If no empty court spots, then try upcoming matches
    setNextAssignments(prev => {
      for (let i = 0; i < prev.length; i++) {
        const updatedMatch = { ...prev[i] };
        if (updatedMatch.team1.length < playersPerSideForSession) {
          updatedMatch.team1 = [...updatedMatch.team1, playerId];
          updatedMatch.gameCreation = markGameCreationAltered(
            updatedMatch.gameCreation,
            updatedMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          const updatedAssignments = [...prev];
          updatedAssignments[i] = updatedMatch;
          return updatedAssignments;
        } else if (updatedMatch.team2.length < playersPerSideForSession) {
          updatedMatch.team2 = [...updatedMatch.team2, playerId];
          updatedMatch.gameCreation = markGameCreationAltered(
            updatedMatch.gameCreation,
            updatedMatch.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL
          );
          const updatedAssignments = [...prev];
          updatedAssignments[i] = updatedMatch;
          return updatedAssignments;
        }
      }
      if (prev.length < numberOfUpcomingCourts) {
        const newMatch = {
          team1: [playerId],
          team2: [],
          courtNumber: prev.length,
          inProgress: false,
          winner: null,
          gameCreation: ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL)
        };
        return normalizeNextUpCourtNumbers([...prev, newMatch]);
      }
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setters stable; intentional minimal deps
  }, [sessionId, isSessionHost, sessionData, tabInstanceId, fetchGamesPlayedCount, numberOfUpcomingCourts, playersPerSideForSession, courtAssignments, persistActiveCourtIfNormalized]);

  // Helper function to check if valid matchmaking is possible with current restrictions
  /**
   * Validates if matchmaking can create a game by running the exact same matchmaking logic
   * This ensures validation always matches actual game creation behavior
   * Returns { possible: boolean, reason: string }
   */
  const canCreateValidMatch = useCallback(() => {
    // Replicate the exact same logic as handleFillUpcomingCourt
    // This ensures validation matches actual behavior
    const ppm = playersPerMatchForSession;

    if (!isDataReady) {
      return {
        possible: false,
        reason: 'Session data is still loading.',
      };
    }
    
    // Quick basic checks first (fail fast)
    if (waitingList.length < ppm) {
      return {
        possible: false,
        reason: `Not enough players in the queue. Need at least ${ppm} players.`,
      };
    }

    // Build active waitlist players (same logic as handleFillUpcomingCourt)
    const activeWaitlistPlayers = waitingList.filter(playerId => {
      const player = playersByIdWithGames[playerId];
      const isActive = sessionPlayerStatus[playerId] === 'active';
      if (!player || !isActive) {
        return false;
      }
      return true;
    });

    if (activeWaitlistPlayers.length < ppm) {
      return {
        possible: false,
        reason: `Not enough active players in the queue. Need at least ${ppm} active players.`,
      };
    }

    // Handle scheduled games (same logic as handleFillUpcomingCourt) — pending only
    const safeScheduledGames = Array.isArray(scheduledGames)
      ? scheduledGames.filter(g => g.status === 'pending' || !g.status)
      : [];
    const reservedScheduledPlayerIds = new Set();
    const validScheduledGamesForPromotion = [];
    if (safeScheduledGames.length > 0) {
      for (const game of safeScheduledGames) {
        const playerIds = [
          game.team1_player1_id,
          game.team1_player2_id,
          game.team2_player1_id,
          game.team2_player2_id
        ].filter(Boolean);

        const allAvailable = playerIds.every(id => {
          const player = playersByIdWithGames[id];
          if (!player) return false;
          if (sessionPlayerStatus[id] !== 'active') return false;
          // Any roster slot on a playing court counts — not only inProgress (finished /
          // post-score courts may still hold players until rotation).
          const isOnCourt = courtAssignments.some(court =>
            (court.team1 || []).includes(id) || (court.team2 || []).includes(id)
          );
          if (isOnCourt) return false;
          const isInUpcoming = nextAssignments.some(match =>
            match.team1.includes(id) || match.team2.includes(id)
          );
          if (isInUpcoming) return false;
          return true;
        });

        if (allAvailable) {
          validScheduledGamesForPromotion.push(game);
          playerIds.forEach(id => reservedScheduledPlayerIds.add(id));
        }
      }
    }

    // If we have a valid scheduled game whose players are all in the waitlist, we can fill the upcoming court by promoting it
    const canPromoteScheduledGame = validScheduledGamesForPromotion.some((game) => {
      const playerIds = [
        game.team1_player1_id,
        game.team1_player2_id,
        game.team2_player1_id,
        game.team2_player2_id
      ].filter(Boolean);
      const t1 = [game.team1_player1_id, game.team1_player2_id].filter(Boolean);
      const t2 = [game.team2_player1_id, game.team2_player2_id].filter(Boolean);
      if (isSinglesFormat(matchFormat)) {
        return (
          playerIds.length === 2 &&
          t1.length === 1 &&
          t2.length === 1 &&
          playerIds.every((id) => activeWaitlistPlayers.includes(id))
        );
      }
      return playerIds.length === 4 && playerIds.every((id) => activeWaitlistPlayers.includes(id));
    });
    if (canPromoteScheduledGame) {
      return { possible: true, reason: '' };
    }

    // Filter out scheduled players from matchmaking pool (same as handleFillUpcomingCourt)
    const nonScheduledPlayerIds = activeWaitlistPlayers.filter(playerId => {
      return !reservedScheduledPlayerIds.has(playerId) && !sessionPlayerScheduledGames[playerId];
    });

    if (nonScheduledPlayerIds.length < ppm) {
      return { possible: false, reason: 'Not enough available players after accounting for scheduled games.' };
    }

    let finalAvailablePlayerIds;
    if (isSinglesFormat(matchFormat)) {
      finalAvailablePlayerIds = nonScheduledPlayerIds;
    } else {
    // Check partnership eligibility (same as handleFillUpcomingCourt)
      const { eligibleIds: partnershipEligibleIds, lockedMap: partnershipLockMap } =
        evaluatePartnershipEligibility(nonScheduledPlayerIds);

      if (partnershipEligibleIds.length < ppm) {
      if (partnershipLockMap.size > 0) {
        const [blockedPlayerId, info] = partnershipLockMap.entries().next().value;
        const blockedPlayer = playersByIdWithGames[blockedPlayerId];
        const partnerPlayer = playersByIdWithGames[info.partnerId];
        const playerName = blockedPlayer?.name || 'Player';
        const partnerName = partnerPlayer?.name || 'their partner';
        const reason = info.reason || 'Partner unavailable';
        return { 
          possible: false, 
            reason: `${playerName} is waiting for partner ${partnerName}. ${reason}`,
        };
      }
      return { possible: false, reason: 'Not enough eligible partnership players to create a game.' };
    }

      finalAvailablePlayerIds = partnershipEligibleIds;
    }
    
    // Eligibility check: enough eligible players exist — avoid running the full matchmaking
    // algorithm here since it is called on every state change and is expensive at 28+ players.
    // The actual fill (handleFillUpcomingCourt) will surface errors if an edge case blocks it.
    return { possible: true, reason: '' };
  }, [
    isDataReady,
    waitingList,
    playersByIdWithGames,
    sessionPlayerStatus,
    gameTypeRestriction,
    evaluatePartnershipEligibility,
    courtAssignments,
    scheduledGames,
    sessionPlayerScheduledGames,
    nextAssignments,
    partnerships,
    matchFormat,
    playersPerMatchForSession,
  ]);

  async function applyFillUpcomingResult({
    matchIndex,
    match,
    bestMatch,
    newTeam1,
    newTeam2,
    originMeta,
    actuallyAddedPlayerIds,
    setNextAssignments: setNext,
    setWaitingList: setWaiting,
  }) {
    setNext(prev => {
      const updatedAssignments = [...prev];
      while (updatedAssignments.length < matchIndex) {
        updatedAssignments.push(emptyUpcomingMatchSlot(updatedAssignments.length));
      }
      if (updatedAssignments.length === matchIndex) {
        updatedAssignments.push({
          courtNumber: matchIndex,
            team1: newTeam1,
            team2: newTeam2,
            inProgress: false,
            winner: null,
          matchmakingRelaxed: bestMatch?.matchmakingRelaxed || false,
          matchmakingRelaxationDetails: bestMatch?.matchmakingRelaxationDetails,
          matchmakingDebugInfo: bestMatch?.matchmakingDebugInfo,
          gameCreation: { ...originMeta, isAltered: false },
        });
        return updatedAssignments;
      }
        const previousMatch = { ...updatedAssignments[matchIndex], courtNumber: matchIndex };
      const previousMeta = previousMatch.gameCreation;
      const hadExistingPlayers =
        (previousMatch.team1?.length || 0) + (previousMatch.team2?.length || 0) > 0;
      updatedAssignments[matchIndex] = {
          ...previousMatch,
        courtNumber: matchIndex,
        team1: newTeam1,
          team2: newTeam2,
        matchmakingRelaxed: bestMatch?.matchmakingRelaxed || false,
        matchmakingRelaxationDetails: bestMatch?.matchmakingRelaxationDetails,
        matchmakingDebugInfo: bestMatch?.matchmakingDebugInfo,
        gameCreation: { ...originMeta, isAltered: previousMeta?.isAltered || hadExistingPlayers || false },
      };
      return updatedAssignments;
    });
    setNumberOfUpcomingCourts((n) => {
      if (n <= 0) return n;
      return Math.max(n, Math.min(MAX_HOST_UPCOMING_QUEUE_SLOTS, matchIndex + 1));
    });
    setWaiting(prev => prev.filter(id => !actuallyAddedPlayerIds.includes(id)));
  }

  const handleFillUpcomingCourt = useCallback(async (matchIndex) => {
    if (numberOfUpcomingCourts <= 0) {
      const result = { success: false, message: 'Upcoming queue is turned off.' };
      addToast({ message: result.message, variant: 'warning', duration: 4000 });
      return result;
    }
    const pps = playersPerSideForSession;
    if (matchIndex > 0) {
      for (let i = 0; i < matchIndex; i++) {
        const prior = nextAssignments[i];
        const priorFull =
          (prior?.team1?.length ?? 0) === pps && (prior?.team2?.length ?? 0) === pps;
        if (!priorFull) {
          const result = {
            success: false,
            message: 'Complete earlier upcoming matches before filling this slot.',
          };
          addToast({ message: result.message, variant: 'warning', duration: 6000 });
          return result;
        }
      }
    }

    const match = matchIndex < nextAssignments.length
      ? nextAssignments[matchIndex]
      : { team1: [], team2: [] };
    const emptySpots = (pps - (match.team1?.length ?? 0)) + (pps - (match.team2?.length ?? 0));
    if (emptySpots === 0) {
      const result = { success: false, message: 'Court is already full' };
      addToast({ message: result.message, variant: 'warning', duration: 5000 });
      return result;
    }

    const startFill = () => planUpcomingSlotCmd({
      sessionId,
      slotIndex: matchIndex,
      tabInstanceId: tabInstanceId || null,
    });
    const expectedVersion = match.version ?? -1;
    if (shouldRunLocalHostPath(offlineQueue, sessionData)) {
      return fillCourtFromLocalEngine({ courtType: 'next_up', index: matchIndex, expectedVersion });
    }
    const efResult = await startFill();

    if (!efResult?.ok) {
      const msg = efResult?.message || 'Server matchmaking failed for upcoming court.';
      if (efResult?.code === 'SLOT_ALREADY_FILLED') {
        addToast({
          message: 'Upcoming slot is out of sync with the server — refreshing session…',
          variant: 'warning',
          duration: 6000,
        });
        void handleRemoteSessionSync({ ignoreSaveGuard: true, forceApplyRemoteNextUp: true, debounceMs: 0 });
      } else {
        addToast({ message: msg, variant: 'warning', duration: 6000 });
      }
      return { success: false, message: msg };
    }

    const fill = efResult.fills?.[0];
    if (!fill) {
      const msg = 'Matchmaking could not find a valid assignment for this slot.';
      addToast({ message: msg, variant: 'warning', duration: 6000 });
      return { success: false, message: msg };
    }

    const isScheduledFill = fill.type === 'scheduled';
    const scheduledGameIdFromFill = fill.selectedScheduledGame?.id ?? null;
    await applyFillUpcomingResult({
      matchIndex,
      match,
      bestMatch: {
        matchmakingRelaxed: fill.matchmakingRelaxed,
        matchmakingRelaxationDetails: fill.matchmakingRelaxationDetails,
        matchmakingDebugInfo: fill.matchmakingDebugInfo,
      },
      newTeam1: fill.team1,
      newTeam2: fill.team2,
      originMeta: isScheduledFill
        ? {
            origin: 'scheduled',
            algorithm: fill.gameCreationAlgorithm ?? 'two_stage_server',
            scheduledGameId: scheduledGameIdFromFill,
            isAltered: false,
          }
        : {
            origin: 'matchmaking',
            algorithm: fill.gameCreationAlgorithm ?? 'two_stage_server',
            isAltered: false,
          },
      actuallyAddedPlayerIds: [...(fill.team1 || []), ...(fill.team2 || [])],
      setNextAssignments,
      setWaitingList,
    });

    notifyPartnershipSplitIfNeeded(fill.team1, fill.team2);
    addToast({ message: 'Upcoming court filled successfully using server matchmaking', variant: 'success', duration: 4000 });
    return { success: true, message: 'Upcoming court filled via server matchmaking' };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- server-only path
  }, [nextAssignments, playersPerSideForSession, sessionId, tabInstanceId, addToast, notifyPartnershipSplitIfNeeded, handleRemoteSessionSync, offlineQueue, sessionData, fillCourtFromLocalEngine, numberOfUpcomingCourts]);

  const deriveWaitingListFromStatus = useCallback(() => {
    const activePlayerIds = Object.entries(sessionPlayerStatus || {})
      .filter(([, status]) => status === 'active')
      .map(([playerId]) => playerId)
      .filter(id => playersByIdWithGames[id]);

    const playersOnCourts = new Set(
      (courtAssignments || []).flatMap(court => [...(court.team1 || []), ...(court.team2 || [])])
    );

    const playersInNextOnCourt = new Set(
      (nextAssignments || []).flatMap(match => [...(match.team1 || []), ...(match.team2 || [])])
    );

    return activePlayerIds.filter(
      id => !playersOnCourts.has(id) && !playersInNextOnCourt.has(id)
    );
  }, [sessionPlayerStatus, courtAssignments, nextAssignments, playersByIdWithGames]);

  // Handler for promoting all upcoming matches to courts.
  // Recovery-only: score submission promotes via finish_session_match RPC; use this
  // when manual intervention is needed (e.g. edge-case recovery), not in the score flow.
  const handlePromoteUpcomingMatches = useCallback(async () => {
    const plan = computePromotionPlan(nextAssignments, courtAssignments, playersPerSideForSession);
    if (!plan.success) {
      addToast({
        message: plan.message || 'Could not promote upcoming match.',
        variant: 'warning',
        duration: 6000,
      });
      return plan;
    }
    const { courtsToUpdate } = plan;
    const promoteCount = courtsToUpdate.length;
    const sourceMatches = nextAssignments.slice(0, promoteCount);
    const remainingMatches = nextAssignments.slice(promoteCount);
    const promotedPlayerIds = courtsToUpdate.flatMap(u => [...u.team1, ...u.team2]);

    setCourtAssignments(prev => {
      const updatedCourts = [...prev];
      courtsToUpdate.forEach(update => {
        const idx = updatedCourts.findIndex(c => c.courtNumber === update.courtNumber);
        if (idx !== -1) {
          const promotedCourt = {
            ...updatedCourts[idx],
            team1: update.team1,
            team2: update.team2,
            inProgress: update.inProgress,
            gameCreation: ensureGameCreationMeta(update.gameCreation, update.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL),
          };
          if (update.inProgress) {
            restartCourtMatchTimer(promotedCourt);
          }
          updatedCourts[idx] = promotedCourt;
        }
      });
      const allPlayers = updatedCourts.flatMap(c => [...(c.team1 || []), ...(c.team2 || [])]);
      if (allPlayers.length !== [...new Set(allPlayers)].length) {
        alert('Validation failed: duplicate players in court assignments. Please report this bug.');
        return prev;
      }
      return updatedCourts;
    });

    setNextAssignments((prev) =>
      padUpcomingQueueToSlotCount(prev.slice(courtsToUpdate.length), numberOfUpcomingCourts, (idx) => ({
        courtNumber: idx,
        team1: [],
        team2: [],
        inProgress: false,
        winner: null,
        score: null,
        gameCreation: ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL),
      }))
    );
    
    setWaitingList(prev => {
      const updated = prev.filter(id => !promotedPlayerIds.includes(id));
      if (updated.length !== [...new Set(updated)].length) {
        alert('Validation failed: duplicate players in the queue. Please report this bug.');
        return prev;
      }
      return updated;
    });
    fetchGamesPlayedCount();

    const promoteRows = courtsToUpdate.map((u, i) => ({
      team1: u.team1 || [],
      team2: u.team2 || [],
      gameCreation: ensureGameCreationMeta(
        u.gameCreation || sourceMatches[i]?.gameCreation,
        u.gameCreation?.origin || sourceMatches[i]?.gameCreation?.origin || GAME_CREATION_ORIGIN.MANUAL,
      ),
    }));
    const promotedScheduledIds = collectScheduledGameIdsFromAssignmentRows(promoteRows);
    if (isSessionHost && promotedScheduledIds.length > 0) {
      void consumeScheduledGamesPlaced(promotedScheduledIds);
    }

    // Persist promotion to DB so server matchmaking (Create Game) sees empty upcoming slots.
    if (sessionId && sessionData?.use_normalized_state !== false) {
      try {
        for (let i = 0; i < promoteCount; i++) {
          const update = courtsToUpdate[i];
          const sourceMatch = sourceMatches[i];
          const gc = promoteRows[i].gameCreation;
          const courtNum = update.courtNumber;
          const activeCourt = courtAssignments.find(c => c.courtNumber === courtNum);
          const ackedVersion = lastAckedCourtVersionsRef.current.active?.[courtNum];
          const expectedVersion = ackedVersion ?? activeCourt?.version ?? -1;
          const lockKey = `active:${courtNum}`;
          localCourtWriteLocksRef.current.add(lockKey);
          let assignResult;
          try {
            assignResult = await assignPlayersToCourtCmd(
              sessionId,
              courtNum,
              'active',
              expectedVersion,
              {
                inProgress: true,
                team1: update.team1,
                team2: update.team2,
                gameCreation: gc,
              },
              tabInstanceId
            );
          } finally {
            localCourtWriteLocksRef.current.delete(lockKey);
          }
          if (!assignResult.ok) {
            addToast({
              message: assignResult.message || 'Could not save promoted match to the server.',
              variant: 'error',
              duration: 6000,
            });
            void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
            return { success: false, message: assignResult.message };
          }
          lastAckedCourtVersionsRef.current = {
            ...lastAckedCourtVersionsRef.current,
            active: { ...lastAckedCourtVersionsRef.current.active, [courtNum]: assignResult.newVersion },
          };
        }

        const emptySlotMeta = ensureGameCreationMeta(null, GAME_CREATION_ORIGIN.MANUAL);
        for (let i = 0; i < promoteCount; i++) {
          const clearResult = await persistUpcomingSlotToDb(i, {
            team1: [],
            team2: [],
            inProgress: false,
            gameCreation: emptySlotMeta,
            version: sourceMatches[i]?.version,
          });
          if (clearResult.ok === false && !clearResult.skipped) {
            addToast({
              message: clearResult.message || 'Promotion saved to court but upcoming slot could not be cleared.',
              variant: 'warning',
              duration: 6000,
            });
            void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
            return { success: false, message: clearResult.message };
          }
        }

        for (let j = 0; j < remainingMatches.length; j++) {
          const match = remainingMatches[j];
          const reindexResult = await persistUpcomingSlotToDb(j, {
            team1: match.team1 || [],
            team2: match.team2 || [],
            inProgress: match.inProgress || false,
            gameCreation: ensureGameCreationMeta(match.gameCreation, GAME_CREATION_ORIGIN.MANUAL),
            version: match.version,
          });
          if (reindexResult.ok === false && !reindexResult.skipped) {
            void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
            break;
          }
        }
      } catch (err) {
        console.error('[handlePromoteUpcomingMatches] persist failed:', err);
        addToast({
          message: 'Promotion shown locally but database sync failed. Refreshing…',
          variant: 'warning',
          duration: 6000,
        });
        void handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
      }
    }

    return { success: true, message: `Promoted ${courtsToUpdate.length} match${courtsToUpdate.length > 1 ? 'es' : ''} to courts` };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional minimal deps
  }, [nextAssignments, courtAssignments, waitingList, playersPerSideForSession, numberOfUpcomingCourts, addToast, scheduledGames, isSessionHost, consumeScheduledGamesPlaced, sessionId, sessionData, tabInstanceId, persistUpcomingSlotToDb, handleRemoteSessionSync]);

  const handleConfirmPegBoardGame = useCallback(async ({
    expectedPickerId,
    partnerId,
    opp1Id,
    opp2Id,
    algorithm,
  }) => {
    setPegBoardConfirming(true);
    try {
      const result = await selectPegBoardGameCmd({
        sessionId,
        expectedPickerId,
        partnerId,
        opp1Id,
        opp2Id,
        algorithm,
      });
      if (!result.ok) {
        addToast({
          message: result.message || 'Could not place this Peg Board game.',
          variant: 'error',
          duration: 6000,
        });
        return result;
      }
      await handleRemoteSessionSync({ ignoreSaveGuard: true, debounceMs: 0 });
      return result;
    } finally {
      setPegBoardConfirming(false);
    }
  }, [sessionId, addToast, handleRemoteSessionSync]);

  // Handler for adding player to waiting list
  const handleAddToWaiting = useCallback((playerId) => {
    // Optimistic update first so the UI responds instantly
    setWaitingList(prev => {
      if (prev.some(p => p === playerId)) return prev;
      return [...prev, playerId];
    });
    // Fire-and-forget the RPC directly (don't wait for the debounce)
    if (sessionId && isSessionHost) {
      joinWaitlistCmd(sessionId, playerId, tabInstanceId).then(result => {
        if (!result.ok) {
          console.error('[handleAddToWaiting] RPC failed:', result);
          addToast({ message: result.message || "Couldn't add player to queue.", variant: 'error', duration: 5000 });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setWaitingList/joinWaitlistCmd stable
  }, [sessionId, isSessionHost, addToast]);

  // Matchmaking for new upcoming slots (legacy JSONB sessions without persisted sessionId only)
  const runMatchmakingForNewUpcomingSlots = useCallback((availablePlayerIds, maxNewMatches, startIndex = 0) => {
    if (maxNewMatches <= 0 || availablePlayerIds.length < playersPerMatchForSession) return [];
    const gamesPlayedThisSession = {};
    activePlayers.forEach((p) => {
      const pw = playersByIdWithGames[p.id];
      gamesPlayedThisSession[p.id] = pw?.effectiveGamesPlayed ?? pw?.gamesPlayedThisSession ?? 0;
    });
    const sessionState = {
      gamesPlayedThisSession,
      recentTeams: new Set(),
      recentMatchups: new Set(),
      currentlyOnCourt: new Set(
        (courtAssignments || []).flatMap((c) => [...(c.team1 || []), ...(c.team2 || [])])
      ),
      playerInteractions,
      playerWaitMetrics: playerWaitMetrics || {},
      totalGamesCompleted,
      matchPreferencesEnabled,
      cooldownEnabled,
    };
    const catEnabled = categorisation.categorizationEnabledRef.current;
    const catPlayers = categorisation.playerCategoriesRef.current;
    const result = getBestCourtAssignments_TwoStage(
      availablePlayerIds,
      maxNewMatches,
      sessionState,
      playersByIdWithGames,
      false,
      partnerships || [],
      playerWaitMetrics || {},
      scheduledGames,
      gameTypeRestriction,
      !isSinglesFormat(matchFormat) && catEnabled ? catPlayers : null,
      matchFormat
    );
    if (!result.courts?.length) return [];
    return result.courts.map((court, i) => ({
      ...court,
      courtNumber: startIndex + i,
      team1: court.team1.filter((id) => playersById[id]),
      team2: court.team2.filter((id) => playersById[id]),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- categorisation refs intentionally minimal
  }, [activePlayers, courtAssignments, playersByIdWithGames, playerInteractions, playerWaitMetrics, totalGamesCompleted, partnerships, scheduledGames, gameTypeRestriction, playersById, playersPerMatchForSession, matchFormat, matchPreferencesEnabled, cooldownEnabled]);

  const handleAdjustUpcomingCourts = useCallback(
    async (increment) => {
    const newCount = numberOfUpcomingCourts + increment;
      if (newCount < 0 || newCount > MAX_HOST_UPCOMING_QUEUE_SLOTS) return;

      const applyUpcomingCountLocally = () => {
        const local = applyLocalUpcomingSlotCount({
          nextAssignments,
          waitingList,
          newCount,
          playersById,
          createEmptySlot: emptyUpcomingMatchSlot,
          requeueUnplayedPlayers,
        });
        setSessionData((prev) =>
          prev ? { ...prev, upcoming_queue_slot_count: newCount } : prev
        );
        setNumberOfUpcomingCourts(newCount);
        numberOfUpcomingCourtsRef.current = newCount;
        setNextAssignments(local.nextAssignments);
        setWaitingList(local.waitingList);
        return local;
      };

      const enqueueUpcomingCount = async (local) => {
        if (!offlineQueue?.enqueue) return;
        await offlineQueue.enqueue(
          {
            type: OFFLINE_COMMAND_TYPES.COURT_COUNT,
            target: 'upcoming',
            newCount,
          },
          {
            ...liveStateRef.current,
            nextAssignments: local.nextAssignments,
            waitingList: local.waitingList,
            session: {
              ...(liveStateRef.current?.session || {}),
              upcoming_queue_slot_count: newCount,
            },
          }
        );
      };

      if (shouldRunLocalHostPath(offlineQueue, sessionData)) {
        const local = applyUpcomingCountLocally();
        await enqueueUpcomingCount(local);
        return;
      }

      if (sessionId && sessionData?.use_normalized_state !== false) {
        try {
          await setSessionUpcomingQueueSlotCount(sessionId, newCount);
          setSessionData((prev) =>
            prev ? { ...prev, upcoming_queue_slot_count: newCount } : prev
          );
          setNumberOfUpcomingCourts(newCount);
          numberOfUpcomingCourtsRef.current = newCount;
          // Writer sync preserves local next_up — pad/trim here so slot count and cards stay in sync.
          if (increment < 0 && nextAssignments.length > newCount) {
            const excessMatches = nextAssignments.slice(newCount);
            const excessPlayers = excessMatches.flatMap((m) => [...m.team1, ...m.team2]);
            setNextAssignments((prev) => prev.slice(0, newCount));
            setWaitingList((prev) => requeueUnplayedPlayers(prev, excessPlayers.filter((id) => playersById[id])));
          } else if (newCount > nextAssignments.length) {
            setNextAssignments((prev) =>
              padUpcomingQueueToSlotCount(prev, newCount, emptyUpcomingMatchSlot)
            );
          }
          await handleRemoteSessionSync({ ignoreSaveGuard: true });
        } catch (err) {
          console.error('setSessionUpcomingQueueSlotCount failed:', err);
          addToast({
            message: err?.message || 'Could not update upcoming queue size.',
            variant: 'error',
            duration: 6000,
          });
        }
        return;
      }

    setNumberOfUpcomingCourts(newCount);
    if (increment < 0 && nextAssignments.length > newCount) {
      const excessMatches = nextAssignments.slice(newCount);
      const excessPlayers = excessMatches.flatMap(m => [...m.team1, ...m.team2]);
      setNextAssignments(prev => prev.slice(0, newCount));
      setWaitingList((prev) => requeueUnplayedPlayers(prev, excessPlayers.filter((id) => playersById[id])));
      return;
    }
    if (increment > 0 && newCount > nextAssignments.length) {
      const startIndex = nextAssignments.length;
      const slotsToFill = newCount - nextAssignments.length;

      if (sessionId) {
        setNumberOfUpcomingCourts(newCount);
        setNextAssignments((prev) =>
          padUpcomingQueueToSlotCount(prev, newCount, emptyUpcomingMatchSlot)
        );

        const slotIndexes = Array.from({ length: slotsToFill }, (_, i) => startIndex + i);
        if (isPegBoard) {
          await handleRemoteSessionSync({
            ignoreSaveGuard: true,
            forceApplyRemoteNextUp: true,
            debounceMs: 0,
          });
          return;
        }
        const efResult = await planCourtFillCmd({
          sessionId,
          targetKind: 'next_up',
          targetIndex: slotIndexes[0],
          targetIndexes: slotIndexes.length > 1 ? slotIndexes : null,
          tabInstanceId,
        });

        if (!efResult.ok) {
          addToast({
            message: efResult.message || 'Could not fill new upcoming slots.',
            variant: 'warning',
            duration: 6000,
          });
        } else {
          await handleRemoteSessionSync({
            ignoreSaveGuard: true,
            forceApplyRemoteNextUp: true,
            debounceMs: 0,
          });
        }
        return;
      }

      const currentUpcomingIds = new Set(nextAssignments.flatMap(m => [...m.team1, ...m.team2]));
      const availablePlayers = waitingList.filter(id => !currentUpcomingIds.has(id) && playersByIdWithGames[id]);
        const maxNewMatches = Math.min(
          slotsToFill,
          Math.floor(availablePlayers.length / playersPerMatchForSession)
        );
      const newMatches = runMatchmakingForNewUpcomingSlots(
        availablePlayers,
        maxNewMatches,
        startIndex,
      );
      if (newMatches.length > 0) {
              setNextAssignments(prev => normalizeNextUpCourtNumbers([...prev, ...newMatches]));
        const newMatchPlayerIds = new Set(newMatches.flatMap(m => [...m.team1, ...m.team2]));
              setWaitingList(prev => prev.filter(id => !newMatchPlayerIds.has(id)));
      }
    }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- categorisation refs and setters intentionally minimal
    [
      numberOfUpcomingCourts,
      nextAssignments,
      waitingList,
      runMatchmakingForNewUpcomingSlots,
      playersById,
      playersByIdWithGames,
      playersPerMatchForSession,
      sessionId,
      sessionData?.use_normalized_state,
      addToast,
      handleRemoteSessionSync,
      setSessionData,
      tabInstanceId,
      planCourtFillCmd,
      isPegBoard,
      requeueUnplayedPlayers,
      offlineQueue,
    ]
  );

  const upcomingQueuePlayerPool = useMemo(() => {
    const inUpcoming = new Set(
      (nextAssignments || []).flatMap((m) => [...(m.team1 || []), ...(m.team2 || [])])
    );
    return (waitingList || []).filter((id) => !inUpcoming.has(id) && playersByIdWithGames[id]);
  }, [nextAssignments, waitingList, playersByIdWithGames]);

  const canIncrementUpcomingQueue = isPegBoard
    ? numberOfUpcomingCourts < MAX_HOST_UPCOMING_QUEUE_SLOTS
    : numberOfUpcomingCourts < MAX_HOST_UPCOMING_QUEUE_SLOTS &&
      upcomingQueuePlayerPool.length >= playersPerMatchForSession;

  const upcomingQueueIncrementTitle = !canIncrementUpcomingQueue
    ? numberOfUpcomingCourts >= MAX_HOST_UPCOMING_QUEUE_SLOTS
      ? `At most ${MAX_HOST_UPCOMING_QUEUE_SLOTS} upcoming slots`
      : `Need at least ${playersPerMatchForSession} players in the queue (not already in upcoming) to add a slot (${upcomingQueuePlayerPool.length} available)`
    : 'Add another upcoming slot';

  const canDecrementUpcomingQueue = numberOfUpcomingCourts > 0;

  // Utility: Can rotate all courts only if all courts are 1v1 or 2v2
  const canRotateAllCourts = courtAssignments?.every(court => {
    const t1 = court.team1.length;
    const t2 = court.team2.length;
    return (t1 === t2) && (t1 === 1 || t1 === 2);
  }) || false;

  // Utility: Is there a slot available on any court or upcoming match?
  const hasCourtSlotAvailable =
    courtAssignments?.some(
      (court) =>
        court.team1.length < playersPerSideForSession ||
        court.team2.length < playersPerSideForSession
    ) ||
    nextAssignments?.some(
      (match) =>
        match.team1.length < playersPerSideForSession ||
        match.team2.length < playersPerSideForSession
    ) ||
    false;

  // Handle game type restriction change
  const handleGameTypeRestrictionChange = async (newRestriction) => {
    if (!sessionId) return;
    
    try {
      
      // Update local state immediately for responsive UI
      setGameTypeRestriction(newRestriction);
      
      // Update in database
      const { error } = await supabase
        .from('sessions')
        .update({ game_type_restriction: newRestriction })
        .eq('id', sessionId);
      
      if (error) {
        console.error('Error updating game type restriction:', error);
        // Revert local state on error
        setGameTypeRestriction(sessionData?.game_type_restriction || 'all_types');
        throw error;
      }
      
      // Also update sessionData to keep it in sync
      setSessionData(prev => ({
        ...prev,
        game_type_restriction: newRestriction
      }));
      
    } catch (err) {
      console.error('Failed to update game type restriction:', err);
    }
  };

  const handleMatchFormatChange = async (newFormat) => {
    if (!sessionId) return;
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ competitive_match_format: newFormat })
        .eq('id', sessionId);
      if (error) {
        console.error('Error updating match format:', error);
        throw error;
      }
      setSessionData((prev) => ({ ...prev, competitive_match_format: newFormat }));
      if (isSinglesFormat(newFormat)) {
        categorisation.setCategorizationEnabled(false);
      }
    } catch (err) {
      console.error('Failed to update match format:', err);
    }
  };

  const handlePointsTargetChange = async (value) => {
    if (!sessionId) return;
    const points_target = parseInt(value, 10);
    if (![15, 21].includes(points_target)) return;
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ points_target })
        .eq('id', sessionId);
      if (error) throw error;
      setSessionData((prev) => (prev ? { ...prev, points_target } : prev));
    } catch (err) {
      console.error('Failed to update points target:', err);
    }
  };

  const handleDeuceModeChange = async (value) => {
    if (!sessionId) return;
    if (!['standard', 'no_deuce'].includes(value)) return;
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ deuce_mode: value })
        .eq('id', sessionId);
      if (error) throw error;
      setSessionData((prev) => (prev ? { ...prev, deuce_mode: value } : prev));
    } catch (err) {
      console.error('Failed to update deuce mode:', err);
    }
  };

  const handlePegBoardSelectionWindowChange = async (value) => {
    if (!sessionId || !isPegBoard) return;
    const peg_board_selection_window = clampPegBoardSelectionWindow(value);
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ peg_board_selection_window })
        .eq('id', sessionId);
      if (error) throw error;
      setSessionData((prev) => (prev ? { ...prev, peg_board_selection_window } : prev));
    } catch (err) {
      console.error('Failed to update peg board selection window:', err);
    }
  };

  // Handle player states when ending session
  const handleEndSessionPlayerStates = async () => {
    try {
      
      // Use the bulk action to mark all players as finished while preserving exit reasons
      const result = await bulkToggleStatus('end_session');
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update player states');
      }
      
    } catch (err) {
      console.error('Error updating player states for session end:', err);
      throw err; // Re-throw to be caught by handleSessionEnd
    }
  };

  // End or cancel session handler
  const handleSessionEnd = async (status) => {
    if (!sessionId) return;
    if (offlineQueue.hasPending) {
      setEndSessionError('Need a more stable connection to save queued games before ending the session.');
      return;
    }
    setEndingSession(true);
    setEndSessionError(null);
    try {
      
      // Convert status to lowercase to match database expectations
      const normalizedStatus = status.toLowerCase();
      
      // First, mark all players as finished while preserving existing exit reasons
      await handleEndSessionPlayerStates();
      
      // Then update the session status
      await updateSessionStatus(sessionId, normalizedStatus);
      
      if (normalizedStatus === 'completed' || normalizedStatus === 'cancelled') {
        // Redirect to session list page
        navigate(`/club/${selectedClub?.id}/sessions/review`);
      }
    } catch (err) {
      console.error('Error ending session:', err);
      setEndSessionError(`Failed to update session: ${err.message || 'Unknown error'}`);
    } finally {
      setEndingSession(false);
    }
  };

  useEffect(() => {
    // Skip during initial load to prevent multiple triggers
    if (!stateLoadedFromDatabase || isLoadingState) {
      return;
    }
    
    // This effect runs after any update to players, courtAssignments, or waitingList
    // You can add side effects here (e.g., show a toast, analytics, etc.)
    // console.log('State updated:', {
    //   players,
    //   courtAssignments,
    //   waitingList
    // });
  }, [players, courtAssignments, waitingList, stateLoadedFromDatabase, isLoadingState]);

  // Update waiting list when player status changes
  useEffect(() => {
    // Skip during initial load to prevent multiple triggers
    if (!stateLoadedFromDatabase || isLoadingState) {
      return;
    }
    
    if (sessionLogger && Object.keys(sessionPlayerStatus).length > 0) {
      // Filter waiting list to only include active players
      const activeWaitingList = waitingList.filter(id => 
        playersById[id] && sessionPlayerStatus[id] === 'active'
      );
      
      // Debounced save handles DB persistence via latestStateRef
    }
  }, [sessionPlayerStatus, waitingList, sessionLogger, playersById, stateLoadedFromDatabase, isLoadingState]);

  // Separate active and paused players for display
  const filteredWaitingList = (waitingList || []).filter(id => playersById[id] && sessionPlayerStatus[id] === 'active');
  const filteredPausedList = (pausedList || []).filter(id => playersById[id] && sessionPlayerStatus[id] === 'paused');

  const inAnyUpcomingPlayerIds = useMemo(
    () =>
      new Set(
        (nextAssignments || []).flatMap((m) => [...(m.team1 || []), ...(m.team2 || [])])
      ),
    [nextAssignments]
  );

  const courtPickPlayingPlayers = useMemo(() => {
    const idSet = new Set([...filteredWaitingList, ...Array.from(inAnyUpcomingPlayerIds)]);
    return Array.from(idSet)
      .map((id) => playersByIdWithGames[id])
      .filter(Boolean);
  }, [filteredWaitingList, inAnyUpcomingPlayerIds, playersByIdWithGames]);

  const pegBoardQueue = useMemo(() => {
    if (!isPegBoard) return null;
    return {
      waitlistIds: filteredWaitingList,
      selectionWindow: clampPegBoardSelectionWindow(sessionData?.peg_board_selection_window),
    };
  }, [isPegBoard, filteredWaitingList, sessionData?.peg_board_selection_window]);

  const canIncrementPlayingCourts =
    numberOfCourts < getMaxCourts() &&
    courtPickPlayingPlayers.length >= playersPerMatchForSession &&
    !courtsLoading;

  const playingCourtsIncrementTitle = !canIncrementPlayingCourts
    ? numberOfCourts >= getMaxCourts()
      ? 'Maximum courts reached'
      : courtsLoading
        ? 'Updating courts…'
        : `Need at least ${playersPerMatchForSession} players in the queue or upcoming to add a court (${courtPickPlayingPlayers.length} available)`
    : 'Add a court';

  const canDecrementPlayingCourts = canReduceCourts && !courtsLoading;

  const playingCourtsDecrementTitle = !canDecrementPlayingCourts
    ? reduceCourtsReason || 'Cannot reduce courts'
    : 'Remove a court';

  const getUpcomingPickPlayingPlayers = useCallback(
    (match, matchIdx) => {
      const inThis = new Set([...(match.team1 || []), ...(match.team2 || [])]);
      const playingIds = new Set([
        ...filteredWaitingList.filter((id) => !inThis.has(id)),
        ...(nextAssignments || []).flatMap((m, j) =>
          j === matchIdx ? [] : [...(m.team1 || []), ...(m.team2 || [])]
        ),
      ]);
      return Array.from(playingIds)
        .map((id) => playersByIdWithGames[id])
        .filter(Boolean);
    },
    [filteredWaitingList, nextAssignments, playersByIdWithGames]
  );
  
  // Calculate games mode for wait priority indicators
  const calculateGamesMode = () => {
    const activePlayerIds = Object.keys(playersById).filter(id => 
      playersById[id] && sessionPlayerStatus[id] === 'active'
    );
    const gamesPlayed = activePlayerIds.map(id => {
      const player = playersById[id];
      return player?.effectiveGamesPlayed !== undefined ? player.effectiveGamesPlayed : (player?.gamesPlayedThisSession || 0);
    });
    if (gamesPlayed.length === 0) return 0;
    
    // Calculate mode (most frequent value)
    const frequency = {};
    gamesPlayed.forEach(games => {
      frequency[games] = (frequency[games] || 0) + 1;
    });
    return parseInt(Object.keys(frequency).reduce((a, b) => 
      frequency[a] > frequency[b] ? a : b
    ));
  };
  const gamesMode = calculateGamesMode();
  
  // Function to determine wait priority based on games played and court rotations (uses session court count for threshold)
  const getWaitPriority = (player) => {
    const effectiveGames = player.effectiveGamesPlayed !== undefined ? player.effectiveGamesPlayed : (player.gamesPlayedThisSession || 0);
    const gamesDeficit = gamesMode - effectiveGames;
    const courtsObserved = playerWaitMetrics[player.id]?.courtsObserved || 0;
    const waitThreshold = numberOfCourts > 0 ? numberOfCourts : 4;
    const isGreedy = gamesDeficit >= 1 || (gamesDeficit >= 0 && gamesDeficit < 1 && courtsObserved >= waitThreshold);
    if (isGreedy) return 'UP_SOON';
    return 'NORMAL';
  };
  
  // Function to get performance badge based on consecutive win streaks
  const getPerformanceBadge = (player) => {
    const streak = playerStreaks[player.id];
    if (!streak) return null;
    const consecutive = streak.currentWinStreak ?? 0;
    if (consecutive >= 5) return { icon: '💎', tooltip: 'Elite Form: 5+ consecutive wins' };
    if (consecutive >= 3) return { icon: '🔥', tooltip: 'Hot Streak: 3+ consecutive wins' };
    return null;
  };
  
  const filteredCourtAssignments = (courtAssignments || []).map(court => ({
    ...court,
    team1: (court.team1 || []).filter(id => {
      const hasPlayer = !!playersById[id];
      const isActive = sessionPlayerStatus[id] === 'active';
      if (!hasPlayer || !isActive) {
      }
      return hasPlayer && isActive;
    }),
    team2: (court.team2 || []).filter(id => {
      const hasPlayer = !!playersById[id];
      const isActive = sessionPlayerStatus[id] === 'active';
      if (!hasPlayer || !isActive) {
      }
      return hasPlayer && isActive;
    }),
  }));

  /* eslint-enable no-unused-vars */
  // Note: Dashboard debug info logging has been removed to reduce console noise.
  // Use the debugConfig.js system for targeted feature logging when needed.

  // Make ELO initialization function available in console
  useEffect(() => {
    if (selectedClub?.id && season?.id) {
      window.initializeEloRatings = () => initializeEloRatingsForClub(selectedClub.id, season.id);
    }
  }, [selectedClub?.id, season?.id]);

  // Track other active host tabs/windows for the same session (same-browser BroadcastChannel).
  useEffect(() => {
    if (!isSessionHost || !sessionId || typeof BroadcastChannel === 'undefined') return;
    const channelName = `session-host:${sessionId}`;
    const bc = new BroadcastChannel(channelName);
    bc.postMessage({ type: 'HOST_ANNOUNCE' });
    const handleMessage = (event) => {
      if (event.data?.type === 'HOST_ANNOUNCE') {
        bc.postMessage({ type: 'HOST_ACK' });
        setOtherHostCount((n) => n + 1);
      } else if (event.data?.type === 'HOST_ACK') {
        setOtherHostCount((n) => n + 1);
      } else if (event.data?.type === 'HOST_CLOSE') {
        setOtherHostCount((n) => Math.max(0, n - 1));
      }
    };
    bc.addEventListener('message', handleMessage);

    return () => {
      bc.postMessage({ type: 'HOST_CLOSE' });
      bc.removeEventListener('message', handleMessage);
      bc.close();
      setOtherHostCount(0);
    };
  }, [isSessionHost, sessionId]);

  // Helper to count courts in progress
  const courtsInProgress = courtAssignments.filter(c => c.inProgress).length; // eslint-disable-line no-unused-vars -- reserved for UI

  // Club Owner: Start New Season logic
  const handleStartNewSeason = async () => { // eslint-disable-line no-unused-vars -- reserved for club owner UI
    setSeasonOpLoading(true);
    setSeasonOpError(null);
    setSeasonOpSuccess(null);
    try {
      // 1. Mark current season as 'past'
      if (season) {
        await supabase
          .from('seasons')
          .update({ season_status: 'past', end_date: new Date().toISOString() })
          .eq('id', season.id);
      }
      // 2. Insert new season
      const { data: newSeason, error: newSeasonError } = await supabase
        .from('seasons')
        .insert([{
          club_id: selectedClub.id,
          season_number: (season?.season_number || 0) + 1,
          name: `Season ${(season?.season_number || 0) + 1}`,
          created_by: currentUser.id,
          season_status: 'current'
        }])
        .select()
        .single();
      if (newSeasonError) throw newSeasonError;
      // 3. Get all players in club
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id')
        .eq('club_id', selectedClub.id);
      if (playersError) throw playersError;
      // 4. Insert new player_club_elo_ratings rows for each player
      const baseElo = 1000;
      const newRatings = players.map(p => ({
        player_id: p.id,
        club_id: selectedClub.id,
        season_id: newSeason.id,
        mixed_elo_rating: baseElo,
        levels_elo_rating: baseElo,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      if (newRatings.length > 0) {
        const { error: ratingsError } = await supabase.from('player_club_elo_ratings').insert(newRatings);
        if (ratingsError) throw ratingsError;
      }
      // 5. Update context
      setSeason(newSeason);
      setSeasonOpSuccess('New season started!');
    } catch (err) {
      setSeasonOpError(err.message || 'Failed to start new season');
    } finally {
      setSeasonOpLoading(false);
    }
  };

  // Handler for creating a scheduled game (wraps hook + session player flags and fetch)
  const handleGameScheduled = useCallback(async (team1, team2) => {
    try {
      const newGame = await createScheduledGameFromHook(team1, team2);
      const scheduledPlayerIds = [...team1, ...team2].filter(Boolean);
      if (scheduledPlayerIds.length > 0) {
        setSessionPlayerScheduledGames(prev => {
          const updated = { ...prev };
          scheduledPlayerIds.forEach(playerId => {
            updated[playerId] = newGame.id;
          });
          return updated;
        });
      }
      setShowAddScheduledGameModal(false);
      if (!shouldHoldRemoteUpcoming(offlineQueue, sessionData)) {
        await fetchPlayers();
      }
    } catch (error) {
      console.error('Error scheduling game:', error);
      addToast({
        message: error.message || 'Failed to schedule game',
        variant: 'error',
        duration: 6000,
      });
      throw error;
    }
  }, [createScheduledGameFromHook, fetchPlayers, setSessionPlayerScheduledGames, addToast, offlineQueue, sessionData]);


  const performRemoveScheduledGame = useCallback(async (scheduledGameId, game) => {
    const rosterIds = game ? rosterPlayerIdsFromScheduledGame(game) : [];

    await removeScheduledGame(scheduledGameId, tabInstanceId);

    // Drop scheduled metadata from linked court/upcoming rows immediately so lock
    // badges disappear without waiting for remote sync (RPC may not clear metadata
    // for on_court rows until a full session_courts refresh).
    setCourtAssignments((prev) =>
      (prev || []).map((row) => clearScheduledGameFromCourtRow(row, scheduledGameId, rosterIds)),
    );
    setNextAssignments((prev) =>
      (prev || []).map((row) => clearScheduledGameFromCourtRow(row, scheduledGameId, rosterIds)),
    );

    if (rosterIds.length > 0) {
      setSessionPlayerScheduledGames((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const pid of rosterIds) {
          if (next[pid] != null && String(next[pid]) === String(scheduledGameId)) {
            delete next[pid];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }

    if (!shouldHoldRemoteUpcoming(offlineQueue, sessionData)) {
      await loadScheduledGames();
      await fetchPlayers(undefined, undefined, { silent: true });
      await handleRemoteSessionSync({ ignoreSaveGuard: true });
    }
  }, [
    removeScheduledGame,
    fetchPlayers,
    loadScheduledGames,
    handleRemoteSessionSync,
    setSessionPlayerScheduledGames,
    offlineQueue,
    sessionData,
    setCourtAssignments,
    setNextAssignments,
    tabInstanceId,
  ]);

  const handleRemoveScheduledGame = useCallback((scheduledGameId) => {
    const game = scheduledGames.find((g) => g.id === scheduledGameId);
    // Always confirm — accidental taps on the sidebar × should never silently drop a scheduled game.
    setScheduledGameCancelTarget({ ...(game || { id: scheduledGameId }), cancelTrigger: 'sidebar' });
  }, [scheduledGames]);

  const handleConfirmCancelScheduledGame = useCallback(async () => {
    if (!scheduledGameCancelTarget?.id) return;
    try {
      await performRemoveScheduledGame(
        scheduledGameCancelTarget.id,
        scheduledGameCancelTarget,
      );
    } catch (error) {
      console.error('Error cancelling scheduled game:', error);
      addToast({
        message: `Could not cancel scheduled game: ${error.message}`,
        variant: 'error',
        duration: 6000,
      });
      throw error;
    }
  }, [scheduledGameCancelTarget, performRemoveScheduledGame, addToast]);

  const scheduledGameCancelModalCopy = useMemo(
    () =>
      getScheduledGameCancelModalCopy(
        scheduledGameCancelTarget,
        scheduledGameCancelTarget?.cancelTrigger ?? 'sidebar',
      ),
    [scheduledGameCancelTarget],
  );

  const handleRemoveSessionPartnershipFromSidebar = useCallback(
    async (partnership) => {
      if (!sessionId || !partnership) return;
      try {
        const result = await removeSessionPartnershipMaybeOffline({
          sessionId,
          player1Id: partnership.player1Id,
          player2Id: partnership.player2Id,
          offlineQueue,
          session: sessionData,
          snapshot: liveStateRef.current,
        });
        if (!result.success) {
          alert(result.error || 'Could not remove partnership');
          return;
        }
        handleUpdatePartnerships(partnerships.filter((p) => p.id !== partnership.id));
      } catch (error) {
        console.error('[PARTNERSHIP] Sidebar remove failed:', error);
        alert(error.message || 'Could not remove partnership');
      }
    },
    [sessionId, partnerships, handleUpdatePartnerships, offlineQueue, sessionData]
  );

  const handleUpdateScheduledGameOrder = useCallback(async (dragIndex, dropIndex) => {
    try {
      await reorderScheduledGames(dragIndex, dropIndex);
    } catch (error) {
      alert('Error reordering scheduled games: ' + error.message);
    }
  }, [reorderScheduledGames]);

  const matchmakingValidation = useMemo(() => canCreateValidMatch(), [canCreateValidMatch]);

  const waitingListPartnershipLocks = useMemo(() => {
    if (isPegBoard) return {};
    const { lockedMap } = evaluatePartnershipEligibility(waitingList);
    const locks = {};
    lockedMap.forEach((info, playerId) => {
      locks[playerId] = info;
    });
    return locks;
  }, [isPegBoard, waitingList, evaluatePartnershipEligibility]);

// Keep the Paused queue group aligned with roster status (including remote self-pause).
useEffect(() => {
  if (!isDataReady) return;
  const pausedPlayerIds = pausedIdsFromSessionStatus(sessionPlayerStatus);
  setPausedList((prev) => (samePlayerIdList(prev, pausedPlayerIds) ? prev : pausedPlayerIds));
}, [isDataReady, sessionPlayerStatus, setPausedList]);

  useEffect(() => {
    courtsSidebarDefaultsAppliedRef.current = false;
    setCourtsSidebarExpanded({
      waitlist: false,
      partnerships: false,
      scheduled: false,
      gamePreference: false,
    });
    setMatchmakingStarted(false);
  }, [sessionId]);

  useEffect(() => {
    if (!stateLoadedFromDatabase || isLoadingState) return;
    const hasActiveWaitlistPlayers = (waitingList || []).some(
      (playerId) => sessionPlayerStatus[playerId] === 'active'
    );
    if (hasActiveWaitlistPlayers) {
      setMatchmakingStarted(true);
    }
  }, [stateLoadedFromDatabase, isLoadingState, waitingList, sessionPlayerStatus]);

  useEffect(() => {
    if (
      !sessionId ||
      !stateLoadedFromDatabase ||
      isLoadingState ||
      playersLoading ||
      courtsSidebarDefaultsAppliedRef.current
    ) {
      return;
    }

    const playersInUpcomingMatches = new Set();
    (nextAssignments || []).forEach((match) => {
      (match.team1 || []).forEach((playerId) => playersInUpcomingMatches.add(playerId));
      (match.team2 || []).forEach((playerId) => playersInUpcomingMatches.add(playerId));
    });

    const signedUpCount = (players || []).filter(
      (p) =>
        p?.id &&
        sessionPlayerStatus[p.id] === 'signed_up' &&
        playersByIdWithGames[p.id] &&
        !playersInUpcomingMatches.has(p.id)
    ).length;

    const waitlistCount = (waitingList || []).filter(
      (playerId) => !playersInUpcomingMatches.has(playerId)
    ).length;

    const pausedCount = (pausedList || []).filter(
      (playerId) =>
        !playersInUpcomingMatches.has(playerId) && sessionPlayerStatus[playerId] === 'paused'
    ).length;

    const doubles = !isSinglesFormat(matchFormat);
    const partnershipCount = (partnerships || []).length;
    const scheduledCount = (scheduledGames || []).length;

    setCourtsSidebarExpanded((prev) => {
      const waitlistOpen = waitlistCount > 0 || pausedCount > 0 || signedUpCount > 0;
      const canManageWaitlist = isSessionHost || isAdmin || showManageTab;

      if (!courtsSidebarDefaultsAppliedRef.current) {
        courtsSidebarDefaultsAppliedRef.current = true;
        return {
          waitlist: waitlistOpen,
          partnerships: doubles && partnershipCount > 0,
          scheduled: scheduledCount > 0,
          gamePreference: false,
        };
      }

      if (waitlistOpen && canManageWaitlist && !prev.waitlist) {
        return { ...prev, waitlist: true };
      }

      return prev;
    });
  }, [
    sessionId,
    stateLoadedFromDatabase,
    isLoadingState,
    playersLoading,
    players,
    sessionPlayerStatus,
    waitingList,
    pausedList,
    nextAssignments,
    partnerships,
    scheduledGames,
    matchFormat,
    playersByIdWithGames,
    isSessionHost,
    isAdmin,
    showManageTab,
  ]);

  const activeHostCount = otherHostCount + 1;

  const endSessionBlockedByUpload = Boolean(offlineQueue.hasPending);
  const endSessionBlockedTitle = endSessionBlockedByUpload
    ? 'You can’t end the session yet because games on this phone haven’t uploaded. Connect to wifi and wait until saving finishes.'
    : 'End session';

  const mobileEndSessionControl = useMemo(() => {
    if (!isSessionHost) return null;
    return (
      <div className="flex max-w-[min(100%,20rem)] flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <HostsActiveIndicator count={activeHostCount} />
          <Button
            type="button"
            onClick={() => setShowEndSessionModal(true)}
            variant="danger"
            size="small"
            className="min-h-[44px] touch-manipulation whitespace-nowrap shadow-brand-sm font-semibold"
            title={endSessionBlockedTitle}
            disabled={endSessionBlockedByUpload}
          >
            End session
          </Button>
        </div>
        {endSessionBlockedByUpload ? (
          <p className="text-right text-[11px] leading-snug text-warning-dark">
            Connect to wifi to upload games, then you can end the session.
          </p>
        ) : null}
      </div>
    );
  }, [isSessionHost, activeHostCount, endSessionBlockedByUpload, endSessionBlockedTitle]);

  useEffect(() => {
    if (!registerMobileHeaderAction) return undefined;
    registerMobileHeaderAction(mobileEndSessionControl);
    return () => registerMobileHeaderAction(null);
  }, [registerMobileHeaderAction, mobileEndSessionControl]);

  if (sessionLoading) {
    return <CompetitiveSessionLoadingSkeleton phase="session" />;
  }

  if (isLoadingState || !isDataReady) {
    return <CompetitiveSessionLoadingSkeleton phase="data" />;
  }

  if (showRemoteOfflineModeScreen) {
    return (
      <div className="relative font-sans bg-background min-h-screen px-4 py-10">
        <div className="mx-auto max-w-lg rounded-xl border border-border-medium bg-surface p-6 shadow-brand-sm">
          <StatusBadge variant="warning" size="small">Offline mode</StatusBadge>
          <h1 className="mt-3 text-xl font-semibold text-text-primary">Session in Offline mode</h1>
          <p className="mt-2 text-sm text-text-secondary">
            The host is running this session on the hall phone. Courts, the queue, and scores will not update here.
          </p>
          {isSessionHost ? (
            <p className="mt-2 text-sm text-text-secondary">
              This session is running on the hall phone. Use that device to score games.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  const rawSessionName = sessionData?.name || 'Session Dashboard';
  const clubSuffix = selectedClub?.name ? ` - ${selectedClub.name}` : '';
  const displaySessionName = clubSuffix && rawSessionName.endsWith(clubSuffix)
    ? rawSessionName.slice(0, -clubSuffix.length)
    : rawSessionName;

  const endSessionButton = isSessionHost ? (
    <div className="flex max-w-md flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <HostsActiveIndicator count={activeHostCount} />
        <Button
          onClick={() => setShowEndSessionModal(true)}
          variant="danger"
          disabled={endSessionBlockedByUpload}
          title={endSessionBlockedTitle}
        >
          End Session
        </Button>
      </div>
      {endSessionBlockedByUpload ? (
        <p className="text-right text-xs leading-snug text-warning-dark">
          You can’t end the session yet because games on this phone haven’t uploaded. Connect to wifi and wait until saving finishes.
        </p>
      ) : null}
    </div>
  ) : null;

  const sessionEndedBanner =
    liveSessionStatus === 'completed' || liveSessionStatus === 'cancelled' ? (
      <div className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 px-4 py-2 bg-warning-subtle border-b border-warning text-warning-foreground text-sm">
        <span>
          This session was marked as <strong>{liveSessionStatus}</strong> on another device or by a host action.
        </span>
        {(isSessionHost || isClubAdmin || isAdmin) && liveSessionStatus === 'completed' ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={handleReopenSession}
              disabled={reopeningSession}
            >
              {reopeningSession ? 'Reopening…' : 'Reopen session'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={() => navigate(`/club/${selectedClub?.id}/session-wrapup/${sessionId}`)}
            >
              Review & archive
            </Button>
          </div>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="relative font-sans bg-background min-h-screen">
      {sessionEndedBanner}
      {isHallPhone ? (
        <OfflineSaveBanner
          pendingCount={offlineQueue.pendingCount}
          isFlushing={offlineQueue.isFlushing}
          hallMode
        />
      ) : null}
      {/* Upcoming Match Banner - Sticky at top */}
      <UpcomingMatchBanner
        nextAssignments={nextAssignments}
        playersById={playersByIdWithGames}
        onFillUpcomingCourt={isPegBoard || numberOfUpcomingCourts <= 0 ? undefined : handleFillUpcomingCourt}
        matchmakingValidation={matchmakingValidation}
        playersPerSide={playersPerSideForSession}
        isHostView={isSessionHost || isClubAdmin || isAdmin}
        viewerPlayerId={currentUser?.id}
        courtAssignments={courtAssignments}
        waitingList={waitingList}
        showEloTooltip={!isPegBoard}
        pegBoardPickerId={isPegBoard ? (pegBoardQueue?.waitlistIds?.[0] ?? pegBoardPickerId) : null}
        onOpenPegBoardPick={pegBoardCanOpenPick ? handleOpenPegBoardPick : undefined}
      />
      <div className="max-w-full mx-auto py-2 md:py-3">
        <div>
          {/* Desktop/tablet: session name + tab bar */}
          <div className="hidden md:block">
            <SessionLayout
              sessionName={displaySessionName}
              sessionCreator={sessionCreator}
              endSessionButton={endSessionButton}
              footer={
                <SessionTabBar
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  isSessionHost={isSessionHost}
                  isAdmin={isAdmin}
                  showManageTab={showManageTab}
                  showYouTab={showYouTab}
                  showRankingTab={!isPegBoard}
                  showPlayerRankingTab={!isPegBoard}
                />
              }
            />
          </div>

          {/* Mobile: full-width tab bar (session name + back live in SessionDashboard) */}
          <div className="mb-2 md:hidden">
            <SessionTabBar
              activeTab={activeTab}
              onTabChange={setActiveTab}
              isSessionHost={isSessionHost}
              isAdmin={isAdmin}
              showManageTab={showManageTab}
              showYouTab={showYouTab}
              showRankingTab={!isPegBoard}
              showPlayerRankingTab={!isPegBoard}
              className="w-full min-w-0"
            />
          </div>
          
          {/* Club Owner/Admin Season Management section removed. Only in ClubAdmin now. */}
        </div>
      </div>
      {isSessionHost && activeTab === 'courts' && courtAssignments.length === 0 && nextAssignments.length === 0 && waitingList.length === 0 && (isSinglesFormat(matchFormat) || partnerships.length === 0) && (
        <SetupGuideBanner
          expanded={isSetupGuideExpanded}
          onToggle={() => setIsSetupGuideExpanded(!isSetupGuideExpanded)}
        >
                <p className="font-medium">To get started, you need to set up your players first:</p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>
                    Open <strong>Manage</strong> in the tab bar, then <strong>Players</strong>, to add players and set their status (or use the sidebar on the <strong>Courts</strong> tab).
                  </li>
                  {!isPegBoard && !isSinglesFormat(matchFormat) && (
                    <li>Configure partnerships if needed (for competitive doubles)</li>
                  )}
                  <li>Start assigning players to courts, or wait for the picker at the front of the queue.</li>
                </ol>
                <div className="mt-4 p-3 bg-info-light border border-info rounded-md">
                  <p className="text-sm font-medium text-info-dark">
                    {isSinglesFormat(matchFormat) ? (
                      <>Tip: <strong>Singles sessions use 1v1 matchmaking</strong> based on singles Elo and wait time.</>
                    ) : isPegBoard ? (
                      <>Tip: <strong>Peg Board</strong> — the player at the front of the queue picks the next doubles game. Scores do not affect Elo.</>
                    ) : (
                      <>Tip: <strong>Competitive sessions use auto-matchmaking</strong> — set up partnerships to practise together!</>
                    )}
                  </p>
              </div>
        </SetupGuideBanner>
      )}

      {/* Main Content Grid */}
      <div className="relative">
          {playersLoading && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80">
              <LoadingState message="Updating players…" size="large" className="py-0" />
            </div>
          )}
          {activeTab === 'courts' ? (
            <CourtsTab>
              {isPegBoard ? (
                <PegBoardPickModal
                  isOpen={pegBoardPickModalOpen}
                  onClose={() => setPegBoardPickModalOpen(false)}
                  waitingList={filteredWaitingList}
                  selectionWindow={sessionData?.peg_board_selection_window}
                  playersById={playersByIdWithGames}
                  occupiedIds={pegBoardOccupiedIds}
                  occupiedPlayers={pegBoardOccupiedPlayers}
                  currentUserId={currentUser?.id}
                  isSessionHost={isSessionHost || isClubAdmin}
                  onConfirm={handleConfirmPegBoardGame}
                  confirming={pegBoardConfirming}
                  matchHistory={matchHistory}
                />
              ) : null}
              {/* Court Assignments Section */}
              <div className="mt-2">
                
                {(isClubAdmin || isAdmin) && !isSessionHost ? (
                  <div className="mb-5 hidden rounded-xl border border-border-medium bg-surface p-3 shadow-brand-sm md:block sm:p-4">
                    <SectionHeader title="Court layout" variant="default" size="sm" className="!mb-3" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                        Courts per row
                      </span>
                      {courtLayoutSegmentButtons}
                    </div>
                  </div>
                ) : null}

                {/* Courts tab stack: playing courts, next on court, then Queue. */}
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleRosterDragStart}
                  onDragCancel={clearWaitlistDragOverlay}
                  onDragEnd={(e) => {
                    clearWaitlistDragOverlay();
                    onDragEnd(e);
                  }}
                >
                <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
                  {waitlistDragOverlayPlayerId && playersByIdWithGames[waitlistDragOverlayPlayerId] ? (
                    <RosterDragOverlayPreview
                      player={playersByIdWithGames[waitlistDragOverlayPlayerId]}
                      useSinglesElo={playersPerSideForSession === 1}
                    />
                  ) : null}
                </DragOverlay>
                <div className={SESSION_SECTION_STACK}>
                  {showOwnPresenceCard ? (
                    <SessionPresenceCard
                      rosterStatus={viewerSessionStatus}
                      pauseBlockReason={ownPresencePauseBlockReason}
                      lateJoinerPenalty={sessionPlayerPenalties[currentUser.id] || 0}
                      onAction={handleOwnSessionPresence}
                      loading={ownPresenceLoading}
                    />
                  ) : null}
                  <div id="session-courts" className="min-w-0 scroll-mt-28">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <SectionHeader title="Playing courts" variant="primary" className="!mb-0" />
                          {isHallPhone ? (
                            <StatusBadge variant="warning" size="small">Offline mode</StatusBadge>
                          ) : null}
                        </div>
                        {isSessionHost ? (
                          <p className="mt-0.5 text-xs text-text-muted">
                            Use the arrows on a court to match the hall layout.
                          </p>
                        ) : null}
                      </div>
                      {isSessionHost && (
                        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
                          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                            <span>Courts:</span>
                            <span className="w-4 text-center font-semibold text-text-primary">{numberOfCourts}</span>
                            <button
                              type="button"
                              onClick={() => handleAdjustCourtCount(-1)}
                              disabled={!canDecrementPlayingCourts}
                              title={playingCourtsDecrementTitle}
                              className={`${SESSION_COUNTER_BTN} ${canDecrementPlayingCourts ? 'hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10' : 'cursor-not-allowed opacity-55'}`}
                            >−</button>
                            <button
                              type="button"
                              onClick={() => handleAdjustCourtCount(1)}
                              disabled={!canIncrementPlayingCourts}
                              title={playingCourtsIncrementTitle}
                              className={`${SESSION_COUNTER_BTN} ${canIncrementPlayingCourts ? 'hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10' : 'cursor-not-allowed opacity-55'}`}
                            >+</button>
                          </div>
                          <div
                            className="hidden items-center gap-2 md:flex"
                            title="Your display only — does not affect other hosts or players"
                          >
                            <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                              Courts per row
                            </span>
                            {courtLayoutSegmentButtons}
                          </div>
                        </div>
                      )}
                    </div>
                <CourtsList
                  key={`courts-regular-${sessionId}`}
                  courtAssignments={filteredCourtAssignments}
                  waitingList={filteredWaitingList}
                  numberOfCourts={numberOfCourts}
                  numberOfWaitingCourts={numberOfWaitingCourts}
                  adjustCourtCount={handleAdjustCourtCount}
                  adjustWaitingCourtCount={adjustWaitingCourtCount}
                  updateMatchResult={handleUpdateMatchResultWithGamesCount}
                  rotatePlayersAfterMatch={handleRotationWithWaitingListUpdate}
                  removeCourt={isSessionHost ? handleRemoveCourt : undefined}
                  generateWaitingCourts={generateWaitingCourts}
                  onAddPlayerToCourt={isSessionHost ? handleAddPlayerToSpecificPosition : undefined}
                  onRemovePlayerFromCourt={isSessionHost ? handleRemovePlayerFromCourt : undefined}
                  onFillEmptyCourt={isSessionHost && !isPegBoard ? handleFillEmptyCourt : null}
                  onPromoteUpcomingMatches={isSessionHost ? handlePromoteUpcomingMatches : null}
                  nextAssignments={nextAssignments}
                  currentClubId={currentClubId}
                  getMaxCourts={getMaxCourts}
                  canRotateAllCourts={canRotateAllCourts}
                  playersById={playersByIdWithGames}
                  sessionPlayerStatus={sessionPlayerStatus}
                  onShowManageCourts={isSessionHost ? () => setShowManageCourts(true) : undefined}
                  playerStreaks={playerStreaks}
                  courtLayout={courtLayout}
                  matchmakingValidation={matchmakingValidation}
                      canEnterMatchScores={isSessionHost || isAdmin || isClubAdmin}
                      showPostMatchRotateButton={showPostMatchRotateButton}
                      courtPickPlayingPlayers={courtPickPlayingPlayers}
                      courtPickWaitingManagementPlayers={[]}
                      playersPerSide={playersPerSideForSession}
                      gridDensity={COURT_GRID_DENSITY}
                      freshRotationHighlightCourtNumber={freshRotationHighlightCourtNumber}
                      playerWaitMetrics={playerWaitMetrics}
                      isHostView={isSessionHost}
                      onMoveCourt={isSessionHost ? handleMoveCourt : undefined}
                      courtReorderBusy={courtReorderInFlight}
                      courtCardLayout={isSessionHost || isAdmin || isClubAdmin ? 'full' : 'mobilePlayer'}
                      viewerPlayerId={currentUser?.id}
                      partnerships={uiPartnerships}
                      scoringConfig={scoringConfig}
                      scheduledGames={uiScheduledGames}
                      showEloTooltip={!isPegBoard}
                      pegBoardQueue={pegBoardQueue}
                      pegBoardPickTarget={pegBoardPickTarget}
                      onOpenPegBoardPick={pegBoardCanOpenPick ? handleOpenPegBoardPick : undefined}
                />
              </div>
              
                  {/* Next on court — side by side grid */}
                  <div id="session-next-on-court" className="scroll-mt-28 overflow-visible">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      <SectionHeader title="Next on court" variant="warning" className="!mb-0" />
                      {isSessionHost && (
                        <div className="flex shrink-0 items-center gap-1.5 text-xs text-text-secondary">
                          <span>Queue slots:</span>
                          <span className="w-4 text-center font-semibold text-text-primary">{numberOfUpcomingCourts}</span>
                          <button
                            type="button"
                            onClick={() => handleAdjustUpcomingCourts(-1)}
                            disabled={!canDecrementUpcomingQueue}
                            title={canDecrementUpcomingQueue ? 'Remove an upcoming slot (players return to the queue)' : 'No upcoming slots'}
                            className={`${SESSION_COUNTER_BTN} ${canDecrementUpcomingQueue ? 'hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10' : 'cursor-not-allowed opacity-55'}`}
                          >−</button>
                          <button
                            type="button"
                            onClick={() => handleAdjustUpcomingCourts(1)}
                            disabled={!canIncrementUpcomingQueue}
                            title={upcomingQueueIncrementTitle}
                            className={`${SESSION_COUNTER_BTN} ${canIncrementUpcomingQueue ? 'hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/10' : 'cursor-not-allowed opacity-55'}`}
                          >+</button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {numberOfUpcomingCourts <= 0 ? null : nextAssignments.length === 0 ? (
                        <div className="h-full">
                        <UpcomingMatch
                          match={{ team1: [], team2: [] }}
                          onRemovePlayer={isSessionHost ? (playerId, teamNumber, matchIndex) => handleRemoveFromUpcoming(playerId, teamNumber, matchIndex) : undefined}
                          onAddPlayerToUpcoming={isSessionHost ? (playerId, teamNumber, matchIndex) => handleAddPlayerToUpcoming(playerId, teamNumber, matchIndex) : undefined}
                            onClearUpcomingMatch={isSessionHost ? handleClearUpcomingMatch : undefined}
                          onFillUpcomingCourt={isSessionHost && !isPegBoard ? (matchIndex) => handleFillUpcomingCourt(matchIndex) : undefined}
                          currentClubId={currentClubId}
                          matchIndex={0}
                          playersById={playersByIdWithGames}
                            availablePlayers={getUpcomingPickPlayingPlayers({ team1: [], team2: [] }, 0)}
                            waitingManagementPlayers={[]}
                            sessionPlayerStatus={sessionPlayerStatus}
                            nextAssignments={nextAssignments}
                          matchmakingValidation={matchmakingValidation}
                            playersPerSide={playersPerSideForSession}
                            gridDensity={COURT_GRID_DENSITY}
                            playerWaitMetrics={playerWaitMetrics}
                            courtCardLayout={isSessionHost || isAdmin || isClubAdmin ? 'full' : 'mobilePlayer'}
                            viewerPlayerId={currentUser?.id}
                            partnerships={uiPartnerships}
                            scheduledGames={uiScheduledGames}
                            showEloTooltip={!isPegBoard}
                            pegBoardQueue={pegBoardQueue}
                            pegBoardPickTarget={pegBoardPickTarget}
                            onOpenPegBoardPick={pegBoardCanOpenPick ? handleOpenPegBoardPick : undefined}
                        />
                      </div>
                    ) : (
                      nextAssignments.map((match, idx) => (
                          <div key={idx} className="h-full">
                          <UpcomingMatch
                            match={match}
                            onRemovePlayer={isSessionHost ? (playerId, teamNumber, matchIndex) => handleRemoveFromUpcoming(playerId, teamNumber, matchIndex) : undefined}
                            onAddPlayerToUpcoming={isSessionHost ? (playerId, teamNumber, matchIndex) => handleAddPlayerToUpcoming(playerId, teamNumber, matchIndex) : undefined}
                              onClearUpcomingMatch={isSessionHost ? handleClearUpcomingMatch : undefined}
                            onFillUpcomingCourt={isSessionHost && !isPegBoard ? (matchIndex) => handleFillUpcomingCourt(matchIndex) : undefined}
                            currentClubId={currentClubId}
                            matchIndex={idx}
                            playersById={playersByIdWithGames}
                              availablePlayers={getUpcomingPickPlayingPlayers(match, idx)}
                              waitingManagementPlayers={[]}
                              sessionPlayerStatus={sessionPlayerStatus}
                              nextAssignments={nextAssignments}
                            matchmakingValidation={matchmakingValidation}
                              playersPerSide={playersPerSideForSession}
                              gridDensity={COURT_GRID_DENSITY}
                              playerWaitMetrics={playerWaitMetrics}
                              courtCardLayout={isSessionHost || isAdmin || isClubAdmin ? 'full' : 'mobilePlayer'}
                              viewerPlayerId={currentUser?.id}
                              partnerships={uiPartnerships}
                              scheduledGames={uiScheduledGames}
                              showEloTooltip={!isPegBoard}
                              pegBoardQueue={pegBoardQueue}
                              pegBoardPickTarget={pegBoardPickTarget}
                              onOpenPegBoardPick={pegBoardCanOpenPick ? handleOpenPegBoardPick : undefined}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                  {/* Queue — collapsible Ready / Paused / Signed up list */}
                    {(() => {
                    const notStartedPlayerIdsSidebar = (players || [])
                      .map((p) => p.id)
                      .filter(
                        (id) =>
                          sessionPlayerStatus[id] === 'signed_up' &&
                          playersByIdWithGames[id] &&
                          !inAnyUpcomingPlayerIds.has(id)
                      )
                      .sort((a, b) =>
                        (playersByIdWithGames[a]?.name || '').localeCompare(
                          playersByIdWithGames[b]?.name || '',
                          undefined,
                          { sensitivity: 'base' }
                        )
                      );
                    const waitlistChips = filteredWaitingList.filter((id) => !inAnyUpcomingPlayerIds.has(id));
                    const pausedChips = filteredPausedList.filter((id) => !inAnyUpcomingPlayerIds.has(id));
                    const waitlistSearchActive = courtsWaitlistSearchQuery.trim().length > 0;
                    const notStartedChipsFiltered = notStartedPlayerIdsSidebar.filter((id) =>
                      playerNameIncludesQuery(playersByIdWithGames[id], courtsWaitlistSearchQuery)
                    );
                    const waitlistChipsFiltered = waitlistChips.filter((id) =>
                      playerNameIncludesQuery(playersByIdWithGames[id], courtsWaitlistSearchQuery)
                    );
                    const pausedChipsFiltered = pausedChips.filter((id) =>
                      playerNameIncludesQuery(playersByIdWithGames[id], courtsWaitlistSearchQuery)
                    );
                    const totalWaitlistPanelCount =
                      waitlistChips.length + pausedChips.length + notStartedPlayerIdsSidebar.length;
                    const waitlistChevronIcon = (open) => (
                      <svg className={`mt-1 h-4 w-4 shrink-0 text-text-secondary transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    );
                      return (
                      <div id="session-waitlist" className={`scroll-mt-28 rounded-xl border border-border-medium bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING}`}>
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-start gap-2 rounded-lg py-0.5 text-left transition hover:bg-surface-subtle/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                            aria-expanded={courtsSidebarExpanded.waitlist}
                            aria-controls="courts-waitlist-panel"
                            onClick={() => setCourtsSidebarExpanded((p) => ({ ...p, waitlist: !p.waitlist }))}
                          >
                            {waitlistChevronIcon(courtsSidebarExpanded.waitlist)}
                            <div className="min-w-0 flex-1">
                              <SectionHeader
                                title={
                                  <>
                                    {`Queue (${totalWaitlistPanelCount})`}
                                    {notStartedPlayerIdsSidebar.length > 0 && (
                                      <span className="ml-1.5 font-normal text-text-muted">
                                        · {notStartedPlayerIdsSidebar.length} signed up
                                      </span>
                                    )}
                                    {pausedChips.length > 0 && (
                                      <span className="ml-1.5 font-bold tabular-nums text-warning" title={`${pausedChips.length} paused`}>
                                        ({pausedChips.length} paused)
                                      </span>
                                    )}
                                  </>
                                }
                                variant="success"
                                size="sm"
                                className="!mb-0 pointer-events-none"
                              />
                            </div>
                          </button>
                          {showManageTab ? (
                            <div className="shrink-0 pt-0.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="small"
                                className="min-h-[44px] touch-manipulation text-xs sm:min-h-0"
                                aria-label="Add a player to the session"
                                onClick={openAddPlayerModal}
                              >
                                Add player
                              </Button>
                            </div>
                          ) : null}
                        </div>
                        {courtsSidebarExpanded.waitlist && (
                          <div id="courts-waitlist-panel" className="mt-3">
                        {showManageTab && notStartedPlayerIdsSidebar.length > 0 ? (
                          <div className="mb-3">
                            <Button
                              type="button"
                              variant="primary"
                              size="small"
                              className="w-full text-xs sm:w-auto"
                              onClick={handleStartMatchmakingForAll}
                              disabled={startMatchmakingLoading}
                              aria-label="Add all sign ups to the queue"
                            >
                              {startMatchmakingLoading
                                ? 'Adding to queue…'
                                : 'Add all sign ups to queue'}
                            </Button>
                          </div>
                        ) : null}
                        <MobileSearchField
                          id="courts-waitlist-search"
                          aria-label="Search queue by name"
                          value={courtsWaitlistSearchQuery}
                          onChange={(e) => setCourtsWaitlistSearchQuery(e.target.value)}
                          placeholder="Search queue…"
                          className="form-input mb-2 w-full min-w-0 rounded-md border-border-medium bg-field px-2.5 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary sm:max-w-sm"
                        />
                        {/* Taller cap so a hall laptop can show ~10–12 names before scroll */}
                        <div
                          className="max-h-[min(24rem,42vh)] overflow-y-auto overscroll-contain border-t border-border-medium pt-2 scroll-smooth"
                          aria-live="polite"
                          aria-label="Queue players"
                        >
                        <div className="pb-2">
                        {waitlistChipsFiltered.length > 0 ? (
                          <div className="mb-3">
                            <p className="mb-1.5 text-xs font-semibold text-text-secondary">
                              Ready ({waitlistChips.length})
                            </p>
                            <WaitingList
                              players={waitlistChipsFiltered}
                              playersById={playersByIdWithGames}
                              sessionPlayerStatus={sessionPlayerStatus}
                              sessionPlayerScheduledGames={uiSessionPlayerScheduledGames}
                              playerWaitMetrics={playerWaitMetrics}
                              playerStreaks={playerStreaks}
                              gamesMode={gamesMode}
                              currentClubId={selectedClub?.id}
                              hasCourtSlotAvailable={numberOfCourts > 0}
                              waitGuaranteeThreshold={computeWaitGuaranteeThreshold(numberOfCourts)}
                              onRemovePlayer={() => {}}
                              partnershipLocks={waitingListPartnershipLocks}
                              useSinglesElo={playersPerSideForSession === 1}
                              variant="list"
                              wrapperClassName=""
                              hostSessionRosterDnDEnabled={isSessionHost}
                              highlightPlayerId={pegBoardPickerId}
                              showQueuePosition={isPegBoard}
                              queueOrderIds={waitlistChips}
                              matchPreferencesEnabled={matchPreferencesEnabled}
                              cooldownEnabled={cooldownEnabled}
                              showEloTooltip={!isPegBoard}
                              showHostOverflowMenu={showManageTab}
                              hostActionContext={hostActionContext}
                              onPausePlayer={showManageTab ? (id) => handleTogglePlayerStatus(id, 'paused') : undefined}
                              onRemoveFromSession={
                                showManageTab ? (id) => handleTogglePlayerStatus(id, 'remove') : undefined
                              }
                              partnershipLookup={uiPartnershipLookup}
                              playersInUpcomingCourts={playersInUpcomingSet}
                              hostMenuOpenPlayerId={courtsHostMenuPlayerId}
                              onHostMenuOpenPlayerIdChange={setCourtsHostMenuPlayerId}
                            />
                          </div>
                        ) : waitlistChips.length > 0 && waitlistSearchActive ? (
                          <p className="py-3 text-sm text-text-muted">No ready players match your search.</p>
                        ) : waitlistChips.length === 0 && pausedChips.length === 0 && notStartedPlayerIdsSidebar.length === 0 ? (
                          <p className="py-3 text-sm text-text-muted">
                            No players in the queue yet. Add signed up players to the queue to begin court assignments.
                          </p>
                        ) : null}
                        {pausedChips.length > 0 && (
                          <div
                            className={`${waitlistChipsFiltered.length > 0 ? 'mt-3 border-t border-border-medium pt-2' : 'mb-3'}`}
                          >
                            <p className="mb-1.5 text-xs font-semibold text-text-secondary">
                              Paused ({pausedChips.length})
                            </p>
                            {pausedChipsFiltered.length > 0 ? (
                              <WaitingList
                                players={pausedChipsFiltered}
                                playersById={playersByIdWithGames}
                                sessionPlayerStatus={sessionPlayerStatus}
                                sessionPlayerScheduledGames={uiSessionPlayerScheduledGames}
                                playerWaitMetrics={playerWaitMetrics}
                                playerStreaks={playerStreaks}
                                gamesMode={gamesMode}
                                currentClubId={selectedClub?.id}
                                hasCourtSlotAvailable={numberOfCourts > 0}
                                waitGuaranteeThreshold={computeWaitGuaranteeThreshold(numberOfCourts)}
                                onRemovePlayer={() => {}}
                                partnershipLocks={waitingListPartnershipLocks}
                                useSinglesElo={playersPerSideForSession === 1}
                                variant="list"
                                wrapperClassName=""
                                hostSessionRosterDnDEnabled={false}
                                matchPreferencesEnabled={matchPreferencesEnabled}
                                cooldownEnabled={cooldownEnabled}
                                showEloTooltip={!isPegBoard}
                                showHostOverflowMenu={showManageTab}
                                hostActionContext={hostActionContext}
                                onResumePlayer={showManageTab ? (id) => handleTogglePlayerStatus(id, 'active') : undefined}
                                onRemoveFromSession={
                                  showManageTab ? (id) => handleTogglePlayerStatus(id, 'remove') : undefined
                                }
                                partnershipLookup={uiPartnershipLookup}
                                playersInUpcomingCourts={playersInUpcomingSet}
                                hostMenuOpenPlayerId={courtsHostMenuPlayerId}
                                onHostMenuOpenPlayerIdChange={setCourtsHostMenuPlayerId}
                              />
                            ) : waitlistSearchActive ? (
                              <p className="py-2 text-sm text-text-muted">No paused players match your search.</p>
                            ) : null}
                          </div>
                        )}
                        {notStartedPlayerIdsSidebar.length > 0 && (
                          <div
                            className={`${
                              waitlistChipsFiltered.length > 0 || pausedChips.length > 0
                                ? 'mt-3 border-t border-border-medium pt-2'
                                : ''
                            }`}
                          >
                            <p className="mb-1.5 text-xs font-semibold text-text-secondary">
                              Signed up ({notStartedPlayerIdsSidebar.length})
                            </p>
                            {notStartedChipsFiltered.length > 0 ? (
                              <div className="grid grid-cols-1 gap-y-1 md:grid-cols-2 md:gap-x-6">
                                {notStartedChipsFiltered.map((playerId) => {
                                  const player = playersByIdWithGames[playerId];
                                  if (!player) return null;
                                  return (
                                    <div
                                      key={playerId}
                                      className="flex min-h-11 items-center justify-between gap-2.5 rounded-md border border-primary/25 bg-info-light/30 px-2.5 py-1.5 dark:bg-primary/10"
                                    >
                                      <span
                                        className={`min-w-0 truncate text-base font-semibold leading-tight ${
                                          player.gender === 'female' ? 'text-female' : 'text-text-primary'
                                        }`}
                                      >
                                        {player.name}
                                      </span>
                                      {showManageTab ? (
                                        <div className="flex shrink-0 items-center gap-1">
                                          <button
                                            type="button"
                                            title={`Add ${player.name} to queue`}
                                            aria-label={`Add ${player.name} to queue`}
                                            onClick={() => handleTogglePlayerStatus(playerId, 'active')}
                                            className="relative inline-flex h-6 w-6 shrink-0 touch-manipulation items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary shadow-sm transition before:absolute before:-inset-2 before:content-[''] hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                          >
                                            <svg className="h-2.5 w-2.5 translate-x-[1px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                              <path d="M8 5v14l11-7L8 5z" />
                                            </svg>
                                          </button>
                                          <CompetitivePlayerOverflowMenu
                                            player={player}
                                            hostActionContext={hostActionContext}
                                            excludeKeys={[HOST_MENU_EXCLUDE_KEYS.addToWaitlist]}
                                            menuOpenPlayerId={courtsHostMenuPlayerId}
                                            onMenuOpenPlayerIdChange={setCourtsHostMenuPlayerId}
                                          />
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : waitlistSearchActive ? (
                              <p className="py-2 text-sm text-text-muted">No signed up players match your search.</p>
                            ) : null}
                          </div>
                        )}
                        </div>
                        </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Host tools (2-col) + player game preference (full width below) */}
                  {(() => {
                    const panelClass =
                      `flex flex-col rounded-xl border border-border-medium bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING}`;

                    const viewerId = currentUser?.id;
                    const viewerStatus = viewerId ? sessionPlayerStatus[viewerId] : null;
                    const inSessionRoster = ['active', 'signed_up', 'paused'].includes(viewerStatus || '');
                    const canSetOwnPreference =
                      sessionId && matchPreferencesEnabled && viewerId && inSessionRoster;
                    const showPrefsOffNote =
                      !isPegBoard && sessionId && !matchPreferencesEnabled && viewerId && inSessionRoster;
                    const showPreferenceCard = canSetOwnPreference || showPrefsOffNote;
                    const prefSlice = viewerId ? playerWaitMetrics[viewerId] : null;
                    const gamePreferenceSummary = (() => {
                      if (!showPreferenceCard) return '';
                      if (!canSetOwnPreference) return 'Session preferences off';
                      const pref = prefSlice?.matchPreference || 'no_preference';
                      if (pref === 'no_preference') return 'Any lineup';
                      const type = pref === 'prefer_mixed' ? 'Mixed Doubles' : 'Levels Doubles';
                      const strength = prefSlice?.preferenceStrength === 'hard' ? 'Hard' : 'Soft';
                      return `${type} · ${strength}`;
                    })();

                    const showPartnershipsPanel = !isPegBoard && !isSinglesFormat(matchFormat);
                    const showScheduledPanel = !isPegBoard && (scheduledGames.length > 0 || showManageTab);
                    const showHostToolsRow = showPartnershipsPanel || showScheduledPanel;

                    const chevronIcon = (open) => (
                      <svg className={`mt-1 h-4 w-4 shrink-0 text-text-secondary transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    );

                                return (
                      <>
                        {showHostToolsRow ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                        {showPartnershipsPanel && (
                          <div id="session-courts-sidebar-partnerships" className={`${panelClass}${!showManageTab ? ' hidden md:flex' : ''}`}>
                            <div className="flex items-start gap-2">
                              <button
                                type="button"
                                className="flex min-w-0 flex-1 items-start gap-2 rounded-lg py-0.5 text-left transition hover:bg-surface-subtle/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                aria-expanded={courtsSidebarExpanded.partnerships}
                                aria-controls="courts-sidebar-partnerships-panel"
                                onClick={() => setCourtsSidebarExpanded((p) => ({ ...p, partnerships: !p.partnerships }))}
                              >
                                {chevronIcon(courtsSidebarExpanded.partnerships)}
                                <SectionHeader title={`Partnerships (${partnerships.length})`} variant="info" size="sm" className="!mb-0 pointer-events-none min-w-0 flex-1" />
                              </button>
                              {showManageTab ? (
                                <div className="shrink-0 pt-0.5">
                                  <Button type="button" variant="outline" size="small" className="text-xs" onClick={() => setShowAddPartnershipModal(true)}>
                                    Add
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                            {courtsSidebarExpanded.partnerships && (
                              <div id="courts-sidebar-partnerships-panel" className="mt-3">
                                {partnerships.length > 0 ? (
                                  <ul className="space-y-1 text-xs text-text-secondary">
                                    {partnerships.map((p) => {
                                      const n1 = playersByIdWithGames[p.player1Id]?.name || 'Unknown';
                                      const n2 = playersByIdWithGames[p.player2Id]?.name || 'Unknown';
                                      const pairTitle = `${n1} & ${n2}`;
                                      return (
                                        <li key={p.id} className="flex items-center gap-1">
                                          <span className="min-w-0 flex-1 truncate" title={pairTitle}>{pairTitle}</span>
                                          {showManageTab ? (
                                            <button type="button" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base leading-none text-text-muted transition-colors hover:bg-danger-light hover:text-danger focus:outline-none focus:ring-2 focus:ring-primary/30" aria-label={`Remove partnership: ${pairTitle}`} onClick={() => handleRemoveSessionPartnershipFromSidebar(p)}>×</button>
                                          ) : null}
                                        </li>
                                );
                              })}
                                  </ul>
                                ) : (
                                  <p className="text-sm text-text-muted">None configured for this session.</p>
                                )}
                            </div>
                            )}
                          </div>
                        )}
                        
                        {showScheduledPanel && (
                        <div id="session-courts-sidebar-scheduled" className={panelClass}>
                          <div className="flex items-start gap-2">
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-start gap-2 rounded-lg py-0.5 text-left transition hover:bg-surface-subtle/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                              aria-expanded={courtsSidebarExpanded.scheduled}
                              aria-controls="courts-sidebar-scheduled-panel"
                              onClick={() => setCourtsSidebarExpanded((p) => ({ ...p, scheduled: !p.scheduled }))}
                            >
                              {chevronIcon(courtsSidebarExpanded.scheduled)}
                              <div className="min-w-0 flex-1">
                                <SectionHeader
                                  title={`Scheduled games (${scheduledGames.length})`}
                                  variant="info"
                                  size="sm"
                                  className="!mb-0 pointer-events-none"
                                />
                              </div>
                            </button>
                            {showManageTab && (() => {
                              const pendingCount = scheduledGames.filter(
                                (g) => g.status === 'pending' || !g.status
                              ).length;
                              return (
                                <div className="shrink-0 pt-0.5">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="small"
                                    className="text-xs"
                                    disabled={pendingCount >= 5}
                                    title={
                                      pendingCount >= 5
                                        ? 'Maximum 5 pending scheduled games'
                                        : 'Create a pre-arranged match'
                                    }
                                    onClick={() => setShowAddScheduledGameModal(true)}
                                  >
                                    Add
                                  </Button>
                                </div>
                              );
                            })()}
                          </div>
                          {courtsSidebarExpanded.scheduled && (
                            <div id="courts-sidebar-scheduled-panel" className="mt-3">
                          {scheduledGames.length === 0 ? (
                            <p className="text-sm text-text-muted">None queued.</p>
                          ) : (
                            <div className="space-y-2">
                              {scheduledGames.map((g, i) => {
                                const team1Ids = [g.team1_player1_id, g.team1_player2_id].filter(Boolean);
                                const team2Ids = [g.team2_player1_id, g.team2_player2_id].filter(Boolean);
                                const isOnCourt = g.status === 'placed' || g.status === 'on_court';
                                const renderTeamCol = (ids) => (
                                  <div className="flex min-w-0 flex-col gap-0.5">
                                    {ids.map((id) => {
                                      const p = playersByIdWithGames[id];
                                      if (!p) return null;
                                      return (
                                        <div key={id} className="flex min-w-0 items-center gap-1.5">
                                          <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${getGenderAvatarBg(p.gender)}`}>
                                            {getInitials(p.name)}
                                          </span>
                                          <span className="min-w-0 truncate text-sm font-semibold leading-tight" title={p.name || undefined}>{p.name}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                                return (
                                  <div
                                    key={g.id || `sg-${i}`}
                                    className={`rounded-lg border p-2.5 ${isOnCourt ? 'border-success/40 bg-success-light/10' : 'border-border-medium bg-surface-subtle'}`}
                                  >
                                    <div className="mb-1.5 flex items-center justify-between gap-1">
                                      <span className={`text-xs font-medium ${isOnCourt ? 'text-success' : 'text-text-secondary'}`}>
                                        {isOnCourt ? '● In progress' : `Scheduled #${i + 1}`}
                                      </span>
                                      {showManageTab && g.id && (
                                        <button
                                          type="button"
                                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-sm leading-none text-text-muted hover:bg-danger-light hover:text-danger focus:outline-none focus:ring-1 focus:ring-primary/30"
                                          aria-label={isOnCourt ? `Cancel in-progress scheduled game #${i + 1}` : `Remove scheduled game #${i + 1}`}
                                          title={isOnCourt ? 'Cancel in-progress scheduled game' : 'Remove scheduled game'}
                                          onClick={() => handleRemoveScheduledGame(g.id)}
                                        >×</button>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-x-2">
                                      {renderTeamCol(team1Ids)}
                                      <span className="self-start pt-[17px] text-[10px] font-semibold uppercase text-text-muted">vs</span>
                                      {renderTeamCol(team2Ids)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                            </div>
                          )}
                        </div>
                        )}

                      </div>
                        ) : null}

                        {showPreferenceCard ? (
                          <div
                            id="viewer-game-preference"
                            className={`${panelClass} mt-4${showYouTab ? ' hidden md:flex' : ''}`}
                          >
                            <button
                              type="button"
                              className="flex w-full min-w-0 items-start gap-2 rounded-lg py-0.5 text-left transition hover:bg-surface-subtle/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                              aria-expanded={courtsSidebarExpanded.gamePreference}
                              aria-controls="viewer-game-preference-panel"
                              onClick={() =>
                                setCourtsSidebarExpanded((p) => ({ ...p, gamePreference: !p.gamePreference }))
                              }
                            >
                              {chevronIcon(courtsSidebarExpanded.gamePreference)}
                              <div className="min-w-0 flex-1">
                                <SectionHeader
                                  title={
                                    <span className="flex items-center gap-1.5">
                                      Your game preference
                                      {!courtsSidebarExpanded.gamePreference ? (
                                        <span className="text-xs text-text-muted" aria-hidden>
                                          ⚙
                                        </span>
                                      ) : null}
                                    </span>
                                  }
                                  variant="info"
                                  size="sm"
                                  className="!mb-0 pointer-events-none"
                                />
                                {!courtsSidebarExpanded.gamePreference ? (
                                  <p className="mt-0.5 text-xs text-text-secondary">
                                    {gamePreferenceSummary} ·{' '}
                                    <span className="text-info">tap to change</span>
                                  </p>
                                ) : null}
                              </div>
                            </button>
                            {courtsSidebarExpanded.gamePreference ? (
                              <div id="viewer-game-preference-panel" className="mt-3 min-w-0">
                                {canSetOwnPreference ? (
                                  <>
                                    <p className="mb-3 text-xs leading-snug text-text-muted">
                                      Choose what types of games you prefer while you wait. Tap a card to apply
                                      instantly.
                                    </p>
                                    <MatchPreferenceSelector
                                      variant="cards"
                                      playerId={viewerId}
                                      sessionId={sessionId}
                                      offlineQueue={offlineQueue}
                                      session={sessionData}
                                      getSnapshot={() => liveStateRef.current}
                                      currentPreference={prefSlice?.matchPreference || 'no_preference'}
                                      currentStrength={prefSlice?.preferenceStrength || 'soft'}
                                      onPreferenceChange={(pid, newPref, newStrength, rowUpdatedAt) => {
                                        setPlayerWaitMetrics((prev) => ({
                                          ...prev,
                                          [pid]: {
                                            ...(prev[pid] || {}),
                                            matchPreference: newPref,
                                            preferenceStrength: newStrength,
                                            sessionPlayerRowUpdatedAt: rowUpdatedAt,
                                          },
                                        }));
                                      }}
                                    />
                                  </>
                                ) : (
                                  <p
                                    className="text-sm leading-snug text-text-muted"
                                    title="The host can enable this under Manage → Match settings"
                                  >
                                    Match preferences are off for this session. The host can turn them on in the
                                    Manage tab under Match settings.
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                      );
                    })()}

                </div>
                </DndContext>
                  </div>

            </CourtsTab>
          ) : activeTab === 'manage' ? (
            <CourtsTab>
              <div className="relative mt-2">
                {playersLoading && (
                  <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80">
                    <LoadingState message="Updating players…" size="large" className="py-0" />
                  </div>
                )}
                {isSessionHost && (
                  <HostAdminCollapsibleSection
                    id="session-match-settings"
                    title="Match settings"
                    variant="info"
                    expanded={hostSectionExpanded.matchSettings}
                    onExpandedChange={(next) =>
                      setHostSectionExpanded((s) => ({ ...s, matchSettings: next }))
                    }
                    className="mt-2"
                  >
                    <div className="text-xs text-text-secondary">
                      {isPegBoard ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="min-w-0 space-y-1">
                            <span className="block text-xs font-medium text-text-secondary">
                              Picker can choose from
                            </span>
                            <select
                              value={String(
                                clampPegBoardSelectionWindow(sessionData?.peg_board_selection_window)
                              )}
                              onChange={(e) => handlePegBoardSelectionWindowChange(e.target.value)}
                              aria-label="How many queue players the picker can choose from"
                              className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              {PEG_BOARD_SELECTION_WINDOW_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label} players
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-text-muted">
                              Includes the picker. Must be at least 4 for doubles.
                            </p>
                          </div>
                        </div>
                      ) : (
                      <div
                        className={`grid gap-3 ${
                          isAdmin && !isSinglesFormat(matchFormat)
                            ? 'sm:grid-cols-2 lg:grid-cols-5'
                            : 'sm:grid-cols-2 md:grid-cols-4'
                        }`}
                      >
                        <div className="min-w-0 space-y-1">
                          <span className="block text-xs font-medium text-text-secondary">Format</span>
                          <select
                            value={matchFormat}
                            onChange={(e) => handleMatchFormatChange(e.target.value)}
                            aria-label="Match format"
                            className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="doubles">Doubles</option>
                            <option value="singles">Singles</option>
                          </select>
                        </div>
                        <div className="min-w-0 space-y-1">
                          <span className="block text-xs font-medium text-text-secondary">Game type</span>
                          <select
                            value={sessionToolbarGameTypeSelect.value}
                            title={sessionToolbarGameTypeSelect.title}
                            onChange={(e) => handleGameTypeRestrictionChange(e.target.value)}
                            aria-label="Game type"
                            className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            {sessionToolbarGameTypeSelect.options.map((opt) => (
                              <option key={opt.value} value={opt.value} title={opt.label}>
                                {opt.shortLabel || opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="min-w-0 space-y-1">
                          <span className="block text-xs font-medium text-text-secondary">
                            Match preferences
                          </span>
                          <select
                            value={matchPreferencesEnabled ? 'on' : 'off'}
                            onChange={(e) => {
                              const next = e.target.value === 'on';
                              if (next !== matchPreferencesEnabled) {
                                void handleMatchPreferencesEnabledToggle();
                              }
                            }}
                            aria-label="Match preferences for this session"
                            className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="on">On</option>
                            <option value="off">Off</option>
                          </select>
                        </div>
                        <div className="min-w-0 space-y-1">
                          <span className="block text-xs font-medium text-text-secondary">
                            Cooldown
                          </span>
                          <select
                            value={cooldownEnabled ? 'on' : 'off'}
                            onChange={(e) => {
                              const next = e.target.value === 'on';
                              if (next !== cooldownEnabled) {
                                void handleCooldownEnabledToggle();
                              }
                            }}
                            aria-label="Cooldown enforcement for this session"
                            className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="on">On</option>
                            <option value="off">Off</option>
                          </select>
                        </div>
                        {isAdmin && !isSinglesFormat(matchFormat) && (
                          <div className="min-w-0 space-y-1 sm:col-span-2 lg:col-span-1">
                            <span className="block text-xs font-medium text-text-secondary">
                              Categorisation{' '}
                              <span className="font-normal text-text-secondary/90">
                                ({categorisation.activeCategorizationPlayerCount})
                              </span>
                            </span>
                            <select
                              value={categorisation.categorizationEnabled ? 'on' : 'off'}
                              onChange={(e) => {
                                const next = e.target.value === 'on';
                                if (next !== categorisation.categorizationEnabled) {
                                  void categorisation.handleCategorizationToggle();
                                }
                              }}
                              aria-label="Player categorisation for this session"
                              className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                          </div>
                        )}
                      </div>
                      )}
                      <div className="mt-3 border-t border-border-subtle pt-3">
                        <p className="mb-2 text-xs font-medium text-text-secondary">Game scoring</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="min-w-0 space-y-1">
                            <span className="block text-xs font-medium text-text-secondary">Points to win</span>
                            <select
                              value={String(scoringConfig.pointsTarget)}
                              onChange={(e) => handlePointsTargetChange(e.target.value)}
                              aria-label="Points to win"
                              className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="21">21 points</option>
                              <option value="15">15 points</option>
                            </select>
                          </div>
                          <div className="min-w-0 space-y-1">
                            <span className="block text-xs font-medium text-text-secondary">Deuce rules</span>
                            <select
                              value={scoringConfig.deuceMode}
                              onChange={(e) => handleDeuceModeChange(e.target.value)}
                              aria-label="Deuce rules"
                              className="h-8 min-h-8 w-full min-w-0 rounded border border-border-medium bg-surface-subtle px-2 text-xs text-text-secondary transition-all focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="standard">Standard</option>
                              <option value="no_deuce">No deuce</option>
                            </select>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-text-muted">
                          {getScoringRulesSummary(scoringConfig)} Applies to new scores only.
                        </p>
                      </div>
                      <div className="mt-3 border-t border-border-subtle pt-3">
                        <span className="block text-xs font-medium text-text-secondary">
                          Offline mode
                        </span>
                        {isHallPhone || sessionData?.offline_mode ? (
                          <p className="mt-1 text-xs text-text-muted">
                            On until you end the session. Players cannot follow courts on their phones.
                          </p>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="small"
                              className="mt-2"
                              onClick={() => setShowOfflineModeConfirm(true)}
                              disabled={enablingOfflineMode}
                            >
                              Turn on Offline mode
                            </Button>
                            <p className="mt-1 text-xs text-text-muted">
                              Use this when you know the hall wifi will be poor. Stays on until End session.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </HostAdminCollapsibleSection>
                )}
                {(isSessionHost || isAdmin || isClubAdmin) && !isPegBoard && (
                  <HostAdminCollapsibleSection
                    id="session-scheduled"
                    title="Scheduled Games"
                    variant="info"
                    infoText={SCHEDULED_GAMES_HOST_INFO}
                    infoAriaLabel="How to add, remove, or cancel scheduled games"
                    expanded={hostSectionExpanded.scheduled}
                    onExpandedChange={(next) =>
                      setHostSectionExpanded((s) => ({ ...s, scheduled: next }))
                    }
                    className="mt-2"
                  >
                  <ScheduledGamesList 
                    scheduledGames={scheduledGames}
                    playersById={playersByIdWithGames}
                    sessionPlayerStatus={sessionPlayerStatus}
                    courtAssignments={courtAssignments}
                    waitingList={waitingList}
                    nextAssignments={nextAssignments}
                    onUpdateOrder={isSessionHost ? handleUpdateScheduledGameOrder : undefined}
                    onRemove={isSessionHost ? handleRemoveScheduledGame : undefined}
                      onAdd={
                        isSessionHost || isAdmin || isClubAdmin
                          ? () => setShowAddScheduledGameModal(true)
                          : undefined
                      }
                    />
                  </HostAdminCollapsibleSection>
                )}

                {(isSessionHost || isAdmin || isClubAdmin) && !isPegBoard && !isSinglesFormat(matchFormat) && (
                  <HostAdminCollapsibleSection
                    id="session-partnerships"
                    title="Partnerships"
                    variant="info"
                    infoText={PARTNERSHIPS_MATCHMAKING_INFO}
                    infoAriaLabel="How partnerships work in matchmaking"
                    expanded={hostSectionExpanded.partnerships}
                    onExpandedChange={(next) =>
                      setHostSectionExpanded((s) => ({ ...s, partnerships: next }))
                    }
                    className="mt-2"
                  >
                <CurrentPartnerships 
                  players={players}
                  partnerships={partnerships}
                  onUpdatePartnerships={handlePartnershipsUpdate}
                  playersById={playersById}
                  sessionPlayerStatus={sessionPlayerStatus}
                  sessionId={sessionId}
                      readOnly={!(isSessionHost || isAdmin || isClubAdmin)}
                      hideOuterHeading
                  onToast={addToast}
                  loadSessionPartnerships={loadSessionPartnerships}
                  offlineQueue={offlineQueue}
                  session={sessionData}
                  getSnapshot={() => liveStateRef.current}
                />
                  </HostAdminCollapsibleSection>
                )}

              {(isSessionHost || isClubAdmin) && (
                  <HostAdminCollapsibleSection
                    id="session-player-management"
                  title="Player Management"
                  variant="primary"
                    expanded={hostSectionExpanded.players}
                    onExpandedChange={(next) =>
                      setHostSectionExpanded((s) => ({ ...s, players: next }))
                    }
                    className="mt-2"
                    panel={false}
                  >
                <PlayerManager
                  variant="competitive"
                  players={players}
                  sessionPlayerStatus={sessionPlayerStatus}
                  sessionPlayerExitReasons={sessionPlayerExitReasons}
                      playersOnActiveCourts={playersOnCourtsSet}
                  playersInUpcomingCourts={playersInUpcomingSet}
                  togglePlayerStatus={handleTogglePlayerStatus}
                  hostActionContext={hostActionContext}
                  openPlayerInfoModal={openHostPlayerInfoModal}
                  suppressHostModals
                  onStartMatchmaking={handleStartMatchmakingForAll}
                  startMatchmakingLoading={startMatchmakingLoading}
                      onAddNewPlayer={openAddPlayerModal}
                  currentClubId={currentClubId}
                  onGuestUpdated={fetchPlayers}
                  sessionId={sessionId}
                  sessionPlayerPenalties={sessionPlayerPenalties}
                  seasonId={season?.id}
                  playerWaitMetrics={playerWaitMetrics}
                  offlineQueue={offlineQueue}
                  session={sessionData}
                  getSnapshot={() => liveStateRef.current}
                  onMatchPreferenceReload={() => {
                    if (shouldHoldRemoteUpcoming(offlineQueue, sessionData)) return;
                    void handleRemoteSessionSync({ ignoreSaveGuard: true });
                  }}
                  onMatchPreferenceSaved={(pid, newPref, newStrength, rowUpdatedAt) => {
                    setPlayerWaitMetrics((prev) => ({
                      ...prev,
                      [pid]: {
                        ...(prev[pid] || {}),
                        matchPreference: newPref,
                        preferenceStrength: newStrength,
                        sessionPlayerRowUpdatedAt: rowUpdatedAt,
                      },
                    }));
                  }}
                  matchPreferencesEnabled={matchPreferencesEnabled}
                  playersById={playersById}
                  sessionPlayerScheduledGames={uiSessionPlayerScheduledGames}
                  partnershipLookup={uiPartnershipLookup}
                  onMensEligibilitySaved={(pid, eligible, shadowElo) => {
                    setPlayerWaitMetrics((prev) => ({
                      ...prev,
                      [pid]: {
                        ...(prev[pid] || {}),
                        mensDoublesEligible: eligible,
                        mensDoublesShadowElo: shadowElo,
                      },
                    }));
                    void handleRemoteSessionSync({ ignoreSaveGuard: true });
                  }}
                  onPenaltyUpdated={async (playerId, penalty) => {
                    await persistLatePenalty(playerId, penalty);
                    try {
                      await fetchPlayers();
                    } catch (error) {
                      if (!isLikelyNetworkError(error)) throw error;
                    }
                  }}
                />
                  </HostAdminCollapsibleSection>
              )}
              </div>
            </CourtsTab>
          ) : activeTab === 'ranking' ? (
            <SessionRankingTab
              sessionId={sessionId}
              playersById={playersById}
              selectedClub={selectedClub}
              season={season}
          sessionPlayerStatus={sessionPlayerStatus}
              matchFormat={matchFormat}
              refreshKey={rankingRefreshKey}
              localGames={localSessionGames}
            />
          ) : activeTab === 'games' ? (
            <GamesTab
                sessionId={sessionId}
              viewerPlayerId={currentUser?.id}
              sessionPlayerStatus={sessionPlayerStatus}
              isSessionHost={isSessionHost}
              onScheduleRematch={isSessionHost && !isPegBoard ? handleGameScheduled : null}
              sessionPlayerScheduledGames={uiSessionPlayerScheduledGames}
              canCorrectScore={canCorrectScore}
              sessionStatus={liveSessionStatus}
              scoringConfig={scoringConfig}
              playersById={playersById}
              onScoreCorrected={handleScoreCorrected}
              gamesRefreshKey={gamesRefreshKey}
              localGames={localSessionGames}
              />
          ) : activeTab === 'playerRanking' ? (
            <PlayerRankingTab
              categorizationEnabled={categorisation.categorizationEnabled}
              categoryStatusText={categorisation.categoryStatusText}
              lastCategoryCalculation={categorisation.lastCategoryCalculation}
              formatRelativeCategoryTime={categorisation.formatRelativeCategoryTime}
              categorySummaryText={categorisation.categorySummaryText}
              handleCategorizationToggle={categorisation.handleCategorizationToggle}
              handleManualCategoryRefresh={categorisation.handleManualCategoryRefresh}
              isCategoryRefreshing={categorisation.isCategoryRefreshing}
              rankingTabOptions={categorisation.rankingTabOptions}
              rankingTab={categorisation.rankingTab}
              setRankingTab={categorisation.setRankingTab}
              currentRankingSection={categorisation.currentRankingSection}
              renderRankingSection={categorisation.renderRankingSection}
            />
          ) : activeTab === 'you' ? (() => {
            const youViewerId = currentUser?.id;
            const youPrefSlice = youViewerId ? playerWaitMetrics[youViewerId] : null;
            const youCanSetPref = !!(sessionId && matchPreferencesEnabled && youViewerId && viewerInSessionRoster);
            return (
              <div className="mx-auto max-w-app-shell pb-6 pt-3 md:pt-4 space-y-3 md:space-y-4">
                {showOwnPresenceCard ? (
                  <SessionPresenceCard
                    rosterStatus={viewerSessionStatus}
                    pauseBlockReason={ownPresencePauseBlockReason}
                    lateJoinerPenalty={sessionPlayerPenalties[currentUser.id] || 0}
                    onAction={handleOwnSessionPresence}
                    loading={ownPresenceLoading}
                  />
                ) : null}
                <div className={`rounded-xl border border-border-medium bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING}`}>
                  <h2 className="mb-0.5 text-base font-semibold text-text-primary">Game preference</h2>
                  <p className="mb-4 text-sm text-text-muted">
                    Choose what types of games you prefer while you wait, then tap Save preference.
                  </p>
                  {youCanSetPref ? (
                    <MatchPreferenceSelector
                      variant="cards"
                      explicitSave
                      playerId={youViewerId}
                      sessionId={sessionId}
                      offlineQueue={offlineQueue}
                      session={sessionData}
                      getSnapshot={() => liveStateRef.current}
                      currentPreference={youPrefSlice?.matchPreference || 'no_preference'}
                      currentStrength={youPrefSlice?.preferenceStrength || 'soft'}
                      onPreferenceChange={(pid, newPref, newStrength, rowUpdatedAt) => {
                        setPlayerWaitMetrics((prev) => ({
                          ...prev,
                          [pid]: {
                            ...(prev[pid] || {}),
                            matchPreference: newPref,
                            preferenceStrength: newStrength,
                            sessionPlayerRowUpdatedAt: rowUpdatedAt,
                          },
                        }));
                      }}
                    />
                  ) : (
                    <p className="text-sm text-text-muted">
                      Match preferences are off for this session. The host can turn them on in the Manage tab under Match settings.
                    </p>
                  )}
                </div>
              </div>
            );
          })() : null}
        </div>
      
      <ScoreRecordingNextUpModal
        isOpen={Boolean(scoreRecordingNextUpModal)}
        onClose={closeScoreRecordingNextUpPreview}
        activeCourtNumber={scoreRecordingNextUpModal?.activeCourtNumber}
        team1Names={scoreRecordingNextUpModal?.team1Names}
        team2Names={scoreRecordingNextUpModal?.team2Names}
        team1Ids={scoreRecordingNextUpModal?.team1Ids}
        team2Ids={scoreRecordingNextUpModal?.team2Ids}
        playersById={playersByIdWithGames}
        isComplete={scoreRecordingNextUpModal?.isComplete}
        hasAnyPlayers={scoreRecordingNextUpModal?.hasAnyPlayers}
      />
      
      {/* Add Guest Player Modal */}
      <AddGuestModal
        isOpen={showAddPlayer}
        onClose={closeAddGuestModal}
        stayOpenAfterAdd={false}
        createGuestInSession={
          shouldRunLocalHostPath(offlineQueue, sessionData)
            ? createOfflineSessionGuest
            : undefined
        }
        onGuestAdded={async (guestWithStatus, meta = {}) => {
          if (!shouldRunLocalHostPath(offlineQueue, sessionData)) {
            await fetchPlayers();
            await fetchSessionPlayerStatus();
          }
          const playerId = guestWithStatus?.id;
          if (meta.addedSessionStatus === 'active' && playerId && sessionId) {
            try {
              if (isPegBoard) {
                await persistLatePenalty(playerId, 0);
              } else {
                const suggestion = await resolveLatePenaltySuggestion(playerId);
                if (suggestion.shouldShowModal) {
                  setPenaltyModalPlayer(guestWithStatus);
                  setPenaltySuggestion(suggestion);
                  setShowLatePenaltyModal(true);
                } else {
                  await persistLatePenalty(playerId, 0);
                }
              }
            } catch (error) {
              console.error('Error checking penalty suggestion for new guest:', error);
              try {
                await persistLatePenalty(playerId, 0);
              } catch (penaltyError) {
                console.warn('[onGuestAdded] late penalty skipped:', penaltyError);
              }
            }
            setWaitingList((prev) => (prev.includes(playerId) ? prev : [...prev, playerId]));
            setMatchmakingStarted(true);
          }
        }}
          sessionId={sessionId}
        clubId={selectedClub?.id}
        currentUser={currentUser}
          seasonId={season?.id}
        sessionEntryStatus={addPlayerEntryTarget}
        initialFirstName={guestNamePrefill.firstName}
        initialLastName={guestNamePrefill.lastName}
      />

      <AddExistingClubPlayerModal
        isOpen={showAddPlayerModal}
        onClose={() => {
          setShowAddPlayerModal(false);
          setAddPlayerEntryTarget(defaultAddEntryTarget);
        }}
        players={players}
        sessionPlayerStatus={sessionPlayerStatus}
        sessionEntryStatus={addPlayerEntryTarget}
        clubId={selectedClub?.id}
        seasonId={season?.id}
        onAddToSession={async (playerId) =>
          handleTogglePlayerStatus(playerId, addPlayerEntryTarget)
        }
        onAddNewGuest={openAddGuestModal}
        onPasteRoster={handlePasteRosterAccept}
        currentUser={currentUser}
      />

      {!isPegBoard && !isSinglesFormat(matchFormat) && (
        <AddSessionPartnershipModal
          isOpen={showAddPartnershipModal}
          onClose={() => setShowAddPartnershipModal(false)}
          players={players}
          partnerships={partnerships}
          onUpdatePartnerships={handlePartnershipsUpdate}
          sessionPlayerStatus={sessionPlayerStatus}
          sessionId={sessionId}
          playersById={playersById}
          onToast={addToast}
          loadSessionPartnerships={loadSessionPartnerships}
          offlineQueue={offlineQueue}
          session={sessionData}
          getSnapshot={() => liveStateRef.current}
        />
      )}
      
      {/* Add Scheduled Game Modal */}
      {!isPegBoard ? (
      <AddScheduledGameModal
        isOpen={showAddScheduledGameModal}
        onClose={() => setShowAddScheduledGameModal(false)}
        sessionId={sessionId}
        clubId={selectedClub?.id}
        playersById={playersByIdWithGames}
          sessionPlayerStatus={sessionPlayerStatus}
        scheduledGames={scheduledGames}
          partnerships={partnerships}
        matchFormat={matchFormat}
        onGameScheduled={handleGameScheduled}
        />
      ) : null}

      <ConfirmModal
        isOpen={showOfflineModeConfirm}
        onClose={() => setShowOfflineModeConfirm(false)}
        onConfirm={handleEnableOfflineMode}
        title="Offline mode"
        message="This phone will run the session without relying on wifi. Players will not see live courts or be able to follow the session on their own phones. This stays on until you end the session."
        confirmText={enablingOfflineMode ? 'Turning on…' : 'Turn on Offline mode'}
        cancelText="Cancel"
        variant="warning"
        confirmDisabled={enablingOfflineMode}
      />

      <ConfirmModal
        isOpen={Boolean(scheduledGameCancelTarget)}
        onClose={() => setScheduledGameCancelTarget(null)}
        onConfirm={handleConfirmCancelScheduledGame}
        title={scheduledGameCancelModalCopy.title}
        message={scheduledGameCancelModalCopy.message}
        confirmText={scheduledGameCancelModalCopy.confirmText}
        cancelText="Keep scheduled game"
        variant="danger"
      />
      
      {/* Late Penalty Modal */}
      {showLatePenaltyModal && penaltyModalPlayer && (
        <LatePenaltyModal
          player={penaltyModalPlayer}
          suggestedPenalty={penaltySuggestion.suggestedPenalty}
          averageGames={penaltySuggestion.averageGames}
          playerGamesPlayed={penaltySuggestion.playerGamesPlayed}
          isReturningPlayer={penaltySuggestion.isReturningPlayer}
          onConfirm={handlePenaltyConfirm}
          onCancel={handlePenaltyCancel}
        />
      )}
      
      {renderHostActionModals()}

      {/* End Session Modal */}
      <EndSessionModal
        isOpen={showEndSessionModal}
        onClose={() => setShowEndSessionModal(false)}
        onEndSession={handleSessionEnd}
        isLoading={endingSession}
        error={endSessionError}
      />
    </div>
  );
};

export default CompetitiveSessionPage; 